import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../api';
import CalendarLib from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import {
  Calendar, Clock, User, CheckCircle, Search,
  LayoutDashboard, Settings, MessageSquare, Video, Zap, LogOut, Users, X,
  CreditCard, ExternalLink, Briefcase, Shield, Award, Info, BookOpen, Bell, XCircle, Lock, ArrowLeft, RotateCcw, GraduationCap,


  Github, GitFork, RefreshCw, Plus, Trash2, Code, Layers, FileText, Share2, Star, Folder,
  Wrench, Link2, Brain, Upload, Edit2, Eye, Check, ChevronDown, ChevronUp, Trophy, Activity, Sparkles, Terminal, Cpu, Target, Compass, BarChart2, ClipboardCheck, Play, Building2, MapPin, DollarSign, Loader2, Lightbulb, ShieldAlert, AlertTriangle, Download
} from 'lucide-react';

import './StudentDashboard.css';
import PaymentModal from '../components/PaymentModal';
import Loader from '../components/Loader.js';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';

import { INDUSTRY_DOMAINS, DOMAIN_METADATA } from '../constants/domains';
import DiscoverProfessors from './DiscoverProfessors';
import OnboardingTour from '../components/OnboardingTour';


import StudentPartnerCourses from '../components/student/StudentPartnerCourses';
import StudentAnalyticsDashboard from '../components/StudentAnalyticsDashboard';
import StudentSidebar from '../components/StudentSidebar';
import { FeatureGate, SubFeatureGate, useFeatures } from '../components/FeatureGate';
import { flushQueuedProctorReports } from '../utils/proctorReportQueue';
import { resolveQuizFailure } from '../utils/quizError';
import { OFFLINE_BACKUP_QUIZ } from '../constants/offlineQuiz';
import PlatformFeedback from '../components/student/PlatformFeedback';
import DiscussionsPage from './DiscussionsPage';
import ChatPage from './ChatPage';
import FormattedResumeSummary from '../components/FormattedResumeSummary';
import StudentRealTimeCalendar from '../components/StudentRealTimeCalendar';
import ProctoringCam from '../components/student/ProctoringCam';

import { clearUIRulesCache } from '../components/UIElementGuard';
import { DetailsModal, normalizePhoneForDisplay } from '../components/student/SessionDetailsModal';
import AiDisclaimerCard from '../components/AiDisclaimerCard';

// react-phone-input-2 expects the value to include the country dial code
// (e.g. 919988776655). Bulk-uploaded students store a bare 10-digit number
// (e.g. 9988776655), which the library mis-parses as a country code
// (998 -> Uzbekistan). Prepend India's dial code for display only;
// the stored value is never modified.
function StudentDashboard() {
  const { isEnabled, isSubEnabled } = useFeatures();
  const canViewInternalJobs = (isEnabled ? isEnabled('internal_jobs') : true) && (isSubEnabled ? isSubEnabled('internal_jobs', 'internal_jobs_view') : true);
  const canApplyInternalJobs = (isEnabled ? isEnabled('internal_jobs') : true) && (isSubEnabled ? isSubEnabled('internal_jobs', 'internal_jobs_express_interest') : true);
  const canDownloadInternalJd = (isEnabled ? isEnabled('internal_jobs') : true) && (isSubEnabled ? isSubEnabled('internal_jobs', 'internal_jobs_download_jd') : true);
  const canViewPlacementDrives = (isEnabled ? isEnabled('jobs') : true) && (isSubEnabled ? isSubEnabled('jobs', 'jobs_view') : true);
  const [profile, setProfile] = useState(null);
  const [publicSessions, setPublicSessions] = useState([]);
  const [myPersonalRequests, setMyPersonalRequests] = useState([]);
  const [expandedSkills, setExpandedSkills] = useState({});
  const [expandedSkillCards, setExpandedSkillCards] = useState({});

  const handleMarkRemarkAsRead = async (driveId) => {
    try {
      await api.post('/student/jobs/read-remarks', { drive_id: driveId });
      setJobs(jobs.map(j => j.drive_id === driveId ? { ...j, student_remarks_read: true } : j));
    } catch (err) {
      console.error("Failed to mark remark as read", err);
    }
  };

  const [loading, setLoading] = useState(true);
  const [showProfile, setShowProfile] = useState(false);
  const [activeTab, setActiveTab] = useState('explore');
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [showAttemptsModal, setShowAttemptsModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());

  // Payment State
  const [paymentModalData, setPaymentModalData] = useState(null); // { sessionId, amount, title }

  // Feedback Modal State
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackData, setFeedbackData] = useState({
    session_id: null,
    session_type: null,
    quality_rating: 0,
    expert_effectiveness: 0,
    relevance_rating: 0,
    comments: ''
  });

  // Skills & Interests Update State
  const [showInterestsModal, setShowInterestsModal] = useState(false);
  const [interestsForm, setInterestsForm] = useState({
    skills: [],
    preferred_domains: [],
    primary_domain: ''
  });
  const [newSkillInput, setNewSkillInput] = useState('');
  const [skillSuggestions, setSkillSuggestions] = useState([]);

  // Notifications
  const [notifications, setNotifications] = useState([]);
  const [showNotifs, setShowNotifs] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);

  // Change Password State
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Refund Request State
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [refundData, setRefundData] = useState({
    payment_id: null,
    amount: 0,
    reason: 'STUDENT_CANCELLATION',
    details: ''
  });

  // Toast notification state
  const [toasts, setToasts] = useState([]);

  // Toast notification helper
  const showToast = (message, type = 'success') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(toast => toast.id !== id));
    }, 4000);
  };

  // Proctoring reports that a previous session could not deliver (backend
  // unreachable, Proctoring feature disabled at the time) are replayed here.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { sent } = await flushQueuedProctorReports();
      if (!cancelled && sent > 0) {
        showToast(`✅ ${sent} pending proctoring report(s) sent for faculty review.`, 'success');
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  // Fetch Notifications
  const fetchNotifs = async () => {
    try {
      const res = await api.get('/compliance/notifications');
      setNotifications(res.data);
    } catch (e) {
      console.error("Failed to fetch notifications", e);
    }
  };

  // Unread Messages State
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);

  // Jobs / Placement Drives State
  const [jobs, setJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [hasNewJobs, setHasNewJobs] = useState(false);
  const [showOnlyMatchedJobs, setShowOnlyMatchedJobs] = useState(true);
  const [jobsSubTab, setJobsSubTab] = useState('available');
  const [selectedJobDetailModal, setSelectedJobDetailModal] = useState(null);
  const [showSkillGapAnalysis, setShowSkillGapAnalysis] = useState(false);
  const [skillGapTab, setSkillGapTab] = useState('technical');
  const [classifyingSkills, setClassifyingSkills] = useState(false);
  const [classifiedSkillsData, setClassifiedSkillsData] = useState(null);
  const [skillGapError, setSkillGapError] = useState(null);
  const [customJobRole, setCustomJobRole] = useState('');

  // Campus Job Openings (TPO Internal Postings) State
  const [campusJobView, setCampusJobView] = useState('campus'); // 'campus' | 'placement'
  const [internalJobs, setInternalJobs] = useState([]);
  const [loadingInternalJobs, setLoadingInternalJobs] = useState(false);
  const [submittingInterestId, setSubmittingInterestId] = useState(null);
  const [selectedInternalJobModal, setSelectedInternalJobModal] = useState(null);

  useEffect(() => {
    if (!canViewInternalJobs && campusJobView === 'campus' && canViewPlacementDrives) {
      setCampusJobView('placement');
    } else if (!canViewPlacementDrives && campusJobView === 'placement' && canViewInternalJobs) {
      setCampusJobView('campus');
    }
  }, [canViewInternalJobs, canViewPlacementDrives, campusJobView]);

  const fetchInternalJobs = async () => {
    setLoadingInternalJobs(true);
    try {
      const res = await api.get('/student/internal-jobs');
      setInternalJobs(res.data || []);
    } catch (err) {
      console.error('Failed to fetch internal campus jobs', err);
    } finally {
      setLoadingInternalJobs(false);
    }
  };

  const handleShowInterest = async (jobId) => {
    setSubmittingInterestId(jobId);
    try {
      const res = await api.post(`/student/internal-jobs/${jobId}/show-interest`);
      showToast(res.data?.message || 'Interest registered successfully!');
      await fetchInternalJobs();
      if (selectedInternalJobModal && selectedInternalJobModal.id === jobId) {
        setSelectedInternalJobModal(prev => ({
          ...prev,
          has_shown_interest: true,
          interest_status: 'INTERESTED'
        }));
      }
    } catch (err) {
      console.error('Failed to show interest', err);
      showToast(err.response?.data?.detail || 'Failed to express interest', 'error');
    } finally {
      setSubmittingInterestId(null);
    }
  };

  const [downloadingJdId, setDownloadingJdId] = useState(null);

  const handleDownloadJobJd = async (job) => {
    if (!job?.id) return;
    setDownloadingJdId(job.id);
    try {
      showToast('Downloading job description...', 'info');
      const token = localStorage.getItem('token');
      const res = await api.get(`/student/internal-jobs/${job.id}/download-jd`, {
        params: token ? { token } : {},
        responseType: 'blob'
      });
      const blob = new Blob([res.data], {
        type: res.headers['content-type'] || 'application/pdf'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      let filename = job.jd_file_name || `JD_${(job.title || 'Role').replace(/[^a-zA-Z0-9_-]/g, '_')}.pdf`;
      const disposition = res.headers['content-disposition'];
      if (disposition && disposition.includes('filename=')) {
        const matches = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (matches && matches[1]) {
          filename = matches[1].replace(/['"]/g, '');
        }
      }
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showToast('Job description downloaded!', 'success');
    } catch (err) {
      console.error('Failed to download JD', err);
      let errorMsg = 'Failed to download Job Description';
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const json = JSON.parse(text);
          if (json.detail) errorMsg = json.detail;
        } catch (_) {}
      } else if (err.response?.data?.detail) {
        errorMsg = err.response.data.detail;
      }
      showToast(errorMsg, 'error');
    } finally {
      setDownloadingJdId(null);
    }
  };

  const handleSkillGapAnalysis = (job) => {
    requireAiDisclaimer('AI Skill Gap Analysis', () => _runSkillGapAnalysis(job));
  };

  const _runSkillGapAnalysis = async (job) => {
    setClassifyingSkills(true);
    setSkillGapError(null);
    setShowSkillGapAnalysis(false);
    const must_have_raw = Array.isArray(job.must_have_skills) ? job.must_have_skills : (job.must_have_skills ? String(job.must_have_skills).split(',') : []);
    const good_to_have_raw = Array.isArray(job.good_to_have_skills) ? job.good_to_have_skills : (job.good_to_have_skills ? String(job.good_to_have_skills).split(',') : []);
    
    const must_have = must_have_raw.map(s => typeof s === 'object' && s !== null ? s.skill : s).map(s => String(s).trim()).filter(Boolean);
    const good_to_have = good_to_have_raw.map(s => typeof s === 'object' && s !== null ? s.skill : s).map(s => String(s).trim()).filter(Boolean);
    
    // Get student's explicit profile skills and AI-detected tools
    const explicitSkills = (profile?.skills_list || profile?.skills || []).map(s => String(s).toLowerCase());
    const aiTools = (profile?.ai_toolset || []).map(t => String(t.tool || t.name).toLowerCase());
    const aiSkills = (profile?.ai_skillset || []).map(s => String(s.name).toLowerCase());
    const studentSkillsList = Array.from(new Set([...explicitSkills, ...aiTools, ...aiSkills]));

    // Format projects to pass to AI classifier
    const projectsList = (profile?.github_projects || []).map(p => ({
      title: p.title || p.name || p.repo || 'Project',
      description: (p.description || '').slice(0, 160),
      techStack: p.techStack || p.language || '',
      url: p.html_url || p.url || ''
    }));

    const jobName = job.job_name || job.title || 'Job Role';

    try {
      const res = await api.post('/ai-agents/classify-skills', {
        job_name: jobName,
        job_description: job.description || '',
        responsibilities: job.responsibilities || '',
        must_have_skills: must_have,
        good_to_have_skills: good_to_have,
        student_skills: studentSkillsList,
        projects: projectsList,
        resume_summary: (profile?.resume_text || '').slice(0, 2000)
      });
      setClassifiedSkillsData(res.data);
      
      // Inject AI inferred skills back into the job object for the UI to display
      const aiGeneratedTools = (res.data.tools || []).map(t => typeof t === 'object' ? t.name : t);
      const aiGeneratedSkills = (res.data.skills || []).map(s => typeof s === 'object' ? s.name : s);
      const updatedJob = {
        ...job,
        job_name: jobName,
        must_have_skills: (job.must_have_skills && job.must_have_skills.length > 0) ? job.must_have_skills : aiGeneratedTools,
        good_to_have_skills: (job.good_to_have_skills && job.good_to_have_skills.length > 0) ? job.good_to_have_skills : aiGeneratedSkills
      };
      
      setSelectedJobDetailModal(updatedJob);
      setShowSkillGapAnalysis(true);
      setSkillGapTab('tools'); // Default to tools tab
    } catch (error) {
      console.error('Error classifying skills:', error);
      const errorDetail = String(error.response?.data?.detail || error.message || '').toLowerCase();
      const tokenLimitReached = error.response?.status === 403
        && (errorDetail.includes('token') || errorDetail.includes('usage'));
      const providerTokenLimitReached = errorDetail.includes('quota')
        || errorDetail.includes('credit')
        || errorDetail.includes('insufficient token')
        || errorDetail.includes('token limit');

      if (tokenLimitReached || providerTokenLimitReached) {
        setSkillGapError('AI token limit reached. Please ask your provider or university administrator to grant additional AI tokens, then try again.');
        showToast('AI token limit reached. Please ask your provider to grant additional AI tokens.', 'error');
      } else {
        setSkillGapError('Unable to generate the analysis right now. Please try again later.');
        showToast('Unable to generate the analysis right now. Please try again later.', 'error');
      }
    } finally {
      setClassifyingSkills(false);
    }
  };

  const fetchJobs = async (isBackground = false) => {
    if (!isBackground) setLoadingJobs(true);
    try {
      const res = await api.get('/student/jobs');
      const fetchedJobs = res.data || [];
      setJobs(fetchedJobs);

      // Check for new placement drives added by TPO that haven't been seen by this student
      const seenJobs = JSON.parse(localStorage.getItem('seen_student_jobs') || '[]');
      const hasUnseen = fetchedJobs.some(j => !seenJobs.includes(j.drive_id));
      if (hasUnseen) {
        if (activeTab === 'jobs') {
          localStorage.setItem('seen_student_jobs', JSON.stringify(fetchedJobs.map(j => j.drive_id)));
          setHasNewJobs(false);
        } else {
          setHasNewJobs(true);
        }
      } else {
        setHasNewJobs(false);
      }
    } catch (err) {
      console.error('Failed to fetch student jobs', err);
    } finally {
      if (!isBackground) setLoadingJobs(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'jobs' && jobs.length > 0) {
      localStorage.setItem('seen_student_jobs', JSON.stringify(jobs.map(j => j.drive_id)));
      setHasNewJobs(false);
    }
  }, [activeTab, jobs]);

  const handleMantrikaHandoff = async (job) => {
    try {
      const handoffRes = await api.post('/api/campus/apply/handoff', { job_id: job.external_job_id });
      const loginPath = handoffRes.data.loginPath;
      if (!loginPath) {
        throw new Error("Missing loginPath in response");
      }
      
      const redirectUrl = new URL(
        loginPath.startsWith('/') ? loginPath : `/${loginPath}`, 
        'https://dev-mantrika.azurewebsites.net'
      ).href;
      
      window.location.href = redirectUrl;
    } catch (err) {
      console.error('Failed to handoff to Mantrika', err);
      showToast(err.response?.data?.detail || err.message || 'Failed to redirect to Mantrika', 'error');
    }
  };

  const handleMantrikaInterview = (job) => {
    requireAiDisclaimer('AI Interview', () => _runMantrikaInterview(job));
  };

  const _runMantrikaInterview = async (job) => {
    try {
      const payload = { job_id: job.external_job_id };
      if (job.interview_intent) payload.intent = job.interview_intent;
      if (job.interview_panel_id) payload.panelId = job.interview_panel_id;
      const handoffRes = await api.post('/api/campus/interview/handoff', payload);
      const launchUrl = handoffRes.data.launchUrl;
      if (!launchUrl) {
        throw new Error("Missing launchUrl in response");
      }
      
      const popupWindow = window.open(launchUrl, 'mantrika-interview', 'width=1100,height=780');
      
      if (!popupWindow) {
        showToast("Popup blocked! Please allow popups for this site and try again.", "error");
        return;
      }

      // Check if popup closes to refresh state
      const checkClosed = setInterval(() => {
        if (popupWindow.closed) {
          clearInterval(checkClosed);
          fetchJobs();
        }
      }, 2000);

    } catch (err) {
      console.error('Failed to launch Mantrika interview', err);
      showToast(err.response?.data?.detail || err.message || 'Failed to launch interview', 'error');
    }
  };

  const handleApplyJob = async (job) => {
    try {
      const res = await api.post('/student/jobs/apply', { drive_id: job.drive_id });
      showToast(res.data?.message || 'Application submitted successfully!', 'success');
      fetchJobs();
    } catch (err) {
      console.error('Failed to apply for job', err);
      showToast(err.response?.data?.detail || 'Failed to apply for job', 'error');
    }
  };


  const fetchUnreadMessages = async () => {
    try {
      const res = await api.get('/chat/conversations');
      const totalUnread = res.data.reduce((acc, curr) => acc + (curr.unread_count || 0), 0);
      setUnreadMessagesCount(totalUnread);
    } catch (err) {
      console.error("Failed to fetch unread messages", err);
    }
  };

  useEffect(() => {
    let intervalId = null;
    let unreadIntervalId = null;
    let isCancelled = false;

    // Stagger secondary fetches slightly so they don't compete with the
    // critical profile + sessions calls that render the dashboard skeleton.
    const timer = setTimeout(() => {
      if (isCancelled) return;
      fetchNotifs();
      fetchJobs();
      fetchInternalJobs();
      intervalId = setInterval(() => {
        fetchJobs(true);
        fetchInternalJobs();
      }, 60000);
      // Unread-message badge polls a messages_view-gated endpoint; skip when the
      // sub is disabled so the console isn't spammed with 403s.
      if (isSubEnabled('messages', 'messages_view')) {
        fetchUnreadMessages();
        unreadIntervalId = setInterval(fetchUnreadMessages, 60000);
      }
    }, 50);

    return () => {
      isCancelled = true;
      clearTimeout(timer);
      if (intervalId) clearInterval(intervalId);
      if (unreadIntervalId) clearInterval(unreadIntervalId);
    };
  }, []);



  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    if (tabParam) {
      setActiveTab(tabParam);
    } else if (location.state?.showSessions) {
      setActiveTab('sessions');
    } else if (location.state?.showSettings || location.state?.showProfile) {
      setActiveTab('settings');
    } else if (location.state?.showDiscover) {
      setActiveTab('discover');
    } else if (location.state?.showDiscussions) {
      setActiveTab('discussions');
    } else if (location.state?.showChat) {
      setActiveTab('chat');
    } else if (location.state?.showCompleted) {
      setActiveTab('completed');
    } else if (location.state?.showLMS) {
      setActiveTab('lms');
    } else if (location.state?.showJobs) {
      setActiveTab('jobs');
    } else if (location.state?.showPlatformFeedback) {
      setActiveTab('platform_feedback');
    } else {
      setActiveTab('explore');
    }
    fetchDashData();
  }, [location.pathname, location.state, location.search]);

  useEffect(() => {
    const fetchSkills = async () => {
      if (newSkillInput.trim().length > 0) {
        try {
          const res = await api.get(`/public/skills/search?q=${newSkillInput}`);
          setSkillSuggestions(res.data.map(s => s.name));
        } catch (err) {
          console.error("Search error:", err);
        }
      } else {
        setSkillSuggestions([]);
      }
    };

    const timeoutId = setTimeout(fetchSkills, 200);
    return () => clearTimeout(timeoutId);
  }, [newSkillInput]);

  const fetchDashData = async () => {
    setLoading(true);
    try {
      // Fetch profile and session feeds in parallel
      const [profResult, exploreResult, reqsResult, uniResult] = await Promise.allSettled([
        api.get('/student/profile/me'),
        api.get('/student/public-sessions/explore'),
        api.get('/student/my-session-requests'),
        api.get('/student/university-sessions')
      ]);

      if (profResult.status === 'fulfilled' && profResult.value?.data) {
        const profData = profResult.value.data;
        setProfile(profData);
        // Initialize settings form
        setSettingsForm({
          full_name: profData.full_name || '',
          phone_number: profData.phone_number || '',
          institution_name: profData.university_name || profData.institution_name || '',
          degree: profData.degree || '',
          graduation_year: profData.graduation_year || '',
          department: profData.department || '',
          year_of_admission: profData.year_of_admission || '',
          admission_number: profData.admission_number || '',
          usn: profData.usn || '',
          gender: (profData.gender || '').toLowerCase().startsWith('m') ? 'Male' : (profData.gender || '').toLowerCase().startsWith('f') ? 'Female' : profData.gender ? 'Other' : '',
          academic_year: profData.academic_year || '',
          academic_batch: profData.academic_batch || '',
          admission_semester: profData.admission_semester || '',
          branch: profData.branch || '',
          graduation_level: profData.graduation_level || '',
          program: profData.program || '',
          student_date_of_birth: profData.student_date_of_birth || '',
          github_profile: profData.github_profile || ''
        });
      }

      let allPublicSessions = [];
      if (exploreResult.status === 'fulfilled' && exploreResult.value?.data) {
        allPublicSessions = [...exploreResult.value.data];
      }

      if (uniResult.status === 'fulfilled' && uniResult.value?.data) {
        const uniSessions = uniResult.value.data.map(req => ({
          ...req,
          id: `req_${req.id}`, // Avoid collision with public sessions
          is_university_requirement: true
        }));
        // Merge them in. They will be filtered into university categories by the existing useMemo hooks.
        allPublicSessions = [...allPublicSessions, ...uniSessions];
      }

      setPublicSessions(allPublicSessions);
      if (reqsResult.status === 'fulfilled' && reqsResult.value?.data) {
        setMyPersonalRequests(reqsResult.value.data);
      }

    } catch (err) {
      console.error("Dashboard data fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    const keysToPreserve = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('read_remarks') || key.startsWith('seen_student_jobs'))) {
        keysToPreserve.push({ key, value: localStorage.getItem(key) });
      }
    }
    
    localStorage.clear();
    
    keysToPreserve.forEach(item => {
      localStorage.setItem(item.key, item.value);
    });
    
    clearUIRulesCache();
    window.dispatchEvent(new Event('auth_changed'));
    navigate('/login');
  };

  const handleJoinPublicSession = async (sessionId) => {
    try {
      if (typeof sessionId === 'string' && sessionId.startsWith('req_')) {
        const reqId = sessionId.replace('req_', '');
        const response = await api.post(`/student/university-sessions/${reqId}/enroll`);
        showToast(response.data.message || "Successfully enrolled in the university program!", 'success');
        fetchDashData();
        return;
      }
      const response = await api.post(`/student/public-sessions/join/${sessionId}`);
      showToast(response.data.message || "Successfully joined the masterclass!", 'success');
      fetchDashData();
    } catch (err) {
      showToast(err.response?.data?.detail || "Enrollment failed.", 'error');
    }
  };

  // --- SETTINGS FORM STATE & LOGIC ---
  const [settingsForm, setSettingsForm] = useState({
    full_name: '',
    phone_number: '',
    institution_name: '',
    delivery_mode: 'Remote',
    location: '',
    year_of_admission: '',
    admission_number: '',
    usn: '',
    gender: '',
    academic_year: '',
    academic_batch: '',
    admission_semester: '',
    branch: '',
    graduation_level: '',
    program: '',
    student_date_of_birth: '',
    github_profile: ''
  });

  // --- SETTINGS TABS & PORTFOLIO STATE ---
  const [settingsSubTab, setSettingsSubTab] = useState('general'); // 'toolset', 'skillset', 'mindset', 'projects', 'general', 'experience', 'certificates', 'assessment', 'marks_cards', 'courses_marks'
  const [coursesAndMarks, setCoursesAndMarks] = useState([]);
  const [loadingCoursesAndMarks, setLoadingCoursesAndMarks] = useState(false);

  // Guard: only fetch courses-and-marks once on mount, and re-fetch only when
  // the user explicitly opens the 'courses_marks' sub-tab (not on every tab switch).
  const hasFetchedCoursesRef = useRef(false);

  useEffect(() => {
    const isCoursesTab = settingsSubTab === 'courses_marks';
    // Fetch on first mount OR whenever the user navigates to the courses tab
    if (hasFetchedCoursesRef.current && !isCoursesTab) return;
    const fetchCoursesAndMarks = async () => {
      try {
        setLoadingCoursesAndMarks(true);
        const res = await api.get('/student/courses-and-marks');
        setCoursesAndMarks(res.data || []);
        hasFetchedCoursesRef.current = true;
      } catch (err) {
        console.error('Error fetching student courses and marks:', err);
      } finally {
        setLoadingCoursesAndMarks(false);
      }
    };
    fetchCoursesAndMarks();
  }, [activeTab, settingsSubTab]);



  // Marks Card State
  const [activeMarksCardSemester, setActiveMarksCardSemester] = useState(1);
  const [isUploadingMarksCard, setIsUploadingMarksCard] = useState(false);
  const marksCardInputRef = useRef(null);
  const [marksCardForm, setMarksCardForm] = useState({ sgpa: '', courses: '', file_url: '', file_name: '' });

  useEffect(() => {
    const existing = parseJsonSafe(profile?.marks_cards, []);
    const mc = existing.find(m => m.semester === activeMarksCardSemester);
    if (mc) {
      setMarksCardForm({ sgpa: mc.sgpa || '', courses: mc.courses || '', file_url: mc.file_url || '', file_name: mc.file_name || '' });
    } else {
      setMarksCardForm({ sgpa: '', courses: '', file_url: '', file_name: '' });
    }
  }, [activeMarksCardSemester, profile?.marks_cards]);

  const [githubModalOpen, setGithubModalOpen] = useState(false);
  const [githubUsernameInput, setGithubUsernameInput] = useState('');
  const [githubPatInput, setGithubPatInput] = useState('');
  const [isFetchingGithub, setIsFetchingGithub] = useState(false);
  const [githubOAuthError, setGithubOAuthError] = useState(null);

  const [customProjectModalOpen, setCustomProjectModalOpen] = useState(false);
  const [showProjectSuccessModal, setShowProjectSuccessModal] = useState(false);
  const [customProjectForm, setCustomProjectForm] = useState({
    title: '', description: '', techStack: '', repoUrl: '', liveUrl: '', language: 'JavaScript'
  });

  const [certModalOpen, setCertModalOpen] = useState(false);
  const [certForm, setCertForm] = useState({
    title: '', issuer: '', issueDate: '', credentialId: '', credentialUrl: '', category: 'Technical'
  });

  const [isAnalyzingAi, setIsAnalyzingAi] = useState(false);
  const [projectAiModalOpen, setProjectAiModalOpen] = useState(false);
  const [selectedProjectAi, setSelectedProjectAi] = useState(null);
  const [isAnalyzingProject, setIsAnalyzingProject] = useState(false);

  const hasTriggeredAiRef = useRef(false);
  const resumeFileInputRef = useRef(null);
  const [isUploadingResume, setIsUploadingResume] = useState(false);
  const [isExtractingResume, setIsExtractingResume] = useState(false);
  const [resumeViewerModalOpen, setResumeViewerModalOpen] = useState(false);
  const [resumeModalTab, setResumeModalTab] = useState('pdf');
  const [resumePdfBlobUrl, setResumePdfBlobUrl] = useState(null);
  const [isLoadingPdfBlob, setIsLoadingPdfBlob] = useState(false);
  const [pdfFetchError, setPdfFetchError] = useState(null);

  useEffect(() => {
    if (!resumeViewerModalOpen) {
      if (resumePdfBlobUrl) {
        URL.revokeObjectURL(resumePdfBlobUrl);
        setResumePdfBlobUrl(null);
      }
      setPdfFetchError(null);
      return;
    }

    let isMounted = true;
    setIsLoadingPdfBlob(true);
    setPdfFetchError(null);

    let targetUrl = '';
    if (profile?.resume_url) {
      if (profile.resume_url.startsWith('http')) {
        targetUrl = profile.resume_url;
      } else {
        const cleanPath = profile.resume_url.replace('/api/documents/', '').replace(/^\/+/, '');
        targetUrl = `/api/documents/${cleanPath}`;
      }
    } else {
      targetUrl = `/api/documents/resumes/resume_${profile?.user_id || profile?.id || ''}.pdf`;
    }

    api.get(targetUrl, { responseType: 'blob' })
      .then(response => {
        if (!isMounted) return;
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const blobUrl = URL.createObjectURL(blob);
        setResumePdfBlobUrl(blobUrl);
      })
      .catch(err => {
        console.error("Error fetching PDF blob:", err);
        if (!isMounted) return;
        const fallbackUrl = `/api/documents/resumes/resume_${profile?.user_id || profile?.id || ''}.pdf`;
        if (targetUrl !== fallbackUrl) {
          api.get(fallbackUrl, { responseType: 'blob' })
            .then(res2 => {
              if (!isMounted) return;
              const blob2 = new Blob([res2.data], { type: 'application/pdf' });
              setResumePdfBlobUrl(URL.createObjectURL(blob2));
            })
            .catch(err2 => {
              if (!isMounted) return;
              setPdfFetchError("Could not load PDF file. Please check AI Extracted Text or re-upload.");
            });
        } else {
          setPdfFetchError("Could not load PDF file. Please check AI Extracted Text or re-upload.");
        }
      })
      .finally(() => {
        if (isMounted) setIsLoadingPdfBlob(false);
      });

    return () => {
      isMounted = false;
    };
  }, [resumeViewerModalOpen, profile]);
  const [isGeneratingQuiz, setIsGeneratingQuiz] = useState(false);
  const [activeQuiz, setActiveQuiz] = useState(null);
  const [currentQuestionIdx, setCurrentQuestionIdx] = useState(0);
  const [userAnswers, setUserAnswers] = useState({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);

  const [isEvaluatingQuiz, setIsEvaluatingQuiz] = useState(false);
  const [quizEvaluationResult, setQuizEvaluationResult] = useState(null);
  const [isDisqualified, setIsDisqualified] = useState(false);
  const [disqualificationData, setDisqualificationData] = useState(null);
  const [proctorReportQueued, setProctorReportQueued] = useState(false);
  const [showAssessmentRulesModal, setShowAssessmentRulesModal] = useState(false);
  const [appealReason, setAppealReason] = useState('');
  const [isSubmittingAppeal, setIsSubmittingAppeal] = useState(false);
  const [appealSubmittedSuccess, setAppealSubmittedSuccess] = useState(false);
  const [showRetakeAppealModal, setShowRetakeAppealModal] = useState(false);

  const handleRequestRetakeAppeal = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!appealReason || appealReason.trim().length < 8) {
      showToast("Please provide a clear explanation (at least 8 characters) for your appeal.", "warning");
      return;
    }
    setIsSubmittingAppeal(true);
    try {
      const res = await api.post('/student/assessment/request-retake', { reason: appealReason.trim() });
      showToast(res.data?.message || "Appeal submitted to Faculty successfully!", "success");
      setAppealSubmittedSuccess(true);
      setShowRetakeAppealModal(false);
      await fetchDashData();
    } catch (err) {
      console.error("Retake appeal error:", err);
      const detail = err?.response?.data?.detail || "Failed to submit appeal. Please try again.";
      showToast(detail, "error");
    } finally {
      setIsSubmittingAppeal(false);
    }
  };

  // AI Disclaimer gate — stores a callback to run after user agrees
  const [showAiDisclaimer, setShowAiDisclaimer] = useState(false);
  const [aiDisclaimerFeature, setAiDisclaimerFeature] = useState('');
  const [pendingAiAction, setPendingAiAction] = useState(null);

  /** Show the disclaimer once per session; run action immediately if already agreed */
  const requireAiDisclaimer = (featureName, action) => {
    if (sessionStorage.getItem('ai_disclaimer_agreed')) {
      action();
    } else {
      setAiDisclaimerFeature(featureName);
      setPendingAiAction(() => action);
      setShowAiDisclaimer(true);
    }
  };

  const handleJoinAssessment = () => {
    const monthlyAssessment = profile?.monthly_limits?.assessment;
    const isDisq = profile?.assessment_disqualified || monthlyAssessment?.is_disqualified;
    const isRetake = profile?.retake_granted_by_faculty || monthlyAssessment?.retake_granted_by_faculty;

    if (isDisq && !isRetake) {
      setShowRetakeAppealModal(true);
      return;
    }

    if (!isRetake && monthlyAssessment && !monthlyAssessment.can_perform) {
      showToast(`Skill assessments can only be taken once per month. Next attempt available on ${monthlyAssessment.next_date_formatted} (${monthlyAssessment.days_remaining} days remaining).`, "warning");
      return;
    }

    requireAiDisclaimer('AI Skills Assessment', () => setShowAssessmentRulesModal(true));
  };

  const startAssessment = async () => {
    const monthlyAssessment = profile?.monthly_limits?.assessment;
    const isDisq = profile?.assessment_disqualified || monthlyAssessment?.is_disqualified;
    const isRetake = profile?.retake_granted_by_faculty || monthlyAssessment?.retake_granted_by_faculty;

    if (isDisq && !isRetake) {
      showToast("Your assessment was disqualified due to proctoring violations. Please submit an appeal to your Faculty.", "error");
      setShowRetakeAppealModal(true);
      return;
    }

    if (!isRetake && monthlyAssessment && !monthlyAssessment.can_perform) {
      showToast(`Skill assessments can only be taken once per month. Next attempt available on ${monthlyAssessment.next_date_formatted} (${monthlyAssessment.days_remaining} days remaining).`, "warning");
      return;
    }

    setShowAssessmentRulesModal(false);
    try {
      if (document.documentElement.requestFullscreen) {
        document.documentElement.requestFullscreen().catch(() => {});
      } else if (document.documentElement.webkitRequestFullscreen) {
        document.documentElement.webkitRequestFullscreen();
      }
    } catch (e) {}

    setIsGeneratingQuiz(true);
    setActiveQuiz(null);
    setCurrentQuestionIdx(0);
    setUserAnswers({});
    setQuizSubmitted(false);
    setQuizEvaluationResult(null);
    setIsEvaluatingQuiz(false);
    setIsDisqualified(false);
    setDisqualificationData(null);
    setProctorReportQueued(false);
    showToast("🤖 AI is analyzing your GitHub portfolio to generate a custom technical quiz...", "info");


    try {
      const res = await api.post('/student/github/generate-quiz');
      if (res.data && res.data.questions) {
        setActiveQuiz(res.data);
        if (res.data.source === 'fallback') {
          showToast("⚠️ AI quiz engine is unavailable — loaded a standard benchmark quiz instead. Contact support if this persists.", "warning");
        } else {
          showToast("✨ AI custom quiz generated successfully!", "success");
        }
      } else {
        showToast("Could not generate quiz. Please try again.", "error");
      }
    } catch (err) {
      console.error("Quiz generation error:", err);
      const failure = resolveQuizFailure(err);
      if (failure.loadBackup) {
        setActiveQuiz(OFFLINE_BACKUP_QUIZ);
      }
      showToast(failure.message, failure.loadBackup ? "warning" : "error");
    } finally {
      setIsGeneratingQuiz(false);
    }
  };

  const handleSubmitQuiz = async () => {
    const questions = activeQuiz?.questions || [];
    const totalQ = questions.length;
    if (Object.keys(userAnswers).length < totalQ) {
      showToast("Please answer all questions before submitting!", "warning");
    }
    setQuizSubmitted(true);
    setIsEvaluatingQuiz(true);
    setQuizEvaluationResult(null);
    showToast("🤖 AI is evaluating your test performance against your GitHub Toolset & Skillset...", "info");

    let correct = 0;
    const breakdown = questions.map((q, idx) => {
      const isCorrect = userAnswers[idx] === q.correct_answer;
      if (isCorrect) correct++;
      return {
        question: q.question,
        project_reference: q.project_reference,
        is_correct: isCorrect,
        selected_option: userAnswers[idx] !== undefined ? q.options[userAnswers[idx]] : "Not answered"
      };
    });
    const score = Math.round((correct / (totalQ || 1)) * 100);

    try {
      const res = await api.post('/student/github/evaluate-quiz', {
        score_percentage: score,
        total_questions: totalQ,
        correct_answers: correct,
        question_breakdown: breakdown
      });
      setQuizEvaluationResult(res.data);
      await fetchDashData();
      showToast("✨ AI Skillset & Toolset alignment updated!", "success");
    } catch (err) {
      console.error("Quiz evaluation error:", err);
      // Always refresh profile so attempt count updates even if OpenRouter AI failed
      await fetchDashData();
      showToast("Could not complete AI alignment analysis, using local verification.", "warning");
    } finally {
      setIsEvaluatingQuiz(false);
    }
  };



  const handleResumeFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const resumeLimit = profile?.monthly_limits?.resume;
    if (resumeLimit && !resumeLimit.can_perform) {
      showToast(`Resume upload is limited to once a month for skill gap tracking. Next upload available on ${resumeLimit.next_date_formatted} (${resumeLimit.days_remaining} days remaining).`, 'warning');
      if (resumeFileInputRef.current) resumeFileInputRef.current.value = '';
      return;
    }

    if (file.type !== 'application/pdf') {
      showToast('Please upload a PDF file.', 'error');
      return;
    }
    setIsUploadingResume(true);
    showToast('📄 Uploading and parsing resume...', 'info');
    try {
      const data = new FormData();
      data.append('resume', file);
      await api.post('/ai-agents/upload-resume', data);
      await fetchDashData();
      showToast('✅ Resume uploaded and parsed successfully!', 'success');
    } catch (err) {
      console.error('Resume upload error:', err);
      showToast(err.response?.data?.detail || 'Failed to upload resume.', 'error');
    } finally {
      setIsUploadingResume(false);
      if (resumeFileInputRef.current) resumeFileInputRef.current.value = '';
    }
  };

  const handleExtractFromResume = async () => {
    if (!profile?.resume_url && !profile?.resume_text) {
      showToast("Please upload a resume first!", "error");
      return;
    }
    setIsExtractingResume(true);
    showToast("🤖 Extracting Experience & Awards from resume...", "info");
    try {
      await api.post('/ai-agents/extract-resume-data', {});
      await fetchDashData();
      showToast("✨ Successfully extracted Experience & Awards from your resume!", "success");
    } catch (err) {
      console.error("Extraction failed:", err);
      showToast(err.response?.data?.detail || "Failed to extract from resume", "error");
    } finally {
      setIsExtractingResume(false);
    }
  };

  const handleRunAiAnalysis = async () => {
    if (!profile?.github_projects || profile.github_projects.length === 0) {
      showToast("Connect your GitHub account first to analyze projects!", "error");
      return;
    }
    requireAiDisclaimer('AI Portfolio Analysis', async () => {
    hasTriggeredAiRef.current = true;
    setIsAnalyzingAi(true);
    showToast("🤖 Analyzing GitHub repositories with AI...", "info");
    try {
      await api.post('/student/github/analyze', {});
      await fetchDashData();
      showToast("✨ AI analysis complete! Toolset & Skillset updated.", "success");
    } catch (err) {
      console.error("AI Analysis failed:", err);
      showToast(err.response?.data?.detail || "AI Analysis failed", "error");
    } finally {
      setIsAnalyzingAi(false);
    }
    });
  };

  const handleAnalyzeProjectClick = async (project) => {
    if (!project) return;
    setSelectedProjectAi({ ...project, loading: true });
    setProjectAiModalOpen(true);
    setIsAnalyzingProject(true);
    try {
      const title = project.title || project.name || project.repo || 'Untitled Project';
      const description = project.description || 'No description provided.';
      const language = project.language || '';
      const techStack = project.techStack || (Array.isArray(project.topics) ? project.topics.join(', ') : '');
      const repoUrl = project.repoUrl || project.html_url || project.url || '';

      const res = await api.post('/student/project/analyze', {
        project_title: title,
        project_description: description,
        language: language,
        tech_stack: techStack,
        repo_url: repoUrl
      });
      setSelectedProjectAi({ ...project, analysis: res.data, loading: false });
    } catch (err) {
      console.error("Project AI analysis failed:", err);
      showToast(err.response?.data?.detail || "Failed to analyze project with AI", "error");
      setSelectedProjectAi({ ...project, error: true, loading: false });
    } finally {
      setIsAnalyzingProject(false);
    }
  };

  const parseJsonSafe = (val, defaultVal = []) => {
    if (!val) return defaultVal;
    if (typeof val === 'string') {
      try { return JSON.parse(val); } catch (e) { return defaultVal; }
    }
    return Array.isArray(val) ? val : defaultVal;
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const code = searchParams.get('code');
    const error = searchParams.get('error');
    if (error) {
      // GitHub bounced us back with an error (e.g. user denied access)
      window.history.replaceState({}, document.title, window.location.pathname);
      sessionStorage.removeItem('github_oauth_state');
      const message = error === 'access_denied'
        ? 'GitHub access was denied. If you want to connect, please try again and approve the access request.'
        : `GitHub OAuth failed: ${error}. Please try connecting again.`;
      setGithubOAuthError(message);
      showToast(message, "error");
      return;
    }
    if (code) {
      window.history.replaceState({}, document.title, window.location.pathname);
      // CSRF protection: verify the OAuth state echoes back what we generated
      const state = searchParams.get('state');
      const expectedState = sessionStorage.getItem('github_oauth_state');
      sessionStorage.removeItem('github_oauth_state');
      if (state && expectedState && state !== expectedState) {
        const message = "GitHub OAuth state mismatch. Please try connecting again.";
        setGithubOAuthError(message);
        showToast(message, "error");
        return;
      }
      handleGithubOAuthCallback(code);
    }
  }, []);

  const handleGithubOAuthCallback = async (code) => {
    setIsFetchingGithub(true);
    setGithubModalOpen(true);
    try {
      const res = await api.post('/student/github/sync', { code });
      showToast(`Synced ${res.data.repo_count} repositories from GitHub! Re-analyzing Toolset & Skillset with AI...`, "info");
      
      setIsAnalyzingAi(true);
      try {
        await api.post('/student/github/analyze', {});
        showToast("✨ AI re-analysis complete! Toolset & Skillset updated from your repositories.", "success");
      } catch (aiErr) {
        console.error("AI analysis error during sync:", aiErr);
        showToast(aiErr.response?.data?.detail || "AI analysis busy or failed. Please try syncing again.", "error");
      } finally {
        setIsAnalyzingAi(false);
      }
      
      fetchDashData();
      setSettingsSubTab('projects');
      setShowProjectSuccessModal(true);
    } catch (err) {
      console.error(err);
      showToast(err.response?.data?.detail || "Failed to sync GitHub projects.", "error");
    } finally {
      setIsFetchingGithub(false);
      setGithubModalOpen(false);
    }
  };

  const handleConnectGithub = async () => {
    const projLimit = profile?.monthly_limits?.project;
    if (projLimit && !projLimit.can_perform) {
      showToast(`GitHub repository sync is limited to once a month for skill gap tracking. Next sync available on ${projLimit.next_date_formatted} (${projLimit.days_remaining} days remaining).`, 'warning');
      return;
    }
    setGithubOAuthError(null);
    try {
      const res = await api.get('/student/github/config');
      const clientId = res.data.client_id;
      if (!clientId) {
          const msg = "GitHub OAuth Client ID is not configured on the backend.";
          setGithubOAuthError(msg);
          showToast(msg, "error");
          return;
      }
      const redirectUri = window.location.origin + '/student-dashboard';
      // Pre-flight redirect_uri validation: GitHub shows its own error page and never
      // returns to the app on a mismatch, so fail loudly HERE with clear guidance.
      const allowed = Array.isArray(res.data.allowed_redirect_uris) ? res.data.allowed_redirect_uris : [];
      if (allowed.length > 0 && !allowed.includes(redirectUri)) {
        const msg = `GitHub OAuth redirect_uri mismatch: "${redirectUri}" is not registered as a callback URL in the GitHub OAuth app. Add this exact URL in GitHub → Settings → Developer settings → OAuth Apps, then try again.`;
        setGithubOAuthError(msg);
        showToast(msg, "error");
        return;
      }
      const state = Math.random().toString(36).substring(2) + Date.now().toString(36);
      sessionStorage.setItem('github_oauth_state', state);
      const githubAuthUrl = `https://github.com/login/oauth/authorize?client_id=${clientId}&redirect_uri=${redirectUri}&scope=repo,read:user&prompt=consent&state=${state}`;
      window.location.href = githubAuthUrl;
    } catch (err) {
      console.error(err);
      const msg = "Failed to fetch GitHub OAuth configuration.";
      setGithubOAuthError(msg);
      showToast(msg, "error");
    }
  };

  const handleDisconnectGithub = async () => {
    if (!window.confirm("Are you sure you want to disconnect your GitHub account? This will remove imported repositories from your profile.")) return;
    try {
      setIsFetchingGithub(true);
      await api.post('/student/github/disconnect');
      showToast("GitHub account disconnected.", "success");
      setGithubModalOpen(false);
      fetchDashData();
    } catch (err) {
      console.error(err);
      showToast("Failed to disconnect GitHub account.", "error");
    } finally {
      setIsFetchingGithub(false);
    }
  };

  const handleAddCustomProject = async (e) => {
    e.preventDefault();
    const projLimit = profile?.monthly_limits?.project;
    if (projLimit && !projLimit.can_perform) {
      showToast(`Project uploads are limited to once a month for skill gap tracking. Next project update unlocks on ${projLimit.next_date_formatted} (${projLimit.days_remaining} days remaining).`, 'warning');
      return;
    }
    if (!customProjectForm.title) {
      showToast("Project title is required", "error");
      return;
    }
    try {
      const existingProjects = parseJsonSafe(profile?.github_projects, []);
      const newProject = {
        id: Math.random().toString(36).substring(2, 9),
        title: customProjectForm.title,
        description: customProjectForm.description || '',
        techStack: customProjectForm.techStack || '',
        repoUrl: customProjectForm.repoUrl || '',
        liveUrl: customProjectForm.liveUrl || '',
        language: customProjectForm.language || 'Other',
        isGithub: false,
        updatedAt: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
      };

      const updatedProjects = [newProject, ...existingProjects];
      await api.put('/student/profile/update', { github_projects: updatedProjects });
      showToast("Project added successfully! Running AI analysis...", "success");
      setCustomProjectModalOpen(false);
      setCustomProjectForm({ title: '', description: '', techStack: '', repoUrl: '', liveUrl: '', language: 'JavaScript' });
      setShowProjectSuccessModal(true);
      try {
        await api.post('/student/github/analyze', {});
        showToast("✨ AI analysis complete! Toolset & Skillset updated.", "success");
      } catch (aiErr) {
        console.error("AI analysis error:", aiErr);
      }
      fetchDashData();
    } catch (err) {
      showToast("Failed to add project", "error");
    }
  };

  const handleDeleteProject = async (projectId) => {
    try {
      const existingProjects = parseJsonSafe(profile?.github_projects, []);
      const updatedProjects = existingProjects.filter(p => p.id !== projectId);
      await api.put('/student/profile/update', { github_projects: updatedProjects });
      showToast("Project removed. Re-running AI analysis...", "success");
      try {
        await api.post('/student/github/analyze', {});
        showToast("✨ AI analysis complete! Toolset & Skillset updated.", "success");
      } catch (aiErr) {
        console.error("AI analysis error:", aiErr);
      }
      fetchDashData();
    } catch (err) {
      showToast("Failed to delete project", "error");
    }
  };

  const handleAddCertificate = async (e) => {
    e.preventDefault();
    if (!certForm.title || !certForm.issuer) {
      showToast("Title and Issuer are required", "error");
      return;
    }
    try {
      const existingCerts = parseJsonSafe(profile?.certificates, []);
      const newCert = {
        id: Math.random().toString(36).substring(2, 9),
        title: certForm.title,
        issuer: certForm.issuer,
        issueDate: certForm.issueDate || new Date().toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
        credentialId: certForm.credentialId || '',
        credentialUrl: certForm.credentialUrl || '',
        category: certForm.category || 'Technical'
      };

      const updatedCerts = [newCert, ...existingCerts];
      await api.put('/student/profile/update', { certificates: updatedCerts });
      showToast("Certificate added successfully!", "success");
      setCertModalOpen(false);
      setCertForm({ title: '', issuer: '', issueDate: '', credentialId: '', credentialUrl: '', category: 'Technical' });
      fetchDashData();
    } catch (err) {
      showToast("Failed to add certificate", "error");
    }
  };

  const handleDeleteCertificate = async (certId) => {
    try {
      const existingCerts = parseJsonSafe(profile?.certificates, []);
      const updatedCerts = existingCerts.filter(c => c.id !== certId);
      await api.put('/student/profile/update', { certificates: updatedCerts });
      showToast("Certificate removed", "success");
      fetchDashData();
    } catch (err) {
      showToast("Failed to delete certificate", "error");
    }
  };

  const handleSettingsChange = (e) => {
    const { name, value } = e.target;
    setSettingsForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveSettings = async () => {
    try {
      if (!settingsForm.full_name || !settingsForm.institution_name) {
        showToast("Name and Institution are required", 'error');
        return;
      }
      await api.put('/student/profile/update', settingsForm);
      showToast("Profile updated successfully", 'success');
      fetchDashData(); // Refresh to ensure sync
    } catch (err) {
      showToast(err.response?.data?.detail || "Update failed", 'error');
    }
  };

  const handleRequestRefund = (session) => {
    setRefundData({
      payment_id: session.payment_id,
      amount: session.proposed_amount || session.price || session.amount || 0,
      // (assuming formatDate helper exists or we format it here)
      professor_name: session.professor_name || session.professor?.pop_profile?.full_name || 'Assigned Expert',
      session_title: session.title || session.session_title || 'Session Details',
      session_date: session.event_date ? new Date(session.event_date.split('T')[0]).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A',
      reason: 'STUDENT_CANCELLATION',
      details: ''
    });
    setShowRefundModal(true);
    setShowDetails(null); // Assuming setShowDetails(null) closes the modal based on earlier code
  };

  const submitRefundRequest = async () => {
    try {
      if (!refundData.payment_id) {
        showToast("Error: Missing payment ID", "error");
        return;
      }
      await api.post('/refunds/request', refundData);
      showToast("Refund request submitted successfully!", "success");
      setShowRefundModal(false);
      fetchDashData();
    } catch (err) {
      showToast(err.response?.data?.detail || "Refund request failed.", 'error');
    }
  };

  const initPayment = async (req) => {
    // Determine session type from object properties if available
    let type = req.type || req.session_type || "PUBLIC";

    // Remap internal database types to Payment API types
    if (type === 'INDIVIDUAL' || type === 'GROUP') {
      type = 'REQUEST';
    }

    // Legacy fallback (only if data is missing type info)
    if (!req.type && !req.session_type) {
      if (myPersonalRequests.some(r => r.id === req.id)) {
        type = "REQUEST";
      }
    }

    // CONFLICT CHECK BEFORE OPENING MODAL
    try {
      // Don't verify conflict for "REQUEST" type if it's just paying for an accepted request? 
      // Actually, YES check it, to ensure they didn't enroll in something else in the meantime.
      // The backend endpoint handles exclusion of the request itself.

      const checkType = type === 'REQUEST' ? 'REQUEST' : 'PUBLIC';
      await api.get(`/student/check-session-conflict?type=${checkType}&id=${req.id}`);

    } catch (err) {
      if (err.response && (err.response.status === 400 || err.response.status === 409)) {
        showToast(err.response.data.detail || "Time Conflict: You cannot pay for this session due to a schedule overlap.", 'error');
        return;
      }
      // If other error (e.g. 500, network), we might want to let them try paying? 
      // Or block? Safer to block or warn. 
      // Let's log and proceed if it's not a 400 conflict, assuming transient error.
      console.warn("Conflict check failed, but proceeding:", err);
    }

    // Determine the payment amount - prioritize proposed_amount set by professor
    const paymentAmount = req.proposed_amount || req.price || req.budget || req.amount || 5000;

    setPaymentModalData({
      session: { ...req, price: paymentAmount },  // Ensure price is set for the modal
      sessionType: type,
      amount: paymentAmount,
      title: req.title
    });
  };

  const [showDetails, setShowDetails] = useState(null);

  const handleGiveFeedback = async (session_id, type) => {
    // Open feedback modal
    setFeedbackData({
      session_id,
      session_type: type,
      quality_rating: 0,
      expert_effectiveness: 0,
      relevance_rating: 0,
      comments: ''
    });
    setShowFeedbackModal(true);
  };

  const submitFeedback = async () => {
    // Validate required fields
    if (feedbackData.quality_rating === 0) {
      showToast("Please provide a quality rating", 'error');
      return;
    }

    try {
      // Submit feedback
      await api.post('/feedback/submit', {
        session_id: feedbackData.session_id,
        session_type: feedbackData.session_type,
        quality_rating: feedbackData.quality_rating,
        expert_effectiveness: feedbackData.expert_effectiveness || null,
        relevance_rating: feedbackData.relevance_rating || null,
        comments: feedbackData.comments || null
      });

      // Mark session as complete
      await api.post(`/payment/session/complete?session_id=${feedbackData.session_id}&session_type=${feedbackData.session_type}`);

      showToast("Feedback submitted and session marked as complete!", 'success');
      setShowFeedbackModal(false);

      // Small delay to ensure backend has committed changes
      setTimeout(() => {
        fetchDashData();
      }, 500);
    } catch (err) {
      // Handle case where feedback is already submitted but session wasn't marked complete
      if (err.response && err.response.status === 400 && err.response.data.detail === "Feedback already submitted for this session") {
        try {
          await api.post(`/payment/session/complete?session_id=${feedbackData.session_id}&session_type=${feedbackData.session_type}`);
          showToast("Session marked as complete!", 'success');
          setShowFeedbackModal(false);
          setTimeout(() => {
            fetchDashData();
          }, 500);
          return;
        } catch (completeErr) {
          showToast(completeErr.response?.data?.detail || "Failed to complete session", 'error');
          return;
        }
      }
      showToast(err.response?.data?.detail || "Failed to submit feedback", 'error');
    }
  };

  const handleJoinAndCheckIn = async (session_id, type, link) => {
    try {
      await api.post(`/student/sessions/check-in?session_id=${session_id}&session_type=${type}`);
      window.open(link, '_blank');
    } catch (err) {
      console.error("Check-in Error:", err);
      // Fallback: If it's a generic error, we still want to let them join for availability reasons?
      window.open(link, '_blank');
    }
  };

  const handleDownloadCertificate = async (sessionId, sessionType) => {
    try {
      showToast('Preparing certificate...', 'info');
      const response = await api.get(`/api/certificates/${sessionId}/download`, {
        params: { session_type: sessionType },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `EdVergenceX_Certificate_${sessionId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showToast('Certificate downloaded successfully!', 'success');
    } catch (err) {
      console.error('Download error:', err);
      // If it's potentially still processing, show as info (greenish-blue) instead of error (red)
      showToast('Certificate is being generated. Please wait a moment and try again.', 'info');
    }
  };


  const handleOpenInterestsModal = () => {
    // Pre-populate form with existing data
    setInterestsForm({
      skills: profile?.skills_list || [],
      preferred_domains: profile?.preferred_domains || [],
      primary_domain: profile?.primary_domain || ''
    });
    setShowInterestsModal(true);
  };

  const handleRemoveSkill = (skillToRemove) => {
    setInterestsForm(prev => ({
      ...prev,
      skills: prev.skills.filter(s => s !== skillToRemove)
    }));
  };

  const handleRemoveDomain = (domainToRemove) => {
    setInterestsForm(prev => ({
      ...prev,
      preferred_domains: prev.preferred_domains.filter(d => d !== domainToRemove)
    }));
  };

  const handleSubmitInterests = async () => {
    try {
      if (interestsForm.skills.length === 0 || interestsForm.preferred_domains.length === 0) {
        showToast('Please add at least one skill and one domain of interest', 'error');
        return;
      }

      await api.put('/student/profile/update-interests', {
        skills: interestsForm.skills,
        preferred_domains: interestsForm.preferred_domains,
        primary_domain: interestsForm.primary_domain || interestsForm.preferred_domains[0]
      });

      showToast('Skills and interests updated successfully!', 'success');
      setShowInterestsModal(false);
      fetchDashData(); // Refresh profile data
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to update interests', 'error');
    }
  };

  const handleChangePassword = async () => {
    // Validate inputs
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      showToast('Please fill in all password fields', 'error');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showToast('New passwords do not match', 'error');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      showToast('New password must be at least 6 characters long', 'error');
      return;
    }

    try {
      await api.post('/change-password', {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      });

      showToast('Password changed successfully!', 'success');
      setShowChangePasswordModal(false);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to change password', 'error');
    }
  };



  // Build a set of personal request keys to deduplicate GROUP sessions that appear as both Masterclass and Personal
  const personalRequestKeys = useMemo(() => {
    const keys = new Set();
    myPersonalRequests.filter(r => r.status === 'ACCEPTED' || r.status === 'COMPLETED').forEach(r => {
      keys.add(`${r.title}|${new Date(r.event_date.split('T')[0]).toDateString()}|${r.start_time}`);
    });
    return keys;
  }, [myPersonalRequests]);

  const filteredPublicSessions = useMemo(() => {
    const base = activeTab === 'explore' ? publicSessions : publicSessions.filter(s => s.is_enrolled);
    // Deduplicate by session id
    const seen = new Set();
    const unique = base.filter(s => {
      // We already fetched university engagements directly in `universitySessions`,
      // so filter out `PublicSession` rows that were auto-generated from requirements.
      if (s.requirement_id != null && !s.is_university_requirement) return false;

      if (seen.has(s.id)) return false;
      seen.add(s.id);
      return true;
    });
    // Exclude sessions that already appear under "My Personal Requests" (e.g. GROUP sessions created from direct student requests)
    return unique.filter(s => !personalRequestKeys.has(`${s.title}|${new Date(s.event_date.split('T')[0]).toDateString()}|${s.start_time}`));
  }, [activeTab, publicSessions, personalRequestKeys]);

  // Exclude completed sessions from active views (they go to the Completed tab)
  const activeSessions = useMemo(() => {
    return filteredPublicSessions.filter(s =>
      !s.is_completed &&
      s.payment_status !== 'RELEASED' &&
      s.payment_status !== 'REFUNDED' &&
      s.payment_status !== 'REFUND_REQUESTED'
    );
  }, [filteredPublicSessions]);

  // Separate sessions into 5 categories
  // 1. University posts under preferred domain
  const universityPreferredSessions = useMemo(() => {
    return activeSessions.filter(s =>
      s.university_name &&
      s.is_preferred_domain &&
      (activeTab === 'explore' || s.is_enrolled) // Show all in explore, only enrolled in sessions tab
    );
  }, [activeSessions, activeTab]);

  // 2. Industry masterclasses under preferred domain
  const industryPreferredSessions = useMemo(() => {
    return activeSessions.filter(s => !s.university_name && s.is_preferred_domain);
  }, [activeSessions]);

  // 4. University posts - other domains
  const universityOtherSessions = useMemo(() => {
    return activeSessions.filter(s =>
      s.university_name &&
      !s.is_preferred_domain &&
      (activeTab === 'explore' || s.is_enrolled)
    );
  }, [activeSessions, activeTab]);

  // 5. Industry masterclasses - other domains
  const industryOtherSessions = useMemo(() => {
    return activeSessions.filter(s => !s.university_name && !s.is_preferred_domain);
  }, [activeSessions]);

  // Helper to check if a session date is in the past
  const isPast = (dateStr) => {
    if (!dateStr) return false;
    const sessionDate = new Date(dateStr.split('T')[0]);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return sessionDate < today;
  };

  // Completed sessions (actually completed or released)
  const trulyCompletedPublic = useMemo(() => {
    const seen = new Set();
    return publicSessions.filter(s => {
      if (seen.has(s.id)) return false;
      seen.add(s.id);
      const isPastSession = isPast(s.event_date);
      return (s.is_completed || s.payment_status === 'RELEASED' || isPastSession) &&
        s.is_enrolled &&
        s.payment_status !== 'REFUNDED' &&
        s.payment_status !== 'REFUND_REQUESTED';
    });
  }, [publicSessions]);

  const trulyCompletedPersonal = useMemo(() => {
    return myPersonalRequests.filter(r =>
      (r.status === 'COMPLETED' || r.is_completed || isPast(r.event_date)) &&
      r.payment_status !== 'REFUNDED' &&
      r.payment_status !== 'REFUND_REQUESTED' &&
      r.status !== 'DECLINED'
    );
  }, [myPersonalRequests]);

  // Refunded or Refund Requested sessions
  const refundedPublic = useMemo(() => {
    const seen = new Set();
    return publicSessions.filter(s => {
      if (seen.has(s.id)) return false;
      seen.add(s.id);
      return (s.payment_status === 'REFUNDED' || s.payment_status === 'REFUND_REQUESTED') && s.is_enrolled;
    });
  }, [publicSessions]);

  const refundedPersonal = useMemo(() => {
    return myPersonalRequests.filter(r => r.payment_status === 'REFUNDED' || r.payment_status === 'REFUND_REQUESTED');
  }, [myPersonalRequests]);

  // Total completed sessions (for the count and filters)
  const completedPublicSessions = useMemo(() => [...trulyCompletedPublic, ...refundedPublic], [trulyCompletedPublic, refundedPublic]);
  const completedPersonalRequests = useMemo(() => [...trulyCompletedPersonal, ...refundedPersonal], [trulyCompletedPersonal, refundedPersonal]);

  const getDaySessionStatus = (date) => {
    const dStr = date.toDateString();
    const enrolledMasters = publicSessions.filter(s => s.is_enrolled && new Date(s.event_date.split('T')[0]).toDateString() === dStr
      && !personalRequestKeys.has(`${s.title}|${dStr}|${s.start_time}`));
    const acceptedPersonal = myPersonalRequests.filter(r => r.status === 'ACCEPTED' && new Date(r.event_date.split('T')[0]).toDateString() === dStr);
    return {
      hasMaster: enrolledMasters.length > 0,
      hasPersonal: acceptedPersonal.length > 0
    };
  };

  const sessionsForSelectedDay = useMemo(() => {
    const dStr = selectedDate.toDateString();
    const masters = publicSessions
      .filter(s => s.is_enrolled && new Date(s.event_date.split('T')[0]).toDateString() === dStr
        && !personalRequestKeys.has(`${s.title}|${dStr}|${s.start_time}`))
      .map(s => ({ title: s.title, time: s.start_time, type: 'Masterclass' }));
    const personal = myPersonalRequests
      .filter(r => r.status === 'ACCEPTED' && new Date(r.event_date.split('T')[0]).toDateString() === dStr)
      .map(r => ({ title: r.title, time: r.start_time, type: 'Personal' }));
    return [...masters, ...personal];
  }, [selectedDate, publicSessions, myPersonalRequests, personalRequestKeys]);

  if (loading) return <Loader />;

  const renderToolsetTab = () => {
    const aiTools = profile?.ai_toolset || [];
    const hasProjects = profile?.github_projects && profile.github_projects.length > 0;
    const displayTools = aiTools.slice(0, 14);

    if (displayTools.length === 0) {
      return (
        <div className="stu-fade-in" style={{ padding: '40px 20px', textAlign: 'center', background: 'var(--graphite-dark)', borderRadius: '16px', border: '1px dashed var(--border)', margin: '20px 0' }}>
          <Sparkles size={52} style={{ color: '#3b82f6', marginBottom: '16px', margin: '0 auto' }} />
          <h3 style={{ fontSize: '20px', fontWeight: '700', color: 'var(--proto-white)', marginBottom: '10px' }}>
            {hasProjects ? "🤖 AI Tools Analysis Ready" : "No Tools Detected Yet"}
          </h3>
          <p style={{ fontSize: '14px', color: 'var(--text-muted)', maxWidth: '520px', margin: '0 auto 24px auto', lineHeight: '1.6' }}>
            {hasProjects
              ? `We do not use hardcoded data! You have ${profile.github_projects.length} GitHub repositories connected. Click below to let AI evaluate your repositories and generate your personalized Tools assessment & radar chart.`
              : "We do not use hardcoded data! To generate your real-world Tools assessment and radar chart, please connect your GitHub account in the Projects tab so AI can evaluate your repositories."}
          </p>
          {hasProjects ? (
            <button
              onClick={handleRunAiAnalysis}
              disabled={isAnalyzingAi}
              style={{
                padding: '12px 28px', background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)', color: 'white', border: 'none',
                borderRadius: '10px', fontSize: '14px', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '8px',
                cursor: isAnalyzingAi ? 'not-allowed' : 'pointer', boxShadow: '0 4px 14px rgba(37, 99, 235, 0.3)', transition: 'all 0.2s'
              }}
            >
              <Sparkles size={18} className={isAnalyzingAi ? "spin" : ""} />
              {isAnalyzingAi ? "Running AI Analysis..." : "⚡ Run AI Tools Analysis Now"}
            </button>
          ) : (
            <button
              onClick={() => setSettingsSubTab('projects')}
              style={{
                padding: '12px 24px', background: '#24292e', color: 'white', border: 'none',
                borderRadius: '8px', fontSize: '14px', fontWeight: '600', display: 'inline-flex',
                alignItems: 'center', gap: '8px', cursor: 'pointer'
              }}
            >
              <Folder size={18} /> Go to Projects Tab to Connect GitHub
            </button>
          )}
        </div>
      );
    }

    // Helper to calculate percentage for radar point
    const getLevelPct = (tool, idx) => {
      if (tool.level) {
        const parsed = parseFloat(tool.level);
        if (!isNaN(parsed)) return parsed / 100;
      }
      if (tool.score) {
        const parsed = parseFloat(tool.score);
        if (!isNaN(parsed)) return parsed / 10;
      }
      return 0.8 - (idx * 0.03);
    };

    const axes = [0, 45, 90, 135, 180, 225, 270, 315];
    const R = 110;
    const toXY = (angleDeg, r) => ({
      x: r * Math.sin(angleDeg * Math.PI / 180),
      y: -r * Math.cos(angleDeg * Math.PI / 180)
    });

    const dataPoints = displayTools.slice(0, 8).map((t, i) => toXY(axes[i % 8], R * getLevelPct(t, i)));
    const dataPolygon = dataPoints.map(p => `${p.x},${p.y}`).join(' ');
    const gridPolygon = axes.map(a => `${toXY(a, R).x},${toXY(a, R).y}`).join(' ');

    return (
      <div className="stu-fade-in" style={{ padding: '10px 0' }}>
        <h3 style={{ fontSize: '18px', fontWeight: '700', color: 'var(--proto-white)', textAlign: 'center', marginBottom: '32px', letterSpacing: '-0.01em' }}>
          AI-Detected Tools & Frameworks
        </h3>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '40px', alignItems: 'center' }}>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '340px', position: 'relative' }}>
            <svg width="340" height="340" viewBox="-170 -170 340 340" style={{ overflow: 'visible' }}>
              {[0.25, 0.5, 0.75, 1.0].map((scale, i) => (
                <polygon key={i} points={gridPolygon} transform={`scale(${scale})`} fill={i === 3 ? 'rgba(59,130,246,0.04)' : 'none'} stroke="#cbd5e1" strokeWidth="1" strokeDasharray={i === 3 ? '0' : '3,3'} />
              ))}
              {axes.map((deg, i) => {
                const end = toXY(deg, R);
                return <line key={i} x1="0" y1="0" x2={end.x} y2={end.y} stroke="#cbd5e1" strokeWidth="1" />;
              })}
              <polygon points={dataPolygon} fill="rgba(59, 130, 246, 0.18)" stroke="#3b82f6" strokeWidth="2.5" />
              {dataPoints.map((p, i) => (
                <circle key={i} cx={p.x} cy={p.y} r="4.5" fill={displayTools[i]?.color || '#3b82f6'} stroke="#ffffff" strokeWidth="1.5" />
              ))}
              {displayTools.slice(0, 8).map((t, i) => {
                const lp = toXY(axes[i % 8], R + 22);
                const anchor = lp.x > 5 ? 'start' : lp.x < -5 ? 'end' : 'middle';
                return (
                  <text key={i} x={lp.x} y={lp.y + 4} textAnchor={anchor} fill="var(--proto-white)" fontSize="11" fontWeight="600">
                    {t.name.length > 14 ? t.name.substring(0, 13) + '…' : t.name}
                  </text>
                );
              })}
            </svg>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxHeight: '460px', paddingRight: '4px' }}>
            {/* Scrollable List */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', overflowY: 'auto', flex: 1, paddingRight: '4px', position: 'relative' }}>
              {/* Table Header Row */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                paddingBottom: '8px',
                borderBottom: '1.5px solid var(--border)',
                marginBottom: '4px',
                position: 'sticky',
                top: 0,
                background: '#ffffff',
                zIndex: 10
              }}>
                <span style={{ fontSize: '11px', fontWeight: '700', color: '#0f172a', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Tools / Frameworks
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <span style={{ fontSize: '11px', fontWeight: '700', color: '#0f172a', textTransform: 'uppercase', width: '110px', textAlign: 'center', letterSpacing: '0.05em', lineHeight: '1.4' }}>
                    Skill<br />Score
                  </span>
                  <span style={{ fontSize: '11px', fontWeight: '700', color: '#0f172a', textTransform: 'uppercase', width: '90px', textAlign: 'center', letterSpacing: '0.05em' }}>
                    Knowledge Score
                  </span>
                </div>
              </div>

              {displayTools.map((tool, idx) => {
                // Skill Score: from GitHub/resume, never changed by quiz
                const toolScore = tool.score ? tool.score.split('/')[0].trim() : '—';

                // Knowledge Score: from ai_knowledge_scores, per-tool, set on every retake
                const knowledgeEntry = (profile?.ai_knowledge_scores || []).find(
                  k => k.name?.toLowerCase() === tool.name?.toLowerCase()
                );
                const kScore = knowledgeEntry?.score || null;
                const kNum = kScore ? parseFloat(kScore) : null;
                const kTested = knowledgeEntry?.tested !== false && kScore !== null;
                const kColor = kNum !== null
                  ? (kNum >= 7 ? '#16a34a' : kNum >= 5 ? '#ea580c' : '#dc2626')
                  : 'var(--text-muted)';
                const kBg = kNum !== null
                  ? (kNum >= 7 ? 'rgba(34,197,94,0.10)' : kNum >= 5 ? 'rgba(251,146,60,0.10)' : 'rgba(248,113,113,0.10)')
                  : 'var(--bg-page)';

                return (
                  <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '10px', borderBottom: '1px solid var(--border)', gap: '10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                      <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: tool.color || '#3b82f6', flexShrink: 0 }}></span>
                      <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-main)' }}>{tool.name}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexShrink: 0 }}>
                      {/* Skill Score: from GitHub/resume, permanently stable */}
                      <div style={{ width: '110px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span style={{ fontSize: '12px', color: 'var(--text-main)', fontWeight: '600', borderRadius: '16px', border: '1px solid var(--border)', padding: '4px 14px' }}>
                          {toolScore} / 10
                        </span>
                      </div>
                      {/* Knowledge Score: from assessment, per-tool */}
                      <div style={{ width: '90px', display: 'flex', justifyContent: 'center' }}>
                        <span
                          title={knowledgeEntry?.status || (kTested ? '' : 'Not yet tested in assessment')}
                          style={{
                            background: kBg,
                            color: kColor,
                            padding: '4px 14px',
                            borderRadius: '16px',
                            fontSize: '12px',
                            fontWeight: '700',
                            border: `1px solid ${kNum !== null ? (kNum >= 7 ? '#bbf7d0' : kNum >= 5 ? '#fed7aa' : '#fecaca') : 'var(--border)'}`,
                            width: '100%',
                            textAlign: 'center',
                            cursor: 'default'
                          }}>
                          {kScore ? kScore : '— / 10'}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderSkillsetTab = () => {
    const aiSkills = profile?.ai_skillset || [];
    const hasProjects = profile?.github_projects && profile.github_projects.length > 0;
    const displaySkills = aiSkills.slice(0, 8);

    if (displaySkills.length === 0) {
      return (
        <div className="stu-fade-in" style={{ padding: '40px 20px', textAlign: 'center', background: 'var(--graphite-dark)', borderRadius: '16px', border: '1px dashed var(--border)', margin: '20px 0' }}>
          <Target size={52} style={{ color: '#10b981', marginBottom: '16px', margin: '0 auto' }} />
          <h3 style={{ fontSize: '20px', fontWeight: '700', color: 'var(--proto-white)', marginBottom: '10px' }}>
            {hasProjects ? "🤖 AI Skills Analysis Ready" : "No Skills Detected Yet"}
          </h3>
          <p style={{ fontSize: '14px', color: 'var(--text-muted)', maxWidth: '520px', margin: '0 auto 24px auto', lineHeight: '1.6' }}>
            {hasProjects
              ? `We do not use hardcoded data! You have ${profile.github_projects.length} GitHub repositories connected. Click below to let AI evaluate your projects and map out your technical mastery across engineering domains.`
              : "We do not use hardcoded data! To generate your real-world Skills assessment and radar map, please connect your GitHub account in the Projects tab so AI can evaluate your repositories."}
          </p>
          {hasProjects ? (
            <button
              onClick={handleRunAiAnalysis}
              disabled={isAnalyzingAi}
              style={{
                padding: '12px 28px', background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: 'white', border: 'none',
                borderRadius: '10px', fontSize: '14px', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '8px',
                cursor: isAnalyzingAi ? 'not-allowed' : 'pointer', boxShadow: '0 4px 14px rgba(16, 185, 129, 0.3)', transition: 'all 0.2s'
              }}
            >
              <Sparkles size={18} className={isAnalyzingAi ? "spin" : ""} />
              {isAnalyzingAi ? "Running AI Analysis..." : "⚡ Run AI Skills Analysis Now"}
            </button>
          ) : (
            <button
              onClick={() => setSettingsSubTab('projects')}
              style={{
                padding: '12px 24px', background: '#24292e', color: 'white', border: 'none',
                borderRadius: '8px', fontSize: '14px', fontWeight: '600', display: 'inline-flex',
                alignItems: 'center', gap: '8px', cursor: 'pointer'
              }}
            >
              <Folder size={18} /> Go to Projects Tab to Connect GitHub
            </button>
          )}
        </div>
      );
    }

    const getLevelPct = (skill, idx) => {
      if (skill.level) {
        const parsed = parseFloat(skill.level);
        if (!isNaN(parsed)) return parsed / 100;
      }
      if (skill.score) {
        const parsed = parseFloat(skill.score);
        if (!isNaN(parsed)) return parsed / 10;
      }
      return 0.85 - (idx * 0.04);
    };

    const axes = [0, 45, 90, 135, 180, 225, 270, 315];
    const R = 110;
    const toXY = (angleDeg, r) => ({
      x: r * Math.sin(angleDeg * Math.PI / 180),
      y: -r * Math.cos(angleDeg * Math.PI / 180)
    });

    const dataPoints = displaySkills.map((s, i) => toXY(axes[i % 8], R * getLevelPct(s, i)));
    const dataPolygon = dataPoints.map(p => `${p.x},${p.y}`).join(' ');
    const gridPolygon = axes.map(a => `${toXY(a, R).x},${toXY(a, R).y}`).join(' ');

    return (
      <div className="stu-fade-in" style={{ padding: '10px 0' }}>
        <h3 style={{ fontSize: '18px', fontWeight: '700', color: 'var(--proto-white)', textAlign: 'center', marginBottom: '32px', letterSpacing: '-0.01em' }}>
          AI-Analyzed Skill Categories
        </h3>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '40px', alignItems: 'center' }}>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '340px' }}>
            <svg width="340" height="340" viewBox="-170 -170 340 340" style={{ overflow: 'visible' }}>
              {[0.25, 0.5, 0.75, 1.0].map((scale, i) => (
                <polygon key={i} points={gridPolygon} transform={`scale(${scale})`} fill={i === 3 ? 'rgba(16,185,129,0.04)' : 'none'} stroke="#cbd5e1" strokeWidth="1" strokeDasharray={i === 3 ? '0' : '3,3'} />
              ))}
              {axes.map((deg, i) => {
                const end = toXY(deg, R);
                return <line key={i} x1="0" y1="0" x2={end.x} y2={end.y} stroke="#cbd5e1" strokeWidth="1" />;
              })}
              <polygon points={dataPolygon} fill="rgba(16, 185, 129, 0.18)" stroke="#10b981" strokeWidth="2.5" />
              {dataPoints.map((p, i) => (
                <circle key={i} cx={p.x} cy={p.y} r="4.5" fill={displaySkills[i]?.color || '#10b981'} stroke="#ffffff" strokeWidth="1.5" />
              ))}
              {displaySkills.map((s, i) => {
                const lp = toXY(axes[i % 8], R + 22);
                const anchor = lp.x > 5 ? 'start' : lp.x < -5 ? 'end' : 'middle';
                return (
                  <text key={i} x={lp.x} y={lp.y + 4} textAnchor={anchor} fill="var(--proto-white)" fontSize="11" fontWeight="600">
                    {s.name.length > 14 ? s.name.substring(0, 13) + '…' : s.name}
                  </text>
                );
              })}
            </svg>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxHeight: '460px', paddingRight: '4px' }}>
            {/* Scrollable List */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', overflowY: 'auto', flex: 1, paddingRight: '4px', position: 'relative' }}>
              {/* Table Header Row */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                paddingBottom: '8px',
                borderBottom: '1.5px solid var(--border)',
                marginBottom: '4px',
                position: 'sticky',
                top: 0,
                background: 'var(--bg-page)',
                zIndex: 10
              }}>
                <span style={{ fontSize: '11px', fontWeight: '700', color: 'var(--text-main)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Skill Category
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <span style={{ fontSize: '11px', fontWeight: '700', color: 'var(--text-main)', textTransform: 'uppercase', width: '110px', textAlign: 'center', letterSpacing: '0.05em', lineHeight: '1.4' }}>
                    Skill<br />Score
                  </span>
                  <span style={{ fontSize: '11px', fontWeight: '700', color: 'var(--text-main)', textTransform: 'uppercase', width: '90px', textAlign: 'center', letterSpacing: '0.05em' }}>
                    Knowledge Score
                  </span>
                </div>
              </div>

              {displaySkills.map((skill, idx) => {
                // Skill Score: from GitHub/resume, never changed by quiz
                const skillScore = skill.score ? skill.score.split('/')[0].trim() : '—';

                // Knowledge Score: from ai_knowledge_scores, per-skill, set on every retake
                const knowledgeEntry = (profile?.ai_knowledge_scores || []).find(
                  k => k.name?.toLowerCase() === skill.name?.toLowerCase()
                );
                const kScore = knowledgeEntry?.score || null;
                const kNum = kScore ? parseFloat(kScore) : null;
                const kTested = knowledgeEntry?.tested !== false && kScore !== null;
                const kColor = kNum !== null
                  ? (kNum >= 7 ? '#16a34a' : kNum >= 5 ? '#ea580c' : '#dc2626')
                  : 'var(--text-muted)';
                const kBg = kNum !== null
                  ? (kNum >= 7 ? 'rgba(34,197,94,0.10)' : kNum >= 5 ? 'rgba(251,146,60,0.10)' : 'rgba(248,113,113,0.10)')
                  : 'var(--bg-page)';

                return (
                  <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '10px', borderBottom: '1px solid var(--border)', gap: '10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 1, minWidth: 0 }}>
                      <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: skill.color || '#10b981', flexShrink: 0, display: 'inline-block' }}></span>
                      <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-main)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{skill.name}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexShrink: 0 }}>
                      {/* Skill Score: from GitHub/resume, permanently stable */}
                      <div style={{ width: '110px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span style={{ fontSize: '12px', color: 'var(--text-main)', fontWeight: '600', borderRadius: '16px', border: '1px solid var(--border)', padding: '4px 14px' }}>
                          {skillScore} / 10
                        </span>
                      </div>
                      {/* Knowledge Score: from assessment, per-skill */}
                      <div style={{ width: '90px', display: 'flex', justifyContent: 'center' }}>
                        <span
                          title={knowledgeEntry?.status || (kTested ? '' : 'Not yet tested in assessment')}
                          style={{
                            background: kBg,
                            color: kColor,
                            padding: '4px 14px',
                            borderRadius: '16px',
                            fontSize: '12px',
                            fontWeight: '700',
                            border: `1px solid ${kNum !== null ? (kNum >= 7 ? '#bbf7d0' : kNum >= 5 ? '#fed7aa' : '#fecaca') : 'var(--border)'}`,
                            width: '100%',
                            textAlign: 'center',
                            cursor: 'default'
                          }}>
                          {kScore ? kScore : '— / 10'}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderMindsetTab = () => (
    <div className="stu-fade-in" style={{ padding: '10px 0' }}>
      <h3 style={{ fontSize: '17px', fontWeight: '700', color: '#1e293b', marginBottom: '24px' }}>Cognitive &amp; Leadership Profile</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px' }}>
        {[
          { title: 'Analytical Problem Solving', score: '9.8 / 10', desc: 'Deconstructs complex systems into modular, manageable engineering solutions.' },
          { title: 'Collaborative Leadership', score: '9.5 / 10', desc: 'Thrives in cross-functional team environments and agile workflows.' },
          { title: 'Rapid Adaptability', score: '9.6 / 10', desc: 'Quickly masters new frameworks, languages, and evolving paradigms.' },
          { title: 'Continuous Innovation', score: '9.4 / 10', desc: 'Driven to optimize performance and explore cutting-edge methodologies.' }
        ].map((item, idx) => (
          <div key={idx} style={{ background: '#f8fafc', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '14px', fontWeight: '700', color: '#1e293b' }}>{item.title}</span>
              <span style={{ fontSize: '12px', fontWeight: '700', background: '#eff6ff', color: '#1d4ed8', padding: '4px 10px', borderRadius: '12px' }}>{item.score}</span>
            </div>
            <p style={{ fontSize: '13px', color: '#64748b', lineHeight: '1.5', margin: 0 }}>{item.desc}</p>
          </div>
        ))}
      </div>
    </div>
  );

  const renderExperienceTab = () => {
    const expList = parseJsonSafe(profile?.experiences, []);
    const displayExp = expList.length > 0 ? expList : [
      { role: 'Senior Student Developer & Researcher', org: profile.institution_name || 'University CSE Dept', period: '2025 - Present', desc: 'Leading software architecture projects, mentoring junior peers, and contributing to open-source tools.' },
      { role: 'Software Engineering Intern', org: 'Tech Innovation Labs', period: 'Summer 2025', desc: 'Developed full-stack web applications, optimized database query performance by 35%.' },
      { role: 'Core Hackathon Finalist & Organizer', org: 'National EdTech Summit', period: '2024', desc: 'Built an AI-powered student mentorship portal in 36 hours.' }
    ];

    return (
      <div className="stu-fade-in" style={{ padding: '10px 0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px', background: '#f8fafc', padding: '16px 20px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
          <div>
            <h3 style={{ fontSize: '17px', fontWeight: '700', color: '#1e293b', margin: '0 0 4px 0' }}>Professional &amp; Academic Journey</h3>
            <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>{expList.length > 0 ? '✨ Extracted from your uploaded resume' : 'Example timeline (Upload resume or click AI Extract to auto-populate)'}</p>
          </div>
          <button
            onClick={handleExtractFromResume}
            disabled={isExtractingResume}
            style={{
              padding: '8px 16px', background: 'transparent', color: 'var(--primary)', border: '1px solid var(--primary)',
              borderRadius: '8px', fontSize: '13px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px',
              cursor: isExtractingResume ? 'not-allowed' : 'pointer', transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--primary-dim)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            <Sparkles size={15} className={isExtractingResume ? "spin" : ""} />
            {isExtractingResume ? "Extracting..." : "⚡ AI Extract from Resume"}
          </button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', borderLeft: '2px solid #3b82f6', paddingLeft: '24px', marginLeft: '12px' }}>
          {displayExp.map((exp, idx) => (
            <div key={idx} style={{ position: 'relative' }}>
              <div style={{ position: 'absolute', left: '-31px', top: '4px', width: '12px', height: '12px', borderRadius: '50%', background: '#3b82f6', border: '3px solid white', boxShadow: '0 0 0 2px #3b82f6' }}></div>
              <h4 style={{ fontSize: '15px', fontWeight: '700', color: '#1e293b', margin: '0 0 4px 0' }}>{exp.role}</h4>
              <div style={{ fontSize: '13px', fontWeight: '600', color: '#3b82f6', marginBottom: '8px' }}>{exp.org} • <span style={{ color: '#64748b', fontWeight: '500' }}>{exp.period}</span></div>
              <p style={{ fontSize: '13px', color: '#64748b', lineHeight: '1.5', margin: 0 }}>{exp.desc}</p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const handleMarksCardUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
    if (!allowed.includes(file.type)) {
      showToast('Please upload a PDF, PNG, or JPEG file.', 'error');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      showToast('File size must be less than 10MB.', 'error');
      return;
    }
    setIsUploadingMarksCard(true);
    showToast('📄 Uploading marks card...', 'info');
    try {
      const data = new FormData();
      data.append('file', file);
      const res = await api.post('/student/upload-marks-card', data);
      setMarksCardForm(prev => ({ ...prev, file_url: res.data.file_path, file_name: res.data.filename }));
      showToast('✅ Marks card uploaded successfully! Remember to save changes.', 'success');
    } catch (err) {
      console.error('Marks card upload error:', err);
      showToast(err.response?.data?.detail || 'Failed to upload marks card.', 'error');
    } finally {
      setIsUploadingMarksCard(false);
      if (marksCardInputRef.current) marksCardInputRef.current.value = '';
    }
  };

  const handleSaveMarksCard = async () => {
    try {
      const existing = parseJsonSafe(profile?.marks_cards, []);
      const updated = [...existing];
      const idx = updated.findIndex(m => m.semester === activeMarksCardSemester);
      const newCard = {
        semester: activeMarksCardSemester,
        sgpa: marksCardForm.sgpa,
        courses: marksCardForm.courses,
        file_url: marksCardForm.file_url,
        file_name: marksCardForm.file_name
      };
      if (idx >= 0) {
        updated[idx] = newCard;
      } else {
        updated.push(newCard);
      }

      await api.put('/student/profile/update', { marks_cards: updated });
      showToast(`Semester ${activeMarksCardSemester} Marks Card saved successfully!`, 'success');
      fetchDashData();
    } catch (err) {
      showToast(err.response?.data?.detail || "Failed to save Marks Card", 'error');
    }
  };

  const renderCoursesAndMarksSection = () => {
    const totalCourses = coursesAndMarks.length;
    let totalScore = 0;
    let totalTestsCount = 0;
    let totalAttendance = 0;
    let totalAttendanceCount = 0;
    coursesAndMarks.forEach(cm => {
      (cm.marks || []).forEach(m => {
        totalScore += (m.percentage || 0);
        totalTestsCount++;
      });
      const attendance = cm.attendance_percentage !== undefined ? cm.attendance_percentage : cm.selection?.course?.attendance_percentage;
      if (attendance !== undefined) {
        totalAttendance += attendance;
        totalAttendanceCount++;
      }
    });
    const avgScore = totalTestsCount > 0 ? `${(totalScore / totalTestsCount).toFixed(1)}%` : 'N/A';
    const avgAttendance = totalAttendanceCount > 0 ? `${(totalAttendance / totalAttendanceCount).toFixed(1)}%` : 'N/A';

    return (
      <div className="stu-fade-in" style={{ padding: '28px', background: '#ffffff', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 4px 25px rgba(0,0,0,0.04)', fontFamily: "'Inter', sans-serif", marginBottom: '28px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', paddingBottom: '20px', borderBottom: '1px solid #f1f5f9', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <h3 style={{ fontSize: '20px', fontWeight: '800', color: '#0f172a', margin: '0 0 6px 0', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '36px', height: '36px', borderRadius: '10px', background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)', color: '#ffffff' }}>
                <BookOpen size={20} />
              </span>
              Enrolled LMS Courses & Academic Test Marks
            </h3>
            <p style={{ fontSize: '14px', color: '#64748b', margin: 0 }}>
              View your assigned curriculum courses, faculty mentors, and detailed performance across internal assessments & tests.
            </p>
          </div>

          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', padding: '10px 16px', borderRadius: '12px', textAlign: 'center' }}>
              <div style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase' }}>Enrolled Courses</div>
              <div style={{ fontSize: '18px', fontWeight: '800', color: '#0f172a' }}>{totalCourses}</div>
            </div>
            <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', padding: '10px 16px', borderRadius: '12px', textAlign: 'center' }}>
              <div style={{ fontSize: '11px', fontWeight: '700', color: '#2563eb', textTransform: 'uppercase' }}>Avg Test Score</div>
              <div style={{ fontSize: '18px', fontWeight: '800', color: '#1d4ed8' }}>{avgScore}</div>
            </div>
            <div style={{ background: '#fffbeb', border: '1px solid #fde68a', padding: '10px 16px', borderRadius: '12px', textAlign: 'center' }}>
              <div style={{ fontSize: '11px', fontWeight: '700', color: '#d97706', textTransform: 'uppercase' }}>Avg Attendance</div>
              <div style={{ fontSize: '18px', fontWeight: '800', color: '#b45309' }}>{avgAttendance}</div>
            </div>
          </div>
        </div>


        {loadingCoursesAndMarks ? (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: '#64748b' }}>
            Loading your courses and test marks...
          </div>
        ) : coursesAndMarks.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 20px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
            <BookOpen size={36} style={{ color: '#94a3b8', marginBottom: '10px' }} />
            <p style={{ fontSize: '15px', fontWeight: '600', color: '#334155', margin: '0 0 4px 0' }}>No courses assigned yet</p>
            <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>Your university administrator will assign LMS courses to your batch soon.</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {coursesAndMarks.map((item, idx) => {
              const sel = item.selection || {};
              const course = sel.course || {};
              const marks = item.marks || [];
              const attendancePerc = item.attendance_percentage !== undefined ? item.attendance_percentage : course.attendance_percentage;

              return (
                <div key={idx} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '20px', transition: 'all 0.2s' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap', marginBottom: '16px', paddingBottom: '16px', borderBottom: '1px solid #e2e8f0' }}>
                    <div>
                      <span style={{ background: '#e0f2fe', color: '#0284c7', padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase' }}>
                        {course.category || 'Curriculum'}
                      </span>
                      <h4 style={{ fontSize: '18px', fontWeight: '800', color: '#0f172a', margin: '8px 0 6px 0' }}>
                        {course.title || 'LMS Course'}
                      </h4>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#ffffff', border: '1px solid #cbd5e1', padding: '4px 10px', borderRadius: '8px', fontSize: '12px', fontWeight: '600', color: '#334155' }}>
                          👨‍🏫 Assigned Faculty: <strong style={{ color: '#0f172a' }}>{sel.faculty_name || 'Not assigned'}</strong>
                        </span>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#f1f5f9', padding: '4px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: '600', color: '#475569' }}>
                          Batch: {sel.batch || 'All Batches'}
                        </span>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#fef3c7', padding: '4px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: '600', color: '#b45309' }}>
                          Attendance: {attendancePerc !== undefined ? `${attendancePerc}%` : 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h5 style={{ fontSize: '13px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em', margin: '0 0 12px 0' }}>
                      Academic Assessments & Test Marks
                    </h5>

                    {marks.length === 0 ? (
                      <div style={{ padding: '16px', background: '#ffffff', borderRadius: '10px', border: '1px solid #e2e8f0', color: '#64748b', fontSize: '13px', fontWeight: '500' }}>
                        No test marks entered yet by faculty.
                      </div>
                    ) : (
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                          <thead>
                            <tr style={{ background: '#f1f5f9', color: '#475569', textAlign: 'left' }}>
                              <th style={{ padding: '10px 14px', borderRadius: '8px 0 0 8px', fontWeight: '700' }}>Test / Assessment Name</th>
                              <th style={{ padding: '10px 14px', fontWeight: '700' }}>Marks</th>
                              <th style={{ padding: '10px 14px', borderRadius: '0 8px 8px 0', fontWeight: '700' }}>Percentage</th>
                            </tr>
                          </thead>
                          <tbody>
                            {marks.map((m, mIdx) => {
                              const pct = m.percentage || Math.round(((m.marks_obtained || 0) / (m.max_marks || 1)) * 100);
                              const gradeColor = pct >= 80 ? '#16a34a' : pct >= 70 ? '#2563eb' : '#d97706';

                              return (
                                <tr key={mIdx} style={{ borderBottom: '1px solid #e2e8f0', background: '#ffffff' }}>
                                  <td style={{ padding: '12px 14px', fontWeight: '600', color: '#0f172a' }}>
                                    {m.test_name}
                                  </td>
                                  <td style={{ padding: '12px 14px', color: '#0f172a', fontWeight: '700' }}>
                                    {m.marks_obtained} <span style={{ color: '#64748b', fontWeight: '600', fontSize: '0.9em' }}>/ {m.max_marks}</span>
                                  </td>
                                  <td style={{ padding: '12px 14px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                      <div style={{ flex: 1, maxWidth: '100px', height: '6px', background: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
                                        <div style={{ width: `${Math.min(100, pct)}%`, height: '100%', background: gradeColor, borderRadius: '4px' }} />
                                      </div>
                                      <span style={{ fontWeight: '700', color: '#0f172a' }}>{pct}%</span>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}

                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderMarksCardTab = () => {
    return (
      <div className="stu-fade-in" style={{ padding: '28px', background: '#ffffff', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 4px 25px rgba(0,0,0,0.04)', fontFamily: "'Inter', sans-serif" }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '16px', borderBottom: '1px solid #f1f5f9' }}>
          <div>
            <h3 style={{ fontSize: '20px', fontWeight: '800', color: '#0f172a', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FileText size={24} color="var(--primary)" /> Marks Cards & Academic Records
            </h3>
            <p style={{ fontSize: '13px', color: '#64748b', margin: '4px 0 0 0' }}>Manage your semester-wise academic performance and marks cards.</p>
          </div>
        </div>

        {/* Semester Selection Tabs */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', overflowX: 'auto', paddingBottom: '4px' }}>
          {[1, 2, 3, 4, 5, 6, 7, 8].map(sem => (
            <button
              key={sem}
              onClick={() => setActiveMarksCardSemester(sem)}
              style={{
                padding: '8px 16px',
                borderRadius: '20px',
                border: 'none',
                background: activeMarksCardSemester === sem ? 'var(--primary)' : '#f1f5f9',
                color: activeMarksCardSemester === sem ? 'var(--bg-page)' : '#64748b',
                fontWeight: '700',
                fontSize: '13px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                whiteSpace: 'nowrap'
              }}
            >
              Semester {sem}
            </button>
          ))}
        </div>

        <div style={{ background: '#f8fafc', padding: '24px', borderRadius: '14px', border: '1px solid #e2e8f0' }}>
          <h4 style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a', marginBottom: '16px' }}>Semester {activeMarksCardSemester} Details</h4>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>SGPA</label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="10"
                value={marksCardForm.sgpa}
                onChange={(e) => setMarksCardForm({ ...marksCardForm, sgpa: e.target.value })}
                placeholder="e.g. 8.5"
                style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px' }}
              />
            </div>
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>Upload Marks Card Document</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <input
                type="file"
                ref={marksCardInputRef}
                style={{ display: 'none' }}
                accept=".pdf, .png, .jpg, .jpeg"
                onChange={handleMarksCardUpload}
              />
              <button
                onClick={() => marksCardInputRef.current?.click()}
                disabled={isUploadingMarksCard}
                style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  padding: '10px 16px', background: 'transparent', color: 'var(--primary)',
                  border: '1px solid var(--primary)', borderRadius: '8px', fontWeight: '600',
                  fontSize: '13px', cursor: isUploadingMarksCard ? 'wait' : 'pointer', transition: 'all 0.2s'
                }}
              >
                <Upload size={16} /> {isUploadingMarksCard ? 'Uploading...' : 'Choose File'}
              </button>

              {marksCardForm.file_name && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, overflow: 'hidden' }}>
                  <FileText size={16} color="#16a34a" />
                  <span style={{ fontSize: '13px', color: '#16a34a', fontWeight: '500', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                    {marksCardForm.file_name}
                  </span>
                  {marksCardForm.file_url && (
                    <a href={marksCardForm.file_url.startsWith('http') ? marksCardForm.file_url : `${api.defaults.baseURL || ''}${marksCardForm.file_url}`} target="_blank" rel="noreferrer" style={{ color: '#2563eb', fontSize: '12px', fontWeight: '600', textDecoration: 'none', marginLeft: 'auto' }}>
                      View File
                    </a>
                  )}
                </div>
              )}
            </div>
            <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '6px' }}>Accepted formats: PDF, PNG, JPG (Max 10MB)</div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '16px', borderTop: '1px solid #e2e8f0' }}>
            <button
              onClick={handleSaveMarksCard}
              style={{ padding: '12px 24px', background: 'var(--primary)', color: 'var(--bg-page)', border: 'none', borderRadius: '8px', fontWeight: '700', fontSize: '14px', cursor: 'pointer', transition: 'all 0.2s' }}
            >
              Save Marks Card
            </button>
          </div>
        </div>
      </div>
    );
  };

  const getTimelineData = () => {
    const experiences = parseJsonSafe(profile?.experiences, []);
    const projects = parseJsonSafe(profile?.github_projects, []);

    const timeline = [];

    experiences.forEach((exp, i) => {
      timeline.push({
        id: `exp-${i}`,
        title: exp.role || 'Experience',
        subtitle: exp.role?.toLowerCase().includes('intern') ? 'internship' : 'experience',
        date: exp.period || 'Recent',
        type: exp.role?.toLowerCase().includes('intern') ? 'internship' : 'experience',
        rawDate: exp.period
      });
    });

    projects.forEach((proj, i) => {
      timeline.push({
        id: `proj-${i}`,
        title: proj.title || 'Project',
        subtitle: 'project',
        date: proj.createdAt || proj.updatedAt || proj.date || 'Recent',
        type: 'project',
        rawDate: proj.createdAt || proj.updatedAt || proj.date
      });
    });

    if (timeline.length === 0) {
      timeline.push({ id: 'mock1', title: 'Web Development Intern', subtitle: 'internship', date: 'August 19 2025', type: 'internship' });
      timeline.push({ id: 'mock2', title: 'Trackify - Smart Personal Expense Tracker', subtitle: 'project', date: 'November 6 2025', type: 'project' });
      timeline.push({ id: 'mock4', title: 'ANN & DEEP LEARNING', subtitle: 'course', date: 'November 21 2025', type: 'course' });
      timeline.push({ id: 'mock5', title: 'BIG DATA ANALYTICS', subtitle: 'course', date: 'November 21 2025', type: 'course' });
      timeline.push({ id: 'mock6', title: 'BUSINESS INTELLIGENCE & DATA VISUALIZATION', subtitle: 'course', date: 'November 21 2025', type: 'course' });
      timeline.push({ id: 'mock7', title: 'DATA MINING & PREDICTIVE MODELLING', subtitle: 'course', date: 'November 21 2025', type: 'course' });
    } else {
      timeline.sort((a, b) => {
        const dateA = new Date(a.rawDate || a.date);
        const dateB = new Date(b.rawDate || b.date);
        if (!isNaN(dateA) && !isNaN(dateB)) return dateB - dateA; // newest first or oldest first? let's do oldest first like the image seems to imply?
        // Wait, image is Aug 19 -> Nov 6 -> Nov 10. That's ascending (dateA - dateB).
        // Let's use dateA - dateB for oldest first.
        if (!isNaN(dateA) && !isNaN(dateB)) return dateA - dateB;
        return 0;
      });
    }

    return timeline;
  };

  const formatTimelineDate = (dateStr) => {
    if (!dateStr || dateStr === 'Recent') return { month: 'Recent', day: '', year: '' };

    const s = String(dateStr).trim();

    // Handle period ranges like "Jan 2024 - Mar 2024" or "2023-06 - Present" → take the start
    // Do not split ISO dates like "2025-08-08"
    let rangePart = s;
    if (!/^\d{4}-\d{2}-\d{2}/.test(s)) {
      rangePart = s.split(/\s*[-–—]\s*/)[0].trim();
    }

    // Try parsing as ISO / standard JS date first
    const tryParse = (str) => {
      const d = new Date(str);
      if (!isNaN(d.getTime())) return d;
      return null;
    };

    let parsed = tryParse(rangePart) || tryParse(s);

    if (parsed) {
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return {
        month: monthNames[parsed.getMonth()],
        day: String(parsed.getDate()),
        year: String(parsed.getFullYear())
      };
    }

    // Handle "Month Year" or "Month Day Year" plain strings
    const parts = rangePart.replace(/,/g, '').split(/\s+/);
    if (parts.length >= 3) {
      const year = parts[parts.length - 1];
      const day = parts[parts.length - 2];
      const month = parts.slice(0, parts.length - 2).join(' ');
      // Only treat as day if it's numeric
      if (/^\d+$/.test(day)) return { month, day, year };
      return { month: parts.slice(0, parts.length - 1).join(' '), day: '', year };
    }
    if (parts.length === 2) return { month: parts[0], day: '', year: parts[1] };

    return { month: s, day: '', year: '' };
  };

  const renderTimelineTab = () => {
    const data = getTimelineData();

    const getTypeColor = (type) => {
      switch (type) {
        case 'internship': return '#f3e8ff';
        case 'event': return '#f3e8ff';
        case 'project': return '#ffedd5';
        case 'course': return '#e0f2fe';
        case 'experience': return '#f3e8ff';
        default: return '#f1f5f9';
      }
    };

    return (
      <div className="stu-fade-in" style={{ padding: '28px', background: '#ffffff', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 4px 25px rgba(0,0,0,0.04)', fontFamily: "'Inter', sans-serif", minHeight: '600px' }}>
        <div style={{ position: 'relative', maxWidth: '800px', margin: '0 auto', padding: '40px 0' }}>
          {/* Central Line */}
          <div style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', top: '0', bottom: '0', width: '2px', background: '#3b82f6' }}></div>

          {data.map((item, index) => {
            const isLeft = index % 2 !== 0;
            const cardBg = getTypeColor(item.type);
            const { month, day, year } = formatTimelineDate(item.date);

            return (
              <div key={item.id} style={{ display: 'flex', justifyContent: isLeft ? 'flex-start' : 'flex-end', width: '100%', marginBottom: '40px', position: 'relative' }}>

                {/* Center Icon */}
                <div style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', width: '28px', height: '28px', borderRadius: '50%', background: '#ffffff', border: '2px solid #3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#3b82f6', zIndex: 1 }}>
                  {item.type === 'project' ? <Folder size={14} /> : item.type === 'course' ? <BookOpen size={14} /> : <ClipboardCheck size={14} />}
                </div>

                {/* Card */}
                <div style={{ width: '45%', display: 'flex', justifyContent: isLeft ? 'flex-end' : 'flex-start' }}>
                  <div style={{ background: cardBg, padding: '16px 20px', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', maxWidth: '350px', gap: '16px' }}>
                    <div style={{ flex: 1 }}>
                      <h4 style={{ fontWeight: '700', fontSize: '13px', color: '#0f172a', margin: '0 0 4px 0', lineHeight: '1.4' }}>{item.title.toUpperCase()}</h4>
                      <div style={{ fontSize: '12px', color: '#64748b' }}>{item.subtitle}</div>
                    </div>
                    <div style={{ borderLeft: '1px dashed #94a3b8', paddingLeft: '16px', textAlign: 'center', minWidth: '70px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      <div style={{ fontSize: '11px', fontWeight: '600', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{month}</div>
                      {day && <div style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a', lineHeight: '1' }}>{day}</div>}
                      {year && <div style={{ fontSize: '12px', fontWeight: '600', color: '#64748b' }}>{year}</div>}
                    </div>
                  </div>
                </div>

              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderAssessmentTab = () => {
    if (isGeneratingQuiz) {
      return (
        <div className="stu-fade-in" style={{ padding: '60px 20px', minHeight: '450px', textAlign: 'center', background: '#ffffff', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', fontFamily: "'Inter', sans-serif" }}>
          <div style={{ width: '72px', height: '72px', borderRadius: '50%', background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px auto' }}>
            <Sparkles size={36} color="#2563eb" className="spin" />
          </div>
          <h3 style={{ fontSize: '22px', fontWeight: '800', color: '#0f172a', marginBottom: '10px' }}>
            🤖 AI Generating Your Custom Portfolio Quiz...
          </h3>
          <p style={{ fontSize: '15px', color: '#64748b', maxWidth: '520px', margin: '0 auto 28px auto', lineHeight: '1.6' }}>
            Analyzing your synced GitHub repositories, tech stacks, and architecture patterns to formulate a dynamic competency benchmark.
          </p>

        </div>
      );
    }

    if (activeQuiz) {
      const questions = activeQuiz.questions || [];
      const currentQ = questions[currentQuestionIdx] || {};
      const totalQ = questions.length;

      const handleSelectOption = (optIdx) => {
        if (quizSubmitted) return;
        setUserAnswers({ ...userAnswers, [currentQuestionIdx]: optIdx });
      };

      const calculateScore = () => {
        let correct = 0;
        questions.forEach((q, idx) => {
          if (userAnswers[idx] === q.correct_answer) correct++;
        });
        return Math.round((correct / (totalQ || 1)) * 100);
      };

      if (quizSubmitted) {
        const score = calculateScore();
        return (
          <div className="stu-fade-in" style={{ padding: '32px', background: '#ffffff', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 4px 25px rgba(0,0,0,0.04)', fontFamily: "'Inter', sans-serif" }}>
            <div style={{ textAlign: 'center', marginBottom: '32px', paddingBottom: '24px', borderBottom: '1px solid #f1f5f9' }}>
              <div style={{ width: '76px', height: '76px', borderRadius: '50%', background: score >= 70 ? '#f0fdf4' : '#fff7ed', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px auto' }}>
                <Trophy size={40} color={score >= 70 ? '#16a34a' : '#ea580c'} />
              </div>
              <h2 style={{ fontSize: '26px', fontWeight: '800', color: '#0f172a', margin: '0 0 8px 0' }}>
                {score >= 80 ? '🏆 Exemplary Technical Mastery!' : score >= 60 ? '✨ Certified Proficiency!' : '📚 Keep Building & Practicing!'}
              </h2>
              <p style={{ fontSize: '14.5px', color: '#64748b', maxWidth: '550px', margin: '0 auto 20px auto' }}>
                You completed the AI Mock Quiz generated based on your {activeQuiz.project_count || 'GitHub'} repositories!
              </p>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '24px', background: '#f8fafc', padding: '18px 32px', borderRadius: '16px', border: '1px solid #e2e8f0' }}>
                {quizEvaluationResult && quizEvaluationResult.previous_assessment_score !== null && quizEvaluationResult.previous_assessment_score !== undefined && (
                  <>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', marginBottom: '4px' }}>Previous Score</div>
                      <div style={{ fontSize: '32px', fontWeight: '800', color: '#94a3b8' }}>
                        {quizEvaluationResult.previous_assessment_score}%
                      </div>
                    </div>
                    <div style={{ width: '1px', height: '40px', background: '#e2e8f0' }}></div>
                  </>
                )}
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', marginBottom: '4px' }}>New Score</div>
                  <div style={{ fontSize: '32px', fontWeight: '800', color: score >= 70 ? '#16a34a' : '#ea580c' }}>
                    {score}%
                  </div>
                </div>
                <div style={{ width: '1px', height: '40px', background: '#e2e8f0' }}></div>
                <div style={{ textAlign: 'left' }}>
                  <div style={{ fontSize: '13.5px', fontWeight: '700', color: '#0f172a' }}>{Object.keys(userAnswers).length} / {totalQ} Answered</div>
                  <div style={{ fontSize: '12px', color: '#16a34a', fontWeight: '600' }}>✔ Verified by AI LLM</div>
                </div>
              </div>
            </div>

            {/* AI Skillset & Toolset Alignment Analysis Box */}
            {isEvaluatingQuiz && (
              <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '16px', padding: '24px', marginBottom: '28px', textAlign: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', color: '#1d4ed8', fontSize: '16px', fontWeight: '700', marginBottom: '8px' }}>
                  <Sparkles size={20} className="spin" /> Evaluating Portfolio Alignment...
                </div>
                <p style={{ fontSize: '13.5px', color: '#3b82f6', margin: 0 }}>
                  AI is cross-referencing your quiz score with your evaluated GitHub Toolset and Skillset to update your verified profile competencies.
                </p>
              </div>
            )}

            {quizEvaluationResult && (
              <div style={{ background: 'linear-gradient(135deg, #f0fdf4 0%, #eff6ff 100%)', border: '1px solid #bbf7d0', borderRadius: '16px', padding: '24px', marginBottom: '28px', textAlign: 'left' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '16px', paddingBottom: '14px', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ width: '38px', height: '38px', borderRadius: '10px', background: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
                      <Sparkles size={20} />
                    </div>
                    <div>
                      <h3 style={{ fontSize: '17px', fontWeight: '800', color: '#0f172a', margin: 0 }}>
                        AI Skillset &amp; Toolset Alignment Verdict
                      </h3>
                      <span style={{ fontSize: '12px', color: '#16a34a', fontWeight: '700' }}>
                        ✔ Profile Competencies Automatically Updated
                      </span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <span style={{ padding: '6px 14px', background: '#ffffff', borderRadius: '20px', border: '1px solid #bbf7d0', fontSize: '13px', fontWeight: '800', color: '#15803d' }}>
                      {quizEvaluationResult.alignment_verdict || 'Strongly Aligned'}
                    </span>
                    <span style={{ padding: '6px 14px', background: '#16a34a', borderRadius: '20px', fontSize: '13px', fontWeight: '800', color: '#ffffff' }}>
                      {quizEvaluationResult.match_percentage || Math.min(100, score + 10)}% Match
                    </span>
                  </div>
                </div>

                <p style={{ fontSize: '14.5px', color: '#334155', lineHeight: '1.6', marginBottom: '18px', background: '#ffffff', padding: '16px', borderRadius: '12px', border: '1px solid rgba(0,0,0,0.05)' }}>
                  💡 <strong>AI Assessment Analysis:</strong> {quizEvaluationResult.alignment_summary}
                </p>

                {quizEvaluationResult.validated_items && quizEvaluationResult.validated_items.length > 0 && (
                  <div style={{ marginBottom: '14px' }}>
                    <div style={{ fontSize: '12px', fontWeight: '700', color: '#15803d', textTransform: 'uppercase', marginBottom: '8px' }}>
                      ✨ Validated Tools &amp; Skills (Boosted in Profile):
                    </div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {quizEvaluationResult.validated_items.map((item, i) => (
                        <span key={i} style={{ padding: '5px 12px', background: '#dcfce7', color: '#166534', borderRadius: '8px', fontSize: '13px', fontWeight: '700', border: '1px solid #86efac' }}>
                          ✔ {item}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {quizEvaluationResult.improvement_items && quizEvaluationResult.improvement_items.length > 0 && (
                  <div>
                    <div style={{ fontSize: '12px', fontWeight: '700', color: '#b45309', textTransform: 'uppercase', marginBottom: '8px' }}>
                      🎯 Recommended Focus Areas:
                    </div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {quizEvaluationResult.improvement_items.map((item, i) => (
                        <span key={i} style={{ padding: '5px 12px', background: '#fef3c7', color: '#92400e', borderRadius: '8px', fontSize: '13px', fontWeight: '600', border: '1px solid #fde68a' }}>
                          📌 {item}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <h4 style={{ fontSize: '18px', fontWeight: '700', color: '#0f172a', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <CheckCircle size={20} color="#2563eb" /> Review AI Question Breakdown
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '28px' }}>
              {questions.map((q, idx) => {
                const userAns = userAnswers[idx];
                const isCorrect = userAns === q.correct_answer;
                return (
                  <div key={idx} style={{ padding: '20px', background: '#f8fafc', borderRadius: '14px', border: isCorrect ? '1px solid #bbf7d0' : '1px solid #fecaca' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px', gap: '12px' }}>
                      <span style={{ fontSize: '11px', fontWeight: '700', background: '#e0e7ff', color: '#4338ca', padding: '4px 12px', borderRadius: '12px' }}>
                        {q.project_reference || `Question ${idx + 1}`}
                      </span>
                      <span style={{ fontSize: '13px', fontWeight: '700', color: isCorrect ? '#16a34a' : '#dc2626', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        {isCorrect ? '✔ Correct' : '✖ Incorrect'}
                      </span>
                    </div>
                    <div style={{ fontSize: '15px', fontWeight: '700', color: '#0f172a', marginBottom: '14px', lineHeight: '1.4' }}>
                      {idx + 1}. {q.question}
                    </div>
                    <div style={{ fontSize: '13.5px', color: '#334155', background: '#ffffff', padding: '14px', borderRadius: '10px', border: '1px solid #e2e8f0', marginBottom: '10px', lineHeight: '1.5' }}>
                      <div style={{ marginBottom: '6px' }}>
                        <strong style={{ color: '#64748b' }}>Your Answer:</strong> {userAns !== undefined ? q.options[userAns] : 'Not answered'}
                      </div>
                      {!isCorrect && (
                        <div style={{ color: '#16a34a' }}>
                          <strong>Correct Answer:</strong> {q.options[q.correct_answer]}
                        </div>
                      )}
                    </div>
                    {q.explanation && (
                      <div style={{ fontSize: '13px', color: '#475569', fontStyle: 'italic', background: '#f1f5f9', padding: '10px 14px', borderRadius: '8px' }}>
                        💡 <strong>AI Explanation:</strong> {q.explanation}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button
                onClick={() => { setActiveQuiz(null); setQuizSubmitted(false); }}
                style={{ padding: '12px 24px', background: '#0046b8', color: '#ffffff', border: 'none', borderRadius: '10px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s' }}
              >
                ← Return to Assessments Overview
              </button>
            </div>
          </div>
        );
      }

      if (isDisqualified) {
        const violations = disqualificationData?.violations || [];
        return (
          <div className="stu-fade-in" style={{ padding: '32px', background: '#ffffff', borderRadius: '16px', border: '1px solid #fecaca', boxShadow: '0 4px 25px rgba(239,68,68,0.08)', fontFamily: "'Inter', sans-serif" }}>
            <div style={{ textAlign: 'center', marginBottom: '28px', paddingBottom: '24px', borderBottom: '1px solid #fee2e2' }}>
              <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: '#fef2f2', border: '2px solid #fca5a5', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 18px auto' }}>
                <XCircle size={44} color="#dc2626" />
              </div>
              <span style={{ fontSize: '12px', fontWeight: '800', background: '#fee2e2', color: '#991b1b', padding: '4px 14px', borderRadius: '16px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Assessment Disqualified
              </span>
              <h2 style={{ fontSize: '26px', fontWeight: '800', color: '#991b1b', margin: '10px 0 8px 0' }}>
                🚫 Test Terminated due to Proctoring Violations
              </h2>
              <p style={{ fontSize: '14.5px', color: '#7f1d1d', maxWidth: '600px', margin: '0 auto 20px auto', lineHeight: '1.6' }}>
                You have exceeded the maximum allowed limit of 2 tab-switch / fullscreen violations during this session.
                The assessment has been automatically marked as <strong>Disqualified</strong> and submitted for Faculty Review.
              </p>

              {proctorReportQueued && (
                <div style={{ maxWidth: '600px', margin: '0 auto', padding: '12px 16px', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '10px', color: '#92400e', fontSize: '13px', fontWeight: '600', textAlign: 'left' }}>
                  ⚠️ The proctoring report could not be sent to the server. It has been stored on this device and will be retried automatically the next time you open your dashboard — no data has been lost.
                </div>
              )}
            </div>

            {/* Violation Log Breakdown */}
            <h4 style={{ fontSize: '17px', fontWeight: '700', color: '#0f172a', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Shield size={20} color="#dc2626" /> Incident Violation Log ({violations.length} Recorded)
            </h4>


            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '28px' }}>
              {violations.map((v, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '14px 18px', background: '#fff1f2', borderRadius: '12px', border: '1px solid #fecaca' }}>
                  {v.snapshot ? (
                    <img src={v.snapshot} alt={`Violation ${i+1}`} style={{ width: '80px', height: '60px', borderRadius: '8px', objectFit: 'cover', border: '1px solid #fca5a5' }} />
                  ) : (
                    <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#dc2626', fontWeight: '800' }}>
                      #{i+1}
                    </div>
                  )}
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                      <span style={{ fontSize: '14px', fontWeight: '700', color: '#991b1b' }}>{v.message}</span>
                      <span style={{ fontSize: '12px', color: '#991b1b', fontWeight: '600' }}>{v.timestamp}</span>
                    </div>
                    <div style={{ fontSize: '12px', color: '#b91c1c' }}>Type: {v.type}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Faculty Retake Appeal Section */}
            <div style={{ marginTop: '24px', marginBottom: '28px', padding: '24px', background: '#f8fafc', borderRadius: '14px', border: '1.5px solid #e2e8f0' }}>
              {profile?.retake_granted_by_faculty ? (
                <div style={{ background: '#ecfdf5', border: '1.5px solid #10b981', borderRadius: '12px', padding: '18px 22px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#065f46', fontWeight: '800', fontSize: '16px', marginBottom: '6px' }}>
                    <Sparkles size={22} color="#059669" /> Retake Granted by Faculty!
                  </div>
                  <p style={{ color: '#047857', fontSize: '14px', margin: '0 0 16px 0', lineHeight: 1.5 }}>
                    Your faculty has audited your proctoring log and approved an immediate retake. You do not need to wait for the 30-day cooldown.
                  </p>
                  <button
                    onClick={() => {
                      setIsDisqualified(false);
                      setDisqualificationData(null);
                      startAssessment();
                    }}
                    style={{
                      padding: '10px 22px', background: '#059669', color: '#ffffff',
                      border: 'none', borderRadius: '8px', fontWeight: '700', fontSize: '14px', cursor: 'pointer'
                    }}
                  >
                    Start Assessment Now →
                  </button>
                </div>
              ) : (appealSubmittedSuccess || profile?.retake_requested_at) ? (
                <div style={{ background: '#fffbeb', border: '1.5px solid #f59e0b', borderRadius: '12px', padding: '18px 22px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#92400e', fontWeight: '800', fontSize: '15.5px', marginBottom: '6px' }}>
                    <Clock size={20} color="#d97706" /> Retake Request Pending Faculty Review
                  </div>
                  <p style={{ color: '#b45309', fontSize: '13.5px', margin: '0 0 10px 0', lineHeight: 1.5 }}>
                    Your explanation has been submitted for Faculty review. Only your Faculty has permission to review your violation snapshots and incident report.
                  </p>
                  <div style={{ background: '#ffffff', border: '1px solid #fcd34d', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', color: '#78350f' }}>
                    <strong>Your submitted explanation:</strong> "{appealReason || profile?.retake_reason || 'Accidental tab switch / technical issue'}"
                  </div>
                  <div style={{ fontSize: '12px', color: '#92400e', marginTop: '8px' }}>
                    Submitted: {profile?.retake_requested_at ? new Date(profile.retake_requested_at).toLocaleString() : 'Just now'}
                  </div>
                </div>
              ) : (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#0f172a', fontWeight: '800', fontSize: '16px', marginBottom: '8px' }}>
                    <AlertTriangle size={20} color="#f59e0b" /> Appeal Disqualification to Faculty
                  </div>
                  <p style={{ fontSize: '13.5px', color: '#64748b', margin: '0 0 14px 0', lineHeight: 1.5 }}>
                    If this termination was caused by an accidental trackpad gesture, system notification, or network freeze, submit an appeal below. <strong>Only your Faculty</strong> has secure access to view the snapshots and can grant an immediate retake.
                  </p>
                  <form onSubmit={handleRequestRetakeAppeal}>
                    <textarea
                      value={appealReason}
                      onChange={(e) => setAppealReason(e.target.value)}
                      placeholder="Explain what happened during your assessment (at least 8 characters)..."
                      rows={3}
                      style={{
                        width: '100%', padding: '12px 14px', borderRadius: '10px',
                        border: '1.5px solid #cbd5e1', fontSize: '13.5px', fontFamily: 'inherit',
                        resize: 'vertical', boxSizing: 'border-box', marginBottom: '12px',
                        outline: 'none', transition: 'border-color 0.2s'
                      }}
                    />
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                      <button
                        type="submit"
                        disabled={isSubmittingAppeal || !appealReason || appealReason.trim().length < 8}
                        style={{
                          padding: '10px 20px',
                          background: (isSubmittingAppeal || !appealReason || appealReason.trim().length < 8) ? '#94a3b8' : '#2563eb',
                          color: '#ffffff', border: 'none', borderRadius: '8px',
                          fontWeight: '700', fontSize: '13.5px', cursor: (isSubmittingAppeal || !appealReason || appealReason.trim().length < 8) ? 'not-allowed' : 'pointer',
                          display: 'flex', alignItems: 'center', gap: '8px'
                        }}
                      >
                        {isSubmittingAppeal ? <Loader2 size={16} className="animate-spin" /> : <ShieldAlert size={16} />}
                        {isSubmittingAppeal ? 'Submitting Appeal...' : 'Submit Appeal to Faculty'}
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button
                onClick={() => { setActiveQuiz(null); setIsDisqualified(false); setDisqualificationData(null); }}
                style={{ padding: '12px 24px', background: '#0f172a', color: '#ffffff', border: 'none', borderRadius: '10px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s' }}
              >
                ← Return to Assessments Overview
              </button>
            </div>
          </div>
        );
      }

      return (
        <div className="stu-fade-in" style={{ padding: '28px', background: '#ffffff', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 4px 25px rgba(0,0,0,0.04)', fontFamily: "'Inter', sans-serif" }}>
          {/* Active AI Proctoring Monitor Component */}
          <ProctoringCam
            assessmentTitle={activeQuiz.title || "AI Mock Competency Assessment"}
            isActive={isSubEnabled('proctoring', 'proctored_interviews')}
            onDisqualified={(violationsList, report) => {
              setIsDisqualified(true);
              setDisqualificationData({ violations: violationsList, report });
              setQuizSubmitted(false);
              // report === null means the save failed and the report was queued
              // locally for retry — surface it instead of failing silently.
              const queued = !report;
              setProctorReportQueued(queued);
              if (queued) {
                showToast("⚠️ The proctoring report could not be delivered. It is stored on this device and will be retried automatically.", 'warning');
              }
            }}
          />

          {/* Quiz Header */}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px', paddingBottom: '16px', borderBottom: '1px solid #f1f5f9' }}>
            <div>
              <span style={{ fontSize: '11px', fontWeight: '700', background: '#eff6ff', color: '#2563eb', padding: '4px 12px', borderRadius: '16px', textTransform: 'uppercase', marginBottom: '6px', display: 'inline-block' }}>
                ⚡ AI Custom Quiz • {activeQuiz.project_count || 'GitHub'} Projects Synced
              </span>
              <h3 style={{ fontSize: '20px', fontWeight: '800', color: '#0f172a', margin: 0 }}>
                {activeQuiz.title || 'AI Mock Competency Assessment'}
              </h3>
            </div>
            <button
              onClick={() => { setActiveQuiz(null); }}
              style={{ padding: '8px 16px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.15s' }}
            >
              ✕ Exit Quiz
            </button>
          </div>

          {/* Progress Bar */}
          <div style={{ marginBottom: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', fontWeight: '600', color: '#64748b', marginBottom: '8px' }}>
              <span>Question {currentQuestionIdx + 1} of {totalQ}</span>
              <span>{Math.round(((currentQuestionIdx + 1) / totalQ) * 100)}% Completed</span>
            </div>
            <div style={{ width: '100%', height: '8px', background: '#f1f5f9', borderRadius: '4px', overflow: 'hidden' }}>
              <div style={{ width: `${((currentQuestionIdx + 1) / totalQ) * 100}%`, height: '100%', background: 'linear-gradient(90deg, #0052cc 0%, #ff7a00 100%)', borderRadius: '4px', transition: 'width 0.3s ease' }}></div>
            </div>
          </div>

          {/* Question Box */}
          <div style={{ background: '#f8fafc', padding: '24px', borderRadius: '14px', border: '1px solid #e2e8f0', marginBottom: '24px' }}>
            {currentQ.project_reference && (
              <div style={{ fontSize: '12px', fontWeight: '700', color: '#ea580c', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                📁 Tested Context: {currentQ.project_reference}
              </div>
            )}
            <h4 style={{ fontSize: '18px', fontWeight: '700', color: '#0f172a', margin: 0, lineHeight: '1.5' }}>
              {currentQuestionIdx + 1}. {currentQ.question}
            </h4>
          </div>

          {/* Options */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '28px' }}>
            {currentQ.options?.map((opt, optIdx) => {
              const isSelected = userAnswers[currentQuestionIdx] === optIdx;
              return (
                <div
                  key={optIdx}
                  onClick={() => handleSelectOption(optIdx)}
                  style={{
                    padding: '16px 20px', borderRadius: '12px', cursor: 'pointer',
                    background: isSelected ? '#eff6ff' : '#ffffff',
                    border: isSelected ? '2px solid #2563eb' : '1px solid #cbd5e1',
                    display: 'flex', alignItems: 'center', gap: '14px', transition: 'all 0.15s',
                    boxShadow: isSelected ? '0 4px 12px rgba(37, 99, 235, 0.1)' : 'none'
                  }}
                >
                  <div style={{
                    width: '30px', height: '30px', borderRadius: '50%',
                    background: isSelected ? '#2563eb' : '#f1f5f9',
                    color: isSelected ? '#ffffff' : '#475569',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '13.5px', fontWeight: '700', flexShrink: 0
                  }}>
                    {String.fromCharCode(65 + optIdx)}
                  </div>
                  <div style={{ fontSize: '14.5px', fontWeight: isSelected ? '600' : '500', color: isSelected ? '#1e3a8a' : '#1e293b' }}>
                    {opt}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer Navigation */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', paddingTop: '16px', borderTop: '1px solid #f1f5f9' }}>
            <button
              onClick={() => setCurrentQuestionIdx(Math.max(0, currentQuestionIdx - 1))}
              disabled={currentQuestionIdx === 0}
              style={{
                padding: '10px 20px', background: '#f1f5f9', color: currentQuestionIdx === 0 ? '#cbd5e1' : '#475569',
                border: 'none', borderRadius: '10px', fontWeight: '600', cursor: currentQuestionIdx === 0 ? 'not-allowed' : 'pointer', transition: 'all 0.15s'
              }}
            >
              ← Previous
            </button>

            <div style={{ display: 'flex', gap: '12px' }}>
              {currentQuestionIdx < totalQ - 1 ? (
                <button
                  onClick={() => setCurrentQuestionIdx(currentQuestionIdx + 1)}
                  style={{
                    padding: '10px 24px', background: '#0046b8', color: '#ffffff',
                    border: 'none', borderRadius: '10px', fontWeight: '600', cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(0, 70, 184, 0.2)', transition: 'all 0.15s'
                  }}
                >
                  Next Question →
                </button>
              ) : (
                <button
                  onClick={() => {
                    if (Object.keys(userAnswers).length < totalQ) {
                      showToast("Please answer all questions before submitting!", "warning");
                      return;
                    }
                    handleSubmitQuiz();
                  }}
                  style={{
                    padding: '10px 28px', background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)', color: '#ffffff',
                    border: 'none', borderRadius: '10px', fontWeight: '700', cursor: 'pointer',
                    boxShadow: '0 4px 15px rgba(22, 163, 74, 0.3)', transition: 'all 0.15s', display: 'flex', alignItems: 'center', gap: '8px'
                  }}
                >
                  <Sparkles size={16} /> Submit &amp; Get AI Score
                </button>
              )}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="stu-fade-in" style={{ padding: '10px 0', minHeight: '400px', fontFamily: "'Inter', sans-serif" }}>
        {/* Section Header */}
        <div style={{ marginBottom: '24px' }}>
          <h2 style={{ fontSize: '24px', fontWeight: '800', color: '#0f172a', margin: '0 0 6px 0', letterSpacing: '-0.5px' }}>
            Assessments
          </h2>
          <p style={{ fontSize: '14px', color: '#64748b', margin: 0 }}>
            Manage and track assessment progress
          </p>
        </div>

        {/* Cards Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 450px))', gap: '24px' }}>
          {/* Card 1: Rank your Projects */}
          <div style={{
            background: '#ffffff',
            borderRadius: '16px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.03)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            overflow: 'hidden',
            transition: 'all 0.2s ease',
            position: 'relative'
          }}>
            {/* Top Accent Bar */}
            <div style={{
              height: '6px',
              width: '100%',
              background: 'var(--primary)'
            }}></div>

            {/* Card Body */}
            <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', flex: 1, justifyContent: 'space-between' }}>
              <div>
                {/* Header Row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
                  <div style={{
                    width: '46px', height: '46px', borderRadius: '12px', background: 'var(--primary-dim)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                  }}>
                    <Link2 size={24} color="var(--primary)" />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                    <h3 style={{ fontSize: '19px', fontWeight: '700', color: 'var(--text-main)', margin: 0 }}>
                      Rank your Projects
                    </h3>
                    <span style={{
                      background: 'var(--primary-dim)', color: 'var(--primary)', fontSize: '12px', fontWeight: '700',
                      padding: '4px 12px', borderRadius: '16px', textTransform: 'none'
                    }}>
                      Mandatory
                    </span>
                  </div>
                </div>

                {/* Description */}
                <p style={{ fontSize: '14px', color: '#64748b', margin: '0 0 24px 0', lineHeight: '1.5' }}>
                  Rank your projects and answer a short questionnaire. Get one step closer to your dream job.
                </p>
              </div>

              {(() => {
                let history = profile?.assessment_attempts_history;
                if (typeof history === 'string') {
                  try { history = JSON.parse(history); } catch (e) { history = []; }
                }
                const attemptsList = Array.isArray(history) ? history : [];
                const attemptsCount = attemptsList.length;
                const isCompleted = profile?.last_assessment_score !== null && profile?.last_assessment_score !== undefined;
                const monthlyAssessment = profile?.monthly_limits?.assessment;
                const isDisq = profile?.assessment_disqualified || monthlyAssessment?.is_disqualified;
                const isRetake = profile?.retake_granted_by_faculty || monthlyAssessment?.retake_granted_by_faculty;
                const hasPendingAppeal = (profile?.retake_requested_at || monthlyAssessment?.retake_requested_at) && !isRetake;
                const canTake = isRetake || (monthlyAssessment ? monthlyAssessment.can_perform : true);
                const daysRemaining = monthlyAssessment?.days_remaining || 0;
                const nextDate = monthlyAssessment?.next_date_formatted || '';

                let statusText = isCompleted ? 'Completed' : (attemptsCount > 0 ? 'Retake Available' : 'In progress');
                let statusColor = isCompleted ? 'var(--green)' : 'var(--primary)';
                let statusBg = isCompleted ? 'rgba(34, 197, 94, 0.15)' : 'var(--primary-dim)';

                if (isDisq && !isRetake) {
                  if (hasPendingAppeal) {
                    statusText = 'Retake Pending Review';
                    statusColor = '#d97706';
                    statusBg = '#fef3c7';
                  } else {
                    statusText = 'Disqualified';
                    statusColor = '#dc2626';
                    statusBg = '#fee2e2';
                  }
                } else if (isRetake) {
                  statusText = 'Retake Granted';
                  statusColor = '#059669';
                  statusBg = '#d1fae5';
                } else if (!canTake) {
                  statusText = `Locked (${daysRemaining}d)`;
                  statusColor = '#64748b';
                  statusBg = '#f1f5f9';
                }

                const lastActivity = attemptsCount > 0 ? attemptsList[attemptsList.length - 1].date : 'No activity yet';

                return (
                  <div>
                    {/* Status Pills Row */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', marginBottom: '18px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                        <span style={{
                          background: '#f1f5f9', color: '#0f172a', fontSize: '13px', fontWeight: '600',
                          padding: '6px 14px', borderRadius: '20px', display: 'flex', alignItems: 'center', gap: '6px'
                        }}>
                          <CheckCircle size={15} color="var(--primary)" /> {attemptsCount} attempts
                        </span>
                        <span style={{
                          background: statusBg, color: statusColor, fontSize: '13px', fontWeight: '600',
                          padding: '6px 14px', borderRadius: '20px', border: 'none', display: 'flex', alignItems: 'center', gap: '6px'
                        }}>
                          <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: statusColor }}></span> {statusText}
                        </span>
                      </div>
                      <span style={{ fontSize: '12.5px', color: '#94a3b8', fontWeight: '500' }}>
                        Last activity: {lastActivity}
                      </span>
                    </div>

                    {/* Explanatory Alert Banner */}
                    {isDisq && !isRetake && (
                      <div style={{
                        marginBottom: '20px', padding: '12px 16px', borderRadius: '10px',
                        background: hasPendingAppeal ? '#fffbeb' : '#fef2f2',
                        border: `1px solid ${hasPendingAppeal ? '#fde68a' : '#fca5a5'}`,
                        color: hasPendingAppeal ? '#92400e' : '#991b1b',
                        fontSize: '13px', lineHeight: 1.5
                      }}>
                        {hasPendingAppeal ? (
                          <>
                            <strong>⏳ Appeal Pending Faculty Review:</strong> Your retake appeal submitted on{' '}
                            {profile?.retake_requested_at ? new Date(profile.retake_requested_at).toLocaleDateString() : 'recently'}{' '}
                            is awaiting Faculty review. Only Faculty can audit violation snapshots and approve retakes.
                          </>
                        ) : (
                          <>
                            <strong>🚫 Proctoring Disqualification:</strong> Your previous assessment was disqualified due to tab switches.
                            You can submit an explanation appeal to your Faculty to request a retake.
                          </>
                        )}
                      </div>
                    )}

                    {isRetake && (
                      <div style={{
                        marginBottom: '20px', padding: '12px 16px', borderRadius: '10px',
                        background: '#ecfdf5', border: '1px solid #a7f3d0', color: '#065f46',
                        fontSize: '13px', lineHeight: 1.5
                      }}>
                        <strong>✨ Retake Granted by Faculty!</strong> Your Faculty audited your report and approved an immediate retake without waiting 30 days.
                      </div>
                    )}

                    {!isDisq && !isRetake && !canTake && (
                      <div style={{
                        marginBottom: '20px', padding: '12px 16px', borderRadius: '10px',
                        background: '#f8fafc', border: '1px solid #cbd5e1', color: '#475569',
                        fontSize: '13px', lineHeight: 1.5
                      }}>
                        <strong>🔒 Monthly Cooldown:</strong> Assessments are limited to monthly once to accurately measure your Skill Gap progression over time. Next attempt unlocks on <strong>{nextDate}</strong> ({daysRemaining} days remaining).
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                      <button
                        onClick={() => {
                          if (attemptsCount === 0) {
                            showToast("You haven't taken any assessments yet.", "info");
                            return;
                          }
                          setShowAttemptsModal(true);
                        }}
                        style={{
                          background: '#ffffff', border: '1.5px solid #0f172a', color: '#0f172a',
                          fontSize: '13.5px', fontWeight: '600', padding: '10px 18px', borderRadius: '10px',
                          cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: '6px'
                        }}
                      >
                        View attempts
                      </button>

                      {isDisq && !isRetake ? (
                        hasPendingAppeal ? (
                          <button
                            disabled
                            style={{
                              background: '#e2e8f0', border: 'none', color: '#64748b',
                              fontSize: '13.5px', fontWeight: '600', padding: '10px 22px', borderRadius: '10px',
                              cursor: 'not-allowed', display: 'flex', alignItems: 'center', gap: '8px'
                            }}
                          >
                            <Clock size={16} /> Awaiting Faculty Review
                          </button>
                        ) : (
                          <button
                            onClick={() => setShowRetakeAppealModal(true)}
                            style={{
                              background: '#dc2626', border: 'none', color: '#ffffff',
                              fontSize: '13.5px', fontWeight: '600', padding: '10px 22px', borderRadius: '10px',
                              cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: '8px',
                              boxShadow: '0 2px 8px rgba(220, 38, 38, 0.25)'
                            }}
                          >
                            <AlertTriangle size={16} /> Appeal to Faculty →
                          </button>
                        )
                      ) : isRetake ? (
                        <button
                          onClick={handleJoinAssessment}
                          style={{
                            background: '#059669', border: 'none', color: '#ffffff',
                            fontSize: '13.5px', fontWeight: '600', padding: '10px 22px', borderRadius: '10px',
                            cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: '8px',
                            boxShadow: '0 2px 8px rgba(5, 150, 105, 0.3)'
                          }}
                        >
                          <Sparkles size={16} /> Retake Assessment Now →
                        </button>
                      ) : !canTake ? (
                        <button
                          disabled
                          style={{
                            background: '#94a3b8', border: 'none', color: '#ffffff',
                            fontSize: '13.5px', fontWeight: '600', padding: '10px 22px', borderRadius: '10px',
                            cursor: 'not-allowed', display: 'flex', alignItems: 'center', gap: '8px'
                          }}
                        >
                          <Lock size={15} /> Locked ({daysRemaining}d left)
                        </button>
                      ) : (
                        <button
                          onClick={handleJoinAssessment}
                          style={{
                            background: 'var(--primary)', border: 'none', color: 'var(--bg-page)',
                            fontSize: '13.5px', fontWeight: '600', padding: '10px 22px', borderRadius: '10px',
                            cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: '8px'
                          }}
                        >
                          {isCompleted ? 'Retake assessment' : 'Join assessment'} <span style={{ fontSize: '16px', lineHeight: '1' }}>→</span>
                        </button>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderProjectsTab = () => {
    const projectsList = parseJsonSafe(profile?.github_projects, []);
    const projectLimit = profile?.monthly_limits?.project;
    const canUploadProject = projectLimit ? projectLimit.can_perform : true;
    const projDaysRemaining = projectLimit?.days_remaining || 0;
    const projNextDate = projectLimit?.next_date_formatted || '';

    const getLangColor = (lang) => {
      const colors = {
        JavaScript: '#f1e05a', Python: '#3572A5', TypeScript: '#3178c6', HTML: '#e34c26',
        CSS: '#563d7c', Java: '#b07219', 'C++': '#f34b7d', C: '#555555', PHP: '#4F5D95',
        Ruby: '#701516', Go: '#00ADD8', Rust: '#dea584', Swift: '#F05138', Kotlin: '#A97BFF'
      };
      return colors[lang] || '#8b949e';
    };

    return (
      <div className="stu-fade-in" style={{ padding: '0' }}>
        {githubOAuthError && (
          <div style={{
            marginBottom: '20px', padding: '14px 18px', borderRadius: '10px',
            background: 'rgba(239, 68, 68, 0.08)', border: '1px solid rgba(239, 68, 68, 0.45)',
            color: '#fca5a5', fontSize: '13.5px', lineHeight: 1.6, display: 'flex',
            alignItems: 'flex-start', gap: '10px'
          }}>
            <AlertTriangle size={18} style={{ flexShrink: 0, marginTop: '2px' }} />
            <div>
              <strong style={{ display: 'block', marginBottom: '2px' }}>GitHub connection issue</strong>
              {githubOAuthError}
              <div style={{ marginTop: '10px' }}>
                <button
                  onClick={() => setGithubOAuthError(null)}
                  style={{ background: 'transparent', border: 'none', color: '#fca5a5', textDecoration: 'underline', cursor: 'pointer', fontSize: '12.5px', fontWeight: 600 }}
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        )}

        {!canUploadProject && (
          <div style={{
            marginBottom: '20px', padding: '14px 18px', borderRadius: '10px',
            background: 'rgba(59, 130, 246, 0.08)', border: '1px solid rgba(59, 130, 246, 0.3)',
            color: '#93c5fd', fontSize: '13.5px', lineHeight: 1.6, display: 'flex',
            alignItems: 'center', gap: '12px'
          }}>
            <Lock size={18} style={{ color: '#60a5fa', flexShrink: 0 }} />
            <div>
              <strong style={{ display: 'block', color: '#bfdbfe', marginBottom: '2px' }}>Monthly Project Limit Active</strong>
              Project uploads &amp; GitHub repository syncing are limited to once a month to track your continuous Skill Gap progression.
              Next update unlocks on <strong>{projNextDate}</strong> ({projDaysRemaining} days remaining).
            </div>
          </div>
        )}

        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginBottom: '28px', flexWrap: 'wrap', gap: '16px', background: 'var(--graphite-dark)',
          padding: '24px', borderRadius: '12px', border: '1px solid var(--border)'
        }}>
          <div>
            <h3 style={{ fontSize: '20px', fontWeight: '700', color: 'var(--proto-white)', display: 'flex', alignItems: 'center', gap: '10px', margin: '0 0 6px 0' }}>
              <Folder size={22} color="#3b82f6" /> Projects and GitHub Repository
            </h3>
            <p style={{ fontSize: '14px', color: 'var(--text-muted)', margin: 0 }}>
              {profile?.github_profile
                ? `Connected to GitHub account @${profile.github_profile}. Automatically imported repositories and custom projects.`
                : 'Connect your GitHub profile to automatically import all your repositories or add projects manually.'}
            </p>
          </div>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            {profile?.github_profile ? (
              <>
                <button
                  onClick={handleConnectGithub}
                  disabled={isFetchingGithub}
                  style={{
                    padding: '10px 18px', background: '#24292e', color: 'white', border: '1px solid #444d56',
                    borderRadius: '8px', fontSize: '13px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px',
                    cursor: 'pointer', transition: 'all 0.2s', boxShadow: '0 2px 6px rgba(0,0,0,0.15)'
                  }}
                >
                  <RefreshCw size={14} className={isFetchingGithub ? "spin" : ""} />
                  {isFetchingGithub ? "Syncing..." : "Sync GitHub Repos"}
                </button>
                <button
                  onClick={() => {
                    setGithubUsernameInput(profile.github_profile || '');
                    setGithubModalOpen(true);
                  }}
                  style={{
                    padding: '10px 16px', background: 'transparent', color: '#3b82f6', border: '1px solid #3b82f6',
                    borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s'
                  }}
                >
                  Change Account
                </button>
              </>
            ) : (
              <button
                onClick={() => {
                  setGithubUsernameInput('');
                  setGithubModalOpen(true);
                }}
                style={{
                  padding: '10px 20px', background: 'linear-gradient(135deg, #24292e 0%, #040d21 100%)', color: 'white',
                  border: '1px solid #444d56', borderRadius: '8px', fontSize: '14px', fontWeight: '600', display: 'flex',
                  alignItems: 'center', gap: '8px', cursor: 'pointer', transition: 'all 0.2s', boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                }}
              >
                <Github size={16} /> Connect to GitHub
              </button>
            )}
            <button
              onClick={() => {
                if (!canUploadProject) {
                  showToast(`Project uploads are limited to once a month. Next update unlocks on ${projNextDate} (${projDaysRemaining} days remaining).`, 'warning');
                  return;
                }
                setCustomProjectModalOpen(true);
              }}
              style={{
                padding: '10px 20px', background: canUploadProject ? '#2563eb' : '#475569', color: 'white', border: 'none',
                borderRadius: '8px', fontSize: '14px', fontWeight: '600', display: 'flex',
                alignItems: 'center', gap: '8px', cursor: canUploadProject ? 'pointer' : 'not-allowed', transition: 'all 0.2s', boxShadow: canUploadProject ? '0 4px 12px rgba(37, 99, 235, 0.2)' : 'none'
              }}
            >
              {canUploadProject ? <Plus size={16} /> : <Lock size={15} />} Add Project
            </button>
          </div>
        </div>

        {projectsList.length === 0 ? (
          <div style={{
            textAlign: 'center', padding: '60px 20px', background: 'var(--graphite-dark)',
            borderRadius: '16px', border: '1px dashed var(--border)', margin: '20px 0'
          }}>
            <Github size={56} style={{ color: 'var(--border)', marginBottom: '16px', margin: '0 auto' }} />
            <h4 style={{ fontSize: '18px', fontWeight: '700', color: 'var(--proto-white)', marginBottom: '8px' }}>
              No Projects Displayed Yet
            </h4>
            <p style={{ fontSize: '14px', color: 'var(--text-muted)', maxWidth: '420px', margin: '0 auto 24px auto' }}>
              Connect your GitHub account to instantly fetch all your repositories, or click "Add Project" to manually showcase a project.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={() => { setGithubUsernameInput(''); setGithubModalOpen(true); }}
                style={{
                  padding: '12px 24px', background: '#24292e', color: 'white', border: 'none',
                  borderRadius: '8px', fontSize: '14px', fontWeight: '600', display: 'flex',
                  alignItems: 'center', gap: '8px', cursor: 'pointer'
                }}
              >
                <Github size={18} /> Connect to GitHub
              </button>
              <button
                onClick={() => setCustomProjectModalOpen(true)}
                style={{
                  padding: '12px 24px', background: 'transparent', color: '#3b82f6', border: '1px solid #3b82f6',
                  borderRadius: '8px', fontSize: '14px', fontWeight: '600', display: 'flex',
                  alignItems: 'center', gap: '8px', cursor: 'pointer'
                }}
              >
                <Plus size={18} /> Add Manually
              </button>
            </div>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
            gap: '20px'
          }}>
            {projectsList.map((project, idx) => (
              <div key={project.id || idx}
                onClick={() => handleAnalyzeProjectClick(project)}
                style={{
                  background: 'var(--graphite-dark)',
                  border: '1px solid var(--border)',
                  borderRadius: '12px',
                  padding: '24px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  transition: 'all 0.2s',
                  position: 'relative',
                  boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-3px)';
                  e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0,0,0,0.1)';
                  e.currentTarget.style.borderColor = '#3b82f6';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0,0,0,0.05)';
                  e.currentTarget.style.borderColor = 'var(--border)';
                }}>
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {project.isGithub ? <Github size={18} color="#3b82f6" /> : <Code size={18} color="#10b981" />}
                      <h4 style={{ fontSize: '16px', fontWeight: '700', color: 'var(--proto-white)', margin: 0, wordBreak: 'break-word' }}>
                        {project.title}
                      </h4>
                    </div>
                    <button
                      onClick={() => handleDeleteProject(project.id)}
                      style={{
                        background: 'transparent', border: 'none', color: 'var(--text-faint)', cursor: 'pointer',
                        padding: '4px', borderRadius: '4px', display: 'flex', alignItems: 'center', transition: 'all 0.2s'
                      }}
                      title="Delete project"
                      onMouseEnter={(e) => { e.currentTarget.style.color = '#ef4444'; e.currentTarget.style.background = '#fee2e2'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-faint)'; e.currentTarget.style.background = 'transparent'; }}
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>

                  <p style={{ fontSize: '13px', color: 'var(--text-muted)', lineHeight: '1.5', marginBottom: '16px', minHeight: '40px' }}>
                    {project.description || 'No description provided.'}
                  </p>

                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center', marginBottom: '16px' }}>
                    {project.language && (
                      <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: '600', color: 'var(--text-muted)' }}>
                        <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: getLangColor(project.language) }}></span>
                        {project.language}
                      </span>
                    )}
                    {project.isGithub && (
                      <>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: 'var(--text-muted)' }}>
                          <Star size={13} color="#f59e0b" /> {project.stars || 0}
                        </span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: 'var(--text-muted)' }}>
                          <GitFork size={13} /> {project.forks || 0}
                        </span>
                      </>
                    )}
                    {project.techStack && !project.isGithub && (
                      <span style={{ fontSize: '12px', background: '#eff6ff', color: '#1d4ed8', padding: '2px 8px', borderRadius: '12px', fontWeight: '500' }}>
                        {project.techStack}
                      </span>
                    )}
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid var(--border)', paddingTop: '12px', marginTop: '4px' }}>
                  <span style={{ fontSize: '11px', color: 'var(--text-faint)' }}>
                    {project.updatedAt ? `Updated ${project.updatedAt}` : 'Custom Portfolio'}
                  </span>
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleAnalyzeProjectClick(project); }}
                      style={{
                        background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                        color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: '16px',
                        padding: '4px 10px', fontSize: '11px', fontWeight: '700', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: '4px', transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#dbeafe'; e.currentTarget.style.transform = 'scale(1.03)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)'; e.currentTarget.style.transform = 'scale(1)'; }}
                    >
                      <Sparkles size={12} color="#2563eb" /> AI Insights
                    </button>
                    {(project.repoUrl || project.url || project.html_url) && (
                      <a href={project.repoUrl || project.url || project.html_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} style={{
                        display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: '600',
                        color: '#3b82f6', textDecoration: 'none'
                      }}>
                        <Code size={14} /> Repo
                      </a>
                    )}
                    {project.liveUrl && (
                      <a href={project.liveUrl} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} style={{
                        display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: '600',
                        color: '#10b981', textDecoration: 'none'
                      }}>
                        <ExternalLink size={14} /> Demo
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderCertificatesTab = () => {
    const certsList = parseJsonSafe(profile?.certificates, []);

    return (
      <div className="stu-fade-in" style={{ padding: '0' }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginBottom: '24px', flexWrap: 'wrap', gap: '12px', background: '#f8fafc',
          padding: '16px 20px', borderRadius: '12px', border: '1px solid #e2e8f0'
        }}>
          <div>
            <h3 style={{ fontSize: '17px', fontWeight: '700', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px', margin: '0 0 4px 0' }}>
              <Award size={18} color="var(--primary)" /> Certifications & Achievements
            </h3>
            <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>
              Showcase your verified certificates, diplomas, and technical awards.
            </p>
          </div>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <button
              onClick={handleExtractFromResume}
              disabled={isExtractingResume}
              style={{
                padding: '8px 16px', background: 'transparent', color: 'var(--primary)', border: '1px solid var(--primary)',
                borderRadius: '8px', fontSize: '13px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px',
                cursor: isExtractingResume ? 'not-allowed' : 'pointer', transition: 'all 0.2s', whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--primary-dim)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              <Sparkles size={15} className={isExtractingResume ? "spin" : ""} />
              {isExtractingResume ? "Extracting..." : "⚡ AI Extract from Resume"}
            </button>
            <button
              onClick={() => setCertModalOpen(true)}
              style={{
                padding: '8px 16px', background: 'var(--primary)', color: '#ffffff', border: 'none',
                borderRadius: '8px', fontSize: '13px', fontWeight: '600', display: 'flex',
                alignItems: 'center', gap: '6px', cursor: 'pointer', transition: 'all 0.2s', whiteSpace: 'nowrap'
              }}
            >
              <Plus size={15} /> Add Certificate
            </button>
          </div>
        </div>

        {certsList.length === 0 ? (
          <div style={{
            textAlign: 'center', padding: '60px 20px', background: 'var(--graphite-dark)',
            borderRadius: '16px', border: '1px dashed var(--border)', margin: '20px 0'
          }}>
            <Award size={56} style={{ color: 'var(--border)', marginBottom: '16px', margin: '0 auto' }} />
            <h4 style={{ fontSize: '18px', fontWeight: '700', color: 'var(--proto-white)', marginBottom: '8px' }}>
              No Certificates Added Yet
            </h4>
            <p style={{ fontSize: '14px', color: 'var(--text-muted)', maxWidth: '420px', margin: '0 auto 24px auto' }}>
              Add online courses (Coursera, NPTEL, Udemy), academic achievements, or professional certifications to highlight your credentials.
            </p>
            <button
              onClick={() => setCertModalOpen(true)}
              style={{
                padding: '12px 24px', background: 'var(--primary)', color: 'var(--bg-page)', border: 'none',
                borderRadius: '8px', fontSize: '14px', fontWeight: '700', display: 'flex',
                alignItems: 'center', gap: '8px', cursor: 'pointer', margin: '0 auto'
              }}
            >
              <Plus size={18} /> Add Your First Certificate
            </button>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
            gap: '20px'
          }}>
            {certsList.map((cert, idx) => (
              <div key={cert.id || idx} style={{
                background: 'var(--graphite-dark)',
                border: '1px solid var(--border)',
                borderRadius: '12px',
                padding: '24px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                transition: 'all 0.2s',
                position: 'relative',
                boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)'
              }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-3px)';
                  e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0,0,0,0.1)';
                  e.currentTarget.style.borderColor = 'var(--primary)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0,0,0,0.05)';
                  e.currentTarget.style.borderColor = 'var(--border)';
                }}>
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{
                        width: '44px', height: '44px', borderRadius: '10px',
                        background: 'var(--primary-dim)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--primary)',
                        flexShrink: 0
                      }}>
                        <Award size={22} />
                      </div>
                      <div>
                        <h4 style={{ fontSize: '16px', fontWeight: '700', color: 'var(--proto-white)', margin: '0 0 4px 0', lineHeight: '1.3' }}>
                          {cert.title}
                        </h4>
                        <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--primary)' }}>
                          {cert.issuer}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => handleDeleteCertificate(cert.id)}
                      style={{
                        background: 'transparent', border: 'none', color: 'var(--text-faint)', cursor: 'pointer',
                        padding: '4px', borderRadius: '4px', display: 'flex', alignItems: 'center', transition: 'all 0.2s'
                      }}
                      title="Delete certificate"
                      onMouseEnter={(e) => { e.currentTarget.style.color = '#ef4444'; e.currentTarget.style.background = '#fee2e2'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-faint)'; e.currentTarget.style.background = 'transparent'; }}
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>

                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
                    <span style={{ fontSize: '12px', background: 'var(--border)', color: 'var(--text-muted)', padding: '2px 10px', borderRadius: '12px', fontWeight: '500' }}>
                      Issued: {cert.issueDate || 'N/A'}
                    </span>
                    {cert.category && (
                      <span style={{ fontSize: '12px', background: 'var(--primary-dim)', color: 'var(--primary)', padding: '2px 10px', borderRadius: '12px', fontWeight: '600' }}>
                        {cert.category}
                      </span>
                    )}
                    {cert.credentialId && (
                      <span style={{ fontSize: '11px', color: 'var(--text-faint)', display: 'flex', alignItems: 'center' }}>
                        ID: {cert.credentialId}
                      </span>
                    )}
                  </div>
                </div>

                <div style={{ borderTop: '1px solid var(--border)', paddingTop: '12px', display: 'flex', justifyContent: 'flex-end' }}>
                  {cert.credentialUrl ? (
                    <a href={cert.credentialUrl} target="_blank" rel="noreferrer" style={{
                      display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: '600',
                      color: 'var(--primary)', textDecoration: 'none'
                    }}>
                      <ExternalLink size={15} /> Verify Certificate
                    </a>
                  ) : (
                    <span style={{ fontSize: '12px', color: 'var(--text-faint)', fontStyle: 'italic' }}>No URL provided</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderPortfolioModals = () => (
    <>
      {/* CONNECT GITHUB MODAL */}
      {githubModalOpen && (
        <div className="stu-modal-overlay" style={{ zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="stu-modal" style={{ maxWidth: '440px', width: '90%', padding: '28px', borderRadius: '16px', background: 'var(--graphite-dark)', border: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '700', color: 'var(--proto-white)', display: 'flex', alignItems: 'center', gap: '8px', margin: 0 }}>
                <Github size={20} /> {profile?.github_profile ? 'GitHub Account' : 'Connect GitHub Account'}
              </h3>
              <button onClick={() => setGithubModalOpen(false)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}>
                <X size={20} />
              </button>
            </div>

            {githubOAuthError && (
              <div style={{
                marginBottom: '16px', padding: '12px 14px', borderRadius: '10px',
                background: 'rgba(239, 68, 68, 0.08)', border: '1px solid rgba(239, 68, 68, 0.45)',
                color: '#fca5a5', fontSize: '13px', lineHeight: 1.6, display: 'flex',
                alignItems: 'flex-start', gap: '10px'
              }}>
                <AlertTriangle size={17} style={{ flexShrink: 0, marginTop: '2px' }} />
                <div>
                  {githubOAuthError}
                  <div style={{ marginTop: '8px' }}>
                    <button
                      onClick={() => setGithubOAuthError(null)}
                      style={{ background: 'transparent', border: 'none', color: '#fca5a5', textDecoration: 'underline', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {profile?.github_profile ? (
                <>
                  <p style={{ fontSize: '14px', color: 'var(--proto-white)', marginBottom: '10px', textAlign: 'center' }}>
                    You are currently connected as <strong>@{profile.github_profile}</strong>.
                  </p>
                  <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '20px', lineHeight: '1.5', textAlign: 'center' }}>
                    Changing your account will re-sync your public and private repositories directly to your profile. Disconnecting will remove your imported repositories.
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', margin: '30px 0' }}>
                    <button 
                      type="button"
                      onClick={handleConnectGithub}
                      disabled={isFetchingGithub}
                      style={{
                        padding: '14px 24px', background: '#24292e', color: 'white', border: 'none', borderRadius: '8px',
                        fontWeight: '600', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer',
                        width: '100%', justifyContent: 'center', opacity: isFetchingGithub ? 0.7 : 1
                      }}
                    >
                      {isFetchingGithub ? <RefreshCw size={20} className="spin" /> : <Github size={20} />}
                      {isFetchingGithub ? "Syncing..." : "Change GitHub Account"}
                    </button>
                    <button 
                      type="button"
                      onClick={handleDisconnectGithub}
                      disabled={isFetchingGithub}
                      style={{
                        padding: '14px 24px', background: 'transparent', color: '#ef4444', border: '1px solid #ef4444', borderRadius: '8px',
                        fontWeight: '600', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer',
                        width: '100%', justifyContent: 'center', opacity: isFetchingGithub ? 0.7 : 1
                      }}
                    >
                      Disconnect Account
                    </button>
                  </div>
                </>
            ) : (
                <>
                  <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '20px', lineHeight: '1.5', textAlign: 'center' }}>
                    Securely connect your GitHub account via OAuth to automatically sync your public and private repositories directly to your profile.
                  </p>
                  <div style={{ display: 'flex', justifyContent: 'center', margin: '30px 0' }}>
                    <button 
                      type="button"
                      onClick={handleConnectGithub}
                      disabled={isFetchingGithub}
                      style={{
                        padding: '14px 24px', background: '#24292e', color: 'white', border: 'none', borderRadius: '8px',
                        fontWeight: '600', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer',
                        width: '100%', justifyContent: 'center', opacity: isFetchingGithub ? 0.7 : 1
                      }}
                    >
                      {isFetchingGithub ? <RefreshCw size={20} className="spin" /> : <Github size={20} />}
                      {isFetchingGithub ? "Syncing..." : "Continue with GitHub"}
                    </button>
                  </div>
                </>
            )}

            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <button
                type="button"
                onClick={() => setGithubModalOpen(false)}
                style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', fontWeight: '600', cursor: 'pointer', fontSize: '14px' }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ADD CUSTOM PROJECT MODAL */}
      {customProjectModalOpen && (
        <div className="stu-modal-overlay" style={{ zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="stu-modal" style={{ maxWidth: '500px', width: '90%', padding: '28px', borderRadius: '16px', background: 'var(--graphite-dark)', border: '1px solid var(--border)', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '700', color: 'var(--proto-white)', display: 'flex', alignItems: 'center', gap: '8px', margin: 0 }}>
                <Plus size={20} color="#3b82f6" /> Add Portfolio Project
              </h3>
              <button onClick={() => setCustomProjectModalOpen(false)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}>
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleAddCustomProject}>
              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '6px' }}>Project Title *</label>
                <input
                  required
                  type="text"
                  value={customProjectForm.title}
                  onChange={(e) => setCustomProjectForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g. AI Student Portal"
                  className="stu-input"
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--border)' }}
                />
              </div>
              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '6px' }}>Description</label>
                <textarea
                  rows="3"
                  value={customProjectForm.description}
                  onChange={(e) => setCustomProjectForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Brief explanation of what this project does and your role..."
                  className="stu-input"
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--border)', fontFamily: 'inherit' }}
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div className="form-group">
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '6px' }}>Primary Language</label>
                  <select
                    value={customProjectForm.language}
                    onChange={(e) => setCustomProjectForm(prev => ({ ...prev, language: e.target.value }))}
                    className="stu-input"
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--border)' }}
                  >
                    <option value="JavaScript">JavaScript</option>
                    <option value="TypeScript">TypeScript</option>
                    <option value="Python">Python</option>
                    <option value="Java">Java</option>
                    <option value="C++">C++</option>
                    <option value="HTML">HTML/CSS</option>
                    <option value="Go">Go</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '6px' }}>Tech Stack / Tags</label>
                  <input
                    type="text"
                    value={customProjectForm.techStack}
                    onChange={(e) => setCustomProjectForm(prev => ({ ...prev, techStack: e.target.value }))}
                    placeholder="React, Node.js, AI"
                    className="stu-input"
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--border)' }}
                  />
                </div>
              </div>
              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '6px' }}>Repository URL (GitHub/GitLab)</label>
                <input
                  type="url"
                  value={customProjectForm.repoUrl}
                  onChange={(e) => setCustomProjectForm(prev => ({ ...prev, repoUrl: e.target.value }))}
                  placeholder="https://github.com/..."
                  className="stu-input"
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--border)' }}
                />
              </div>
              <div className="form-group" style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '6px' }}>Live Demo URL</label>
                <input
                  type="url"
                  value={customProjectForm.liveUrl}
                  onChange={(e) => setCustomProjectForm(prev => ({ ...prev, liveUrl: e.target.value }))}
                  placeholder="https://myproject-demo.com"
                  className="stu-input"
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--border)' }}
                />
              </div>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setCustomProjectModalOpen(false)}
                  style={{ padding: '10px 18px', background: 'transparent', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--text-muted)', fontWeight: '600', cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{ padding: '10px 20px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer' }}
                >
                  Save Project
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* PROJECT ADDED SUCCESS MODAL */}
      {showProjectSuccessModal && (
        <div 
          className="stu-modal-overlay" 
          onClick={() => setShowProjectSuccessModal(false)}
          style={{ 
            zIndex: 1200, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            padding: '20px', 
            background: 'rgba(0, 0, 0, 0.7)', 
            backdropFilter: 'blur(6px)' 
          }}
        >
          <div 
            className="stu-modal" 
            onClick={(e) => e.stopPropagation()} 
            style={{ 
              maxWidth: '460px', 
              width: '90%', 
              padding: '36px 32px 28px 32px', 
              borderRadius: '20px', 
              background: '#12161f', 
              border: '1px solid rgba(255, 255, 255, 0.1)', 
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.75)',
              textAlign: 'center',
              color: '#ffffff',
              position: 'relative'
            }}
          >
            {/* Circular Badge Icon */}
            <div 
              style={{ 
                width: '68px', 
                height: '68px', 
                borderRadius: '50%', 
                background: 'radial-gradient(circle at 50% 35%, #ffffff 0%, #e0f2fe 65%, #bae6fd 100%)', 
                boxShadow: '0 8px 20px rgba(56, 189, 248, 0.25)', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                margin: '0 auto 20px auto' 
              }}
            >
              <GraduationCap size={34} color="#1d4ed8" strokeWidth={2.2} />
            </div>

            <h2 style={{ fontSize: '24px', fontWeight: '800', color: '#ffffff', margin: '0 0 8px 0', letterSpacing: '-0.02em' }}>
              Project Added Successfully!
            </h2>

            <p style={{ fontSize: '14px', color: '#94a3b8', margin: '0 0 24px 0', lineHeight: 1.4 }}>
              Your new project has been added to your profile.
            </p>

            <div 
              style={{ 
                background: 'rgba(255, 255, 255, 0.035)', 
                border: '1px solid rgba(255, 255, 255, 0.08)', 
                borderRadius: '14px', 
                padding: '18px 20px', 
                textAlign: 'left', 
                marginBottom: '24px' 
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <Info size={17} color="#3b82f6" />
                <span style={{ fontWeight: '700', fontSize: '14px', color: '#ffffff' }}>Knowledge Score Update</span>
              </div>
              <p style={{ fontSize: '13px', color: '#94a3b8', lineHeight: '1.55', margin: '0 0 14px 0' }}>
                Adding a new project will not automatically update your Dashboard Knowledge Score. Your score will be updated after you complete your next eligible quarterly assessment.
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#94a3b8' }}>
                <Calendar size={15} color="#94a3b8" />
                <span>Assessments are available once every quarter.</span>
              </div>
            </div>

            <button 
              onClick={() => setShowProjectSuccessModal(false)}
              style={{ 
                width: '100%', 
                padding: '13px 20px', 
                background: '#2563eb', 
                color: '#ffffff', 
                border: 'none', 
                borderRadius: '10px', 
                fontSize: '15px', 
                fontWeight: '700', 
                cursor: 'pointer', 
                boxShadow: '0 4px 14px rgba(37, 99, 235, 0.4)', 
                transition: 'background 0.2s' 
              }}
              onMouseOver={(e) => e.currentTarget.style.background = '#1d4ed8'}
              onMouseOut={(e) => e.currentTarget.style.background = '#2563eb'}
            >
              Got it
            </button>

            <p style={{ fontSize: '12px', color: '#64748b', margin: '14px 0 0 0', lineHeight: 1.4 }}>
              Your project is saved. You don't need to retake an assessment immediately.
            </p>
          </div>
        </div>
      )}

      {/* ADD CERTIFICATE MODAL */}
      {certModalOpen && (
        <div className="stu-modal-overlay" style={{ zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="stu-modal" style={{ maxWidth: '480px', width: '90%', padding: '28px', borderRadius: '16px', background: 'var(--graphite-dark)', border: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '700', color: 'var(--proto-white)', display: 'flex', alignItems: 'center', gap: '8px', margin: 0 }}>
                <Award size={20} color="#10b981" /> Add Certificate or Award
              </h3>
              <button onClick={() => setCertModalOpen(false)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}>
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleAddCertificate}>
              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '6px' }}>Certificate Title *</label>
                <input
                  required
                  type="text"
                  value={certForm.title}
                  onChange={(e) => setCertForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g. AWS Certified Solutions Architect"
                  className="stu-input"
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--border)' }}
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div className="form-group">
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '6px' }}>Issuing Organization *</label>
                  <input
                    required
                    type="text"
                    value={certForm.issuer}
                    onChange={(e) => setCertForm(prev => ({ ...prev, issuer: e.target.value }))}
                    placeholder="Coursera, AWS, NPTEL"
                    className="stu-input"
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--border)' }}
                  />
                </div>
                <div className="form-group">
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '6px' }}>Issue Date</label>
                  <input
                    type="text"
                    value={certForm.issueDate}
                    onChange={(e) => setCertForm(prev => ({ ...prev, issueDate: e.target.value }))}
                    placeholder="e.g. May 2026"
                    className="stu-input"
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--border)' }}
                  />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div className="form-group">
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '6px' }}>Credential ID</label>
                  <input
                    type="text"
                    value={certForm.credentialId}
                    onChange={(e) => setCertForm(prev => ({ ...prev, credentialId: e.target.value }))}
                    placeholder="e.g. UC-12345678"
                    className="stu-input"
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--border)' }}
                  />
                </div>
                <div className="form-group">
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '6px' }}>Category</label>
                  <select
                    value={certForm.category}
                    onChange={(e) => setCertForm(prev => ({ ...prev, category: e.target.value }))}
                    className="stu-input"
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--border)' }}
                  >
                    <option value="Technical">Technical</option>
                    <option value="Academic">Academic</option>
                    <option value="Leadership">Leadership</option>
                    <option value="Hackathon / Competition">Hackathon / Competition</option>
                  </select>
                </div>
              </div>
              <div className="form-group" style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '6px' }}>Credential / Verification URL</label>
                <input
                  type="url"
                  value={certForm.credentialUrl}
                  onChange={(e) => setCertForm(prev => ({ ...prev, credentialUrl: e.target.value }))}
                  placeholder="https://coursera.org/verify/..."
                  className="stu-input"
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--border)' }}
                />
              </div>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setCertModalOpen(false)}
                  style={{ padding: '10px 18px', background: 'transparent', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--text-muted)', fontWeight: '600', cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{ padding: '10px 20px', background: '#10b981', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer' }}
                >
                  Add Certificate
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* PROJECT AI ANALYSIS MODAL */}
      {projectAiModalOpen && selectedProjectAi && (
        <div className="stu-modal-overlay" style={{ zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div className="stu-modal" style={{ maxWidth: '620px', width: '100%', maxHeight: '85vh', overflowY: 'auto', padding: '30px', borderRadius: '18px', background: 'var(--graphite-dark)', border: '1px solid #3b82f6', boxShadow: '0 20px 40px rgba(0,0,0,0.5)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', borderBottom: '1px solid var(--border)', paddingBottom: '16px' }}>
              <div>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)', color: '#1d4ed8', padding: '4px 10px', borderRadius: '12px', marginBottom: '8px' }}>
                  <Sparkles size={13} color="#2563eb" /> AI Portfolio Inspector
                </span>
                <h3 style={{ fontSize: '20px', fontWeight: '700', color: 'var(--proto-white)', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {selectedProjectAi.title}
                </h3>
              </div>
              <button onClick={() => setProjectAiModalOpen(false)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: '4px' }}>
                <X size={22} />
              </button>
            </div>

            {selectedProjectAi.loading ? (
              <div style={{ textAlign: 'center', padding: '50px 20px' }}>
                <div className="stu-spinner" style={{ margin: '0 auto 16px auto', borderColor: '#3b82f6', borderRightColor: 'transparent' }}></div>
                <h4 style={{ fontSize: '16px', fontWeight: '600', color: 'var(--proto-white)', marginBottom: '8px' }}>
                  Running Senior Engineer Code Analysis...
                </h4>
                <p style={{ fontSize: '13px', color: 'var(--text-muted)', maxWidth: '360px', margin: '0 auto' }}>
                  AI is examining the repository structure, tech stack, and description to extract deep technical insights.
                </p>
              </div>
            ) : selectedProjectAi.error ? (
              <div style={{ textAlign: 'center', padding: '40px 20px', color: '#ef4444' }}>
                <XCircle size={44} style={{ margin: '0 auto 12px auto' }} />
                <h4 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>Analysis Temporarily Unavailable</h4>
                <p style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Could not connect to AI server or repository details are inaccessible.</p>
              </div>
            ) : selectedProjectAi.analysis ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '22px' }}>
                {/* Badges */}
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  {selectedProjectAi.analysis.category && (
                    <span style={{ background: '#1e3a8a', color: '#ffffff', border: '1px solid #3b82f6', padding: '4px 12px', borderRadius: '14px', fontSize: '12px', fontWeight: '600' }}>
                      📁 {selectedProjectAi.analysis.category}
                    </span>
                  )}
                  {selectedProjectAi.analysis.complexity && (
                    <span style={{ background: '#065f46', color: '#ffffff', border: '1px solid #10b981', padding: '4px 12px', borderRadius: '14px', fontSize: '12px', fontWeight: '600' }}>
                      ⚡ Complexity: {selectedProjectAi.analysis.complexity}
                    </span>
                  )}
                </div>

                {/* Summary */}
                <div style={{ background: 'var(--graphite)', padding: '16px', borderRadius: '12px', border: '1px solid var(--border)' }}>
                  <h5 style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', margin: '0 0 8px 0', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Info size={14} /> Executive Summary
                  </h5>
                  <p style={{ fontSize: '14px', color: 'var(--proto-white)', margin: 0, lineHeight: '1.6' }}>
                    {selectedProjectAi.analysis.summary}
                  </p>
                </div>

                {/* Tech & Skills */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px' }}>
                  <div>
                    <h5 style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', margin: '0 0 10px 0', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Code size={14} color="#3b82f6" /> Tech Stack Highlights
                    </h5>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {(selectedProjectAi.analysis.tech_highlights || []).map((tech, i) => (
                        <span key={i} style={{ background: '#1e293b', color: '#ffffff', padding: '4px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', border: '1px solid #475569' }}>
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h5 style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', margin: '0 0 10px 0', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Target size={14} color="#10b981" /> Skills Demonstrated
                    </h5>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {(selectedProjectAi.analysis.skills_demonstrated || []).map((skill, i) => (
                        <span key={i} style={{ background: '#065f46', color: '#ffffff', padding: '4px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', border: '1px solid #10b981' }}>
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Strengths & Suggestions */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px', borderTop: '1px solid var(--border)', paddingTop: '16px' }}>
                  <div>
                    <h5 style={{ fontSize: '13px', fontWeight: '700', color: '#34d399', margin: '0 0 10px 0', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <CheckCircle size={15} /> Key Strengths
                    </h5>
                    <ul style={{ margin: 0, paddingLeft: '18px', color: 'var(--proto-white)', fontSize: '13px', lineHeight: '1.6' }}>
                      {(selectedProjectAi.analysis.strengths || []).map((str, i) => (
                        <li key={i} style={{ marginBottom: '6px' }}>{str}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h5 style={{ fontSize: '13px', fontWeight: '700', color: '#fbbf24', margin: '0 0 10px 0', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Compass size={15} /> Growth & Next Steps
                    </h5>
                    <ul style={{ margin: 0, paddingLeft: '18px', color: 'var(--proto-white)', fontSize: '13px', lineHeight: '1.6' }}>
                      {(selectedProjectAi.analysis.suggestions || []).map((sug, i) => (
                        <li key={i} style={{ marginBottom: '6px' }}>{sug}</li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
                  <button onClick={() => setProjectAiModalOpen(false)} style={{ padding: '10px 24px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s' }}>
                    Done
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </>
  );

  // --- TAB: SETTINGS ---
  const renderMySettings = () => {
    if (!profile) return <div className="stu-loading-spinner">Loading Profile...</div>;

    // Check if student needs to update interests (bulk-uploaded students)
    const needsInterestsUpdate = (!profile.skills_list || profile.skills_list.length === 0) ||
      (!profile.preferred_domains || profile.preferred_domains.length === 0);

    return (
      <div className="stu-fade-in">
        <header className="stu-top-bar">
          <h1>Profile</h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <span style={{ fontSize: '0.875rem', color: 'var(--text-muted)', fontWeight: 500 }}>
              Manage your profile and account preferences
            </span>
          </div>
        </header>

        <div className="stu-padded" style={{ background: '#f0f4f8', minHeight: '100vh', padding: '0' }}>
          <div className="stu-profile-layout" style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: '0', alignItems: 'start', width: '100%', background: '#ffffff', borderRadius: '0', boxShadow: 'none', border: 'none', borderTop: '1px solid #e8ecf0', minHeight: 'calc(100vh - 64px)' }}>
            {/* --- LEFT SIDEBAR COLUMN --- */}
            <div className="hide-scrollbar stu-profile-sidebar" style={{ position: 'sticky', top: '70px', height: 'calc(100vh - 70px)', overflowY: 'auto', display: 'flex', flexDirection: 'column', background: '#f5f8fc', borderRight: '1px solid #e8ecf0', padding: '28px 20px', boxSizing: 'border-box' }}>
              {/* Profile Card */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', marginBottom: '24px' }}>
                <div style={{ position: 'relative', marginBottom: '14px' }}>
                  <div style={{
                    width: '90px', height: '90px', borderRadius: '50%',
                    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                    color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '36px', fontWeight: '700',
                    border: '3px solid #ffffff',
                    boxShadow: '0 4px 16px rgba(37, 99, 235, 0.3)'
                  }}>
                    {profile.full_name ? profile.full_name.charAt(0).toUpperCase() : 'S'}
                  </div>
                </div>
                <h3 style={{ fontSize: '17px', fontWeight: '700', color: '#1e293b', margin: '0 0 4px 0' }}>{profile.full_name}</h3>
                <div style={{ fontSize: '13px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <span style={{ fontSize: '14px' }}>🎓</span>
                  <span>{profile.branch || 'CSE'} • Semester : {profile.admission_semester || '8'}</span>
                </div>
              </div>

              {/* Resume Section */}
              <div style={{ paddingTop: '20px', borderTop: '1px solid #e2e8f0', marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '10px', fontWeight: '700', color: '#94a3b8', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  RESUME {profile?.resume_text && <span style={{ color: '#16a34a', textTransform: 'none', fontWeight: '600' }}>(✅ Uploaded)</span>}
                </label>
                <input
                  type="file"
                  ref={resumeFileInputRef}
                  accept="application/pdf"
                  style={{ display: 'none' }}
                  onChange={handleResumeFileUpload}
                />
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button onClick={() => {
                    if (!profile?.resume_text) {
                      showToast('No resume uploaded yet. Click Replace/Upload to add one!', 'warning');
                    } else {
                      setResumeViewerModalOpen(true);
                    }
                  }} style={{
                    flex: 1, padding: '8px 10px', background: '#ffffff', border: '1.5px solid #cbd5e1',
                    borderRadius: '8px', fontSize: '12px', fontWeight: '600', color: '#374151',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', cursor: 'pointer',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
                  }}>
                    <Eye size={13} /> View Resume
                  </button>
                  <button onClick={() => resumeFileInputRef?.current?.click()} disabled={isUploadingResume} style={{
                    flex: 1, padding: '8px 10px', background: isUploadingResume ? '#f1f5f9' : '#ffffff', border: '1.5px solid #cbd5e1',
                    borderRadius: '8px', fontSize: '12px', fontWeight: '600', color: '#374151',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', cursor: 'pointer',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
                  }}>
                    <Upload size={13} /> {isUploadingResume ? 'Uploading...' : (profile?.resume_text ? 'Replace' : 'Upload')}
                  </button>
                </div>
              </div>

              {/* GitHub Profile Section */}
              <div style={{ paddingTop: '20px', borderTop: '1px solid #e2e8f0' }}>
                <label style={{ display: 'block', fontSize: '10px', fontWeight: '700', color: '#94a3b8', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  GITHUB PROFILE
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: '#ffffff', padding: '12px', borderRadius: '10px', border: '1.5px solid #e2e8f0', marginBottom: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                  <Github size={20} color="#1e293b" />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '14px', fontWeight: '700', color: '#1e293b' }}>{profile.github_profile ? (profile.full_name ? profile.full_name.split(' ')[0] : 'Student') : 'Not Connected'}</div>
                    <div style={{ fontSize: '12px', color: '#64748b' }}>@{profile.github_profile || 'username'}</div>
                  </div>
                  {profile.github_profile && (
                    <div style={{ position: 'relative' }}>
                      <div style={{ width: '28px', height: '28px', borderRadius: '6px', background: '#dcfce7', border: '1.5px solid #86efac', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span style={{ fontSize: '14px' }}>✅</span>
                      </div>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => {
                    setGithubUsernameInput(profile.github_profile || '');
                    setGithubModalOpen(true);
                  }}
                  style={{
                    width: '100%', padding: '9px 12px', background: '#ffffff', border: '1.5px solid #cbd5e1',
                    borderRadius: '8px', fontSize: '12px', fontWeight: '600', color: '#374151',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', cursor: 'pointer',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
                  }}
                >
                  <FileText size={14} /> {profile.github_profile ? 'View details' : '+ Connect GitHub'}
                </button>
              </div>
            </div>

            {/* --- RIGHT CONTENT AREA COLUMN --- */}
            <div className="stu-profile-content" style={{ background: '#ffffff', minHeight: '700px', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              {/* TOP TAB BAR */}
              <div className="hide-scrollbar" style={{
                display: 'flex', gap: '0', padding: '0 20px',
                borderBottom: '1.5px solid #e8ecf0',
                background: '#ffffff', alignItems: 'center',
                width: '100%', overflowX: 'auto', boxSizing: 'border-box'
              }}>
                {[
                  { id: 'general', label: 'Personal Info', icon: User },
                  { id: 'courses_marks', label: 'Courses & Marks', icon: BookOpen, count: coursesAndMarks.length },
                  { id: 'toolset', label: 'Tools', icon: Wrench },
                  { id: 'skillset', label: 'Skills', icon: Link2 },
                  { id: 'projects', label: 'Projects', icon: Folder, count: parseJsonSafe(profile?.github_projects, []).length },
                  { id: 'assessment', label: 'Assessment', icon: ClipboardCheck },
                  { id: 'experience', label: 'Experience', icon: Briefcase },
                  { id: 'certificates', label: 'Awards', icon: Trophy, count: parseJsonSafe(profile?.certificates, []).length },
                  { id: 'marks_cards', label: 'Marks Card', icon: FileText },
                  { id: 'timeline', label: 'Timeline', icon: Clock }
                ].filter(tab => (tab.id !== 'courses_marks' && tab.id !== 'marks_cards') || isSubEnabled('lms', 'lms_marks_view')).map(tab => {
                  const IconComp = tab.icon;
                  const isActive = settingsSubTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setSettingsSubTab(tab.id)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '6px',
                        padding: '14px 14px',
                        borderBottom: isActive ? '2.5px solid var(--primary)' : '2.5px solid transparent',
                        background: 'transparent',
                        color: isActive ? 'var(--primary)' : 'var(--text-faint)',
                        fontWeight: isActive ? '700' : '500', fontSize: '13px', cursor: 'pointer',
                        transition: 'color 0.15s', borderTop: 'none', borderLeft: 'none', borderRight: 'none',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      <IconComp size={15} /> {tab.label}
                      {tab.count !== undefined && tab.count > 0 && (
                        <span style={{
                          background: isActive ? 'var(--primary-dim)' : 'var(--bg-soft)',
                          color: isActive ? 'var(--primary)' : 'var(--text-faint)',
                          padding: '1px 7px', borderRadius: '10px', fontSize: '10px', fontWeight: '700'
                        }}>
                          {tab.count}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* TAB CONTENT RECEPTACLE */}
              <div className="stu-profile-tab-content" style={{ padding: '28px 32px', flex: 1 }}>
                {settingsSubTab === 'courses_marks' && renderCoursesAndMarksSection()}
                {settingsSubTab === 'toolset' && renderToolsetTab()}
                {settingsSubTab === 'skillset' && renderSkillsetTab()}
                {settingsSubTab === 'experience' && renderExperienceTab()}
                {settingsSubTab === 'assessment' && renderAssessmentTab()}
                {settingsSubTab === 'marks_cards' && renderMarksCardTab()}
                {settingsSubTab === 'timeline' && renderTimelineTab()}

                {/* General profile form wraps when tab is general */}
                <div style={{ display: settingsSubTab === 'general' ? 'block' : 'none' }}>
                  {/* Read-Only Notice Banner */}
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: '10px',
                    padding: '12px 16px', background: '#eff6ff', border: '1px solid #bfdbfe',
                    borderRadius: '10px', marginBottom: '24px', color: '#1e40af', fontSize: '13px', fontWeight: '500'
                  }}>
                    <Info size={18} color="#2563eb" style={{ flexShrink: 0 }} />
                    <span>Profile information is managed by your institution and is read-only. Please contact your university administrator to request updates.</span>
                  </div>

                  {/* Personal Information Form */}
                  <section style={{ marginBottom: '32px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '12px', borderBottom: '1px solid #f1f5f9' }}>
                      <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '8px', margin: 0 }}>
                        <User size={20} color="#2563eb" />
                        Personal Information
                      </h3>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                      <div className="form-group">
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>Full Name</label>
                        <input
                          name="full_name"
                          value={settingsForm.full_name}
                          className="stu-input"
                          placeholder="Your Full Name"
                          readOnly
                        />
                      </div>
                      <div className="form-group">
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>Phone Number</label>
                        <div className="phone-input-container">
                          <PhoneInput
                            country={'in'}
                            value={normalizePhoneForDisplay(settingsForm.phone_number)}
                            disabled={true}
                            enableSearch={true}
                            disableSearchIcon={true}
                            placeholder="Your Phone Number"
                            inputProps={{
                              name: 'phone_number',
                              required: true,
                              readOnly: true
                            }}
                            containerClass="react-tel-input"
                            inputClass="stu-input"
                            style={{ width: '100%' }}
                            buttonStyle={{ borderRadius: '8px 0 0 8px', border: '1px solid #cbd5e1', borderRight: 'none', background: '#f8fafc' }}
                            inputStyle={{ width: '100%', padding: '10px 10px 10px 48px', borderRadius: '8px', border: '1px solid #cbd5e1', height: '42px' }}
                          />
                        </div>
                      </div>
                      <div className="form-group">
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>Date of Birth</label>
                        <input
                          type="date"
                          name="student_date_of_birth"
                          value={settingsForm.student_date_of_birth}
                          className="stu-input"
                          readOnly
                        />
                      </div>
                      <div className="form-group">
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>Gender</label>
                        <select
                          name="gender"
                          value={settingsForm.gender}
                          className="stu-input"
                          disabled
                        >
                          <option value="">Select Gender</option>
                          <option value="Male">Male</option>
                          <option value="Female">Female</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                    </div>
                  </section>

                  {/* Academic Information Form */}
                  <section style={{ marginBottom: '32px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '12px', borderBottom: '1px solid #f1f5f9' }}>
                      <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '8px', margin: 0 }}>
                        <Briefcase size={20} color="#2563eb" />
                        Academic Information
                      </h3>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                      <div className="form-group">
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>Institution Name</label>
                        <input
                          name="institution_name"
                          value={settingsForm.institution_name}
                          className="stu-input"
                          placeholder="University/College"
                          readOnly
                        />
                      </div>

                      <div className="form-group">
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>USN (University Seat Number)</label>
                        <input
                          name="usn"
                          value={settingsForm.usn}
                          className="stu-input"
                          placeholder="Unique ID"
                          readOnly
                        />
                      </div>

                      <div className="form-group">
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>Branch</label>
                        <input
                          name="branch"
                          value={settingsForm.branch}
                          className="stu-input"
                          placeholder="e.g. CSE, ECE"
                          readOnly
                        />
                      </div>

                      <div className="form-group">
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>Academic Year</label>
                        <input
                          name="academic_year"
                          value={settingsForm.academic_year}
                          className="stu-input"
                          placeholder="e.g. 2nd Year"
                          readOnly
                        />
                      </div>

                      <div className="form-group">
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>Academic Batch</label>
                        <input
                          name="academic_batch"
                          value={settingsForm.academic_batch}
                          className="stu-input"
                          placeholder="e.g. 2023-2027"
                          readOnly
                        />
                      </div>

                      <div className="form-group">
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>Graduation Year</label>
                        <input
                          name="graduation_year"
                          value={settingsForm.graduation_year}
                          className="stu-input"
                          placeholder="e.g. 2027"
                          readOnly
                        />
                      </div>

                      <div className="form-group">
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>Admission Semester</label>
                        <input
                          name="admission_semester"
                          value={settingsForm.admission_semester}
                          className="stu-input"
                          placeholder="e.g. Fall 2023"
                          readOnly
                        />
                      </div>

                      <div className="form-group">
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>Graduation Level</label>
                        <select
                          name="graduation_level"
                          value={settingsForm.graduation_level}
                          className="stu-input"
                          disabled
                        >
                          <option value="">Select Graduation Level</option>
                          <option value="UG">Undergraduate (UG)</option>
                          <option value="PG">Postgraduate (PG)</option>
                          <option value="PhD">PhD</option>
                          <option value="Diploma">Diploma</option>
                        </select>
                      </div>

                      <div className="form-group">
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>Program</label>
                        <input
                          type="text"
                          name="program"
                          value={settingsForm.program || ""}
                          className="stu-input"
                          placeholder="e.g. B.Tech"
                          readOnly
                        />
                      </div>
                    </div>
                  </section>

                  {/* Security - Change Password */}
                  <div style={{ marginBottom: '32px', borderTop: '1px solid #e2e8f0', paddingTop: '24px' }}>
                    <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Lock size={18} color="#ef4444" /> Security
                    </h3>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f8fafc', padding: '16px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#64748b', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Password</label>
                        <div style={{ fontSize: '15px', fontWeight: '600', color: '#0f172a' }}>
                          ••••••••••••
                        </div>
                      </div>
                      <button
                        onClick={() => setShowChangePasswordModal(true)}
                        style={{
                          padding: '8px 16px',
                          borderRadius: '8px',
                          border: '1px solid #cbd5e1',
                          background: '#ffffff',
                          color: '#2563eb',
                          fontWeight: '600',
                          fontSize: '13px',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = '#eff6ff';
                          e.target.style.borderColor = '#2563eb';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = '#ffffff';
                          e.target.style.borderColor = '#cbd5e1';
                        }}
                      >
                        <Lock size={14} />
                        Change Password
                      </button>
                    </div>
                  </div>
                </div>

                {/* --- PROJECTS SUB-TAB --- */}
                {settingsSubTab === 'projects' && renderProjectsTab()}

                {/* --- CERTIFICATES SUB-TAB --- */}
                {settingsSubTab === 'certificates' && renderCertificatesTab()}
              </div>
            </div>
          </div>

          {/* --- MODALS FOR GITHUB, PROJECTS, CERTIFICATES --- */}
          {renderPortfolioModals()}
        </div>
      </div>
    );
  };

  const renderInternalJobDetailView = () => {
    if (!selectedInternalJobModal) return null;
    const j = selectedInternalJobModal;

    const parseSkillsList = (val) => {
      if (!val) return [];
      if (Array.isArray(val)) {
        return val.map(s => typeof s === 'object' && s !== null ? (s.skill || s.name || String(s)) : String(s)).map(s => s.trim()).filter(Boolean);
      }
      if (typeof val === 'string') {
        try {
          const p = JSON.parse(val);
          if (Array.isArray(p)) return parseSkillsList(p);
        } catch (e) {}
        return val.split(',').map(s => s.trim()).filter(Boolean);
      }
      return [];
    };

    const mustSkills = parseSkillsList(j.must_have_skills);
    const goodSkills = parseSkillsList(j.good_to_have_skills);

    return (
      <div className="stu-fade-in" style={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0' }}>
          {/* Top action bar */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 28px', borderBottom: '1px solid #f1f5f9', flexWrap: 'wrap', gap: '10px' }}>
            <button
              onClick={() => setSelectedInternalJobModal(null)}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'transparent', border: 'none', color: '#64748b', padding: '6px 0', fontSize: '0.875rem', fontWeight: '600', cursor: 'pointer', transition: 'color 0.2s' }}
              onMouseOver={e => e.currentTarget.style.color = '#0f172a'}
              onMouseOut={e => e.currentTarget.style.color = '#64748b'}
            >
              <ArrowLeft size={16} /> Back to Campus Openings
            </button>

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <button
                type="button"
                onClick={() => handleSkillGapAnalysis({
                  ...j,
                  job_name: j.title
                })}
                disabled={classifyingSkills}
                style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  background: '#fff', border: '1.5px solid #e2e8f0', color: '#334155',
                  padding: '8px 18px', borderRadius: '8px', fontSize: '0.85rem', fontWeight: '600',
                  cursor: classifyingSkills ? 'not-allowed' : 'pointer', transition: 'all 0.2s',
                  opacity: classifyingSkills ? 0.6 : 1
                }}
                onMouseOver={e => { if (!classifyingSkills) { e.currentTarget.style.borderColor = '#3b82f6'; e.currentTarget.style.color = '#2563eb'; e.currentTarget.style.background = '#eff6ff'; } }}
                onMouseOut={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.color = '#334155'; e.currentTarget.style.background = '#fff'; }}
              >
                {classifyingSkills ? <Loader2 size={15} className="animate-spin" /> : <Target size={15} color="#3b82f6" />}
                {classifyingSkills ? 'Analyzing…' : 'Skill Gap Analysis'}
              </button>

              {j.has_jd_file && canDownloadInternalJd && (
                <button
                  type="button"
                  onClick={() => handleDownloadJobJd(j)}
                  disabled={downloadingJdId === j.id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '8px',
                    background: '#eff6ff', border: '1.5px solid #bfdbfe',
                    color: '#2563eb', padding: '8px 18px', borderRadius: '8px',
                    fontSize: '0.85rem', fontWeight: '600',
                    cursor: downloadingJdId === j.id ? 'not-allowed' : 'pointer',
                    opacity: downloadingJdId === j.id ? 0.7 : 1,
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={e => { if (downloadingJdId !== j.id) { e.currentTarget.style.background = '#dbeafe'; } }}
                  onMouseOut={e => { e.currentTarget.style.background = '#eff6ff'; }}
                >
                  {downloadingJdId === j.id ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
                  {downloadingJdId === j.id ? 'Downloading…' : 'Download Official JD'}
                </button>
              )}

              {!j.has_shown_interest ? (
                canApplyInternalJobs ? (
                  <button
                    type="button"
                    onClick={() => handleShowInterest(j.id)}
                    disabled={submittingInterestId === j.id}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'linear-gradient(135deg, #10b981, #059669)', color: '#fff', border: 'none', padding: '9px 22px', borderRadius: '8px', fontSize: '0.88rem', fontWeight: '700', cursor: 'pointer', boxShadow: '0 2px 8px rgba(16,185,129,0.3)', transition: 'all 0.2s' }}
                  >
                    {submittingInterestId === j.id ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}
                    {submittingInterestId === j.id ? 'Submitting...' : 'Show Interest'}
                  </button>
                ) : null
              ) : (
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: '6px',
                  padding: '8px 16px', borderRadius: '8px', fontWeight: '700', fontSize: '0.84rem',
                  background: j.interest_status === 'APPROVED' ? '#ecfdf5' : (j.interest_status === 'REJECTED' ? '#fef2f2' : '#fffbeb'),
                  color: j.interest_status === 'APPROVED' ? '#065f46' : (j.interest_status === 'REJECTED' ? '#991b1b' : '#92400e'),
                  border: `1px solid ${j.interest_status === 'APPROVED' ? '#6ee7b7' : (j.interest_status === 'REJECTED' ? '#fecaca' : '#fcd34d')}`,
                }}>
                  {j.interest_status === 'APPROVED' ? <CheckCircle size={15} /> : (j.interest_status === 'REJECTED' ? <XCircle size={15} /> : <Clock size={15} />)}
                  {j.interest_status === 'APPROVED' ? 'Approved / Shortlisted' : (j.interest_status === 'REJECTED' ? 'Not Shortlisted' : '✓ Interest Expressed (Under Review)')}
                </span>
              )}
            </div>
          </div>

          {/* Job identity */}
          <div style={{ padding: '24px 28px 0' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '20px' }}>
              <div>
                <h1 style={{ margin: '0 0 6px 0', fontSize: '1.9rem', fontWeight: '800', color: '#0f172a', letterSpacing: '-0.5px', lineHeight: 1.15 }}>
                  {j.title}
                </h1>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#2563eb', fontSize: '1.1rem', fontWeight: '600' }}>
                  <Building2 size={16} />
                  {j.company_name || 'Campus Recruitment Partner'}
                </div>
              </div>

              {j.has_shown_interest && (
                <span style={{
                  padding: '8px 16px', borderRadius: '20px', fontWeight: '700', fontSize: '0.82rem',
                  background: j.interest_status === 'APPROVED' ? '#ecfdf5' : '#fef3c7',
                  color: j.interest_status === 'APPROVED' ? '#065f46' : '#92400e',
                  border: `1px solid ${j.interest_status === 'APPROVED' ? '#a7f3d0' : '#fde68a'}`
                }}>
                  Status: {j.interest_status || 'INTERESTED'}
                </span>
              )}
            </div>

            {/* Meta stat row */}
            <div style={{ display: 'flex', gap: '0', marginTop: '24px', borderTop: '1px solid #f1f5f9' }}>
              {[
                { icon: <DollarSign size={16} />, iconBg: '#dcfce7', iconColor: '#16a34a', label: 'Salary / CTC', value: j.salary_package || 'Industry Standard' },
                { icon: <MapPin size={16} />, iconBg: '#fee2e2', iconColor: '#dc2626', label: 'Location', value: j.location || 'On-Site / Hybrid' },
                { icon: <Calendar size={16} />, iconBg: '#dbeafe', iconColor: '#2563eb', label: 'Posted On', value: j.created_at || 'Recently' },
              ].map((item, i) => (
                <div key={i} style={{
                  flex: 1, display: 'flex', alignItems: 'center', gap: '14px',
                  padding: '16px 24px', borderRight: i < 2 ? '1px solid #f1f5f9' : 'none',
                }}>
                  <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: item.iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: item.iconColor }}>
                    {item.icon}
                  </div>
                  <div>
                    <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '3px' }}>{item.label}</div>
                    <div style={{ fontSize: '0.9rem', color: '#0f172a', fontWeight: '700' }}>{item.value}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Content body matching Mantrika layout */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '24px 28px' }}>
          {/* TPO Remarks Banner if any */}
          {j.remarks && (
            <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '14px', padding: '20px 24px', marginBottom: '24px', boxShadow: '0 2px 8px rgba(245,158,11,0.08)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <span style={{ fontWeight: 800, fontSize: '0.95rem', color: '#92400e', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <MessageSquare size={18} /> Remarks from Placement Officer ({j.remarks_added_by || 'TPO'})
                </span>
                {j.remarks_added_at && (
                  <span style={{ fontSize: '0.8rem', color: '#b45309' }}>{j.remarks_added_at}</span>
                )}
              </div>
              <p style={{ margin: 0, color: '#78350f', fontSize: '0.92rem', lineHeight: 1.6 }}>
                {j.remarks}
              </p>
            </div>
          )}

          <div style={{ background: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 2px 12px rgba(0,0,0,0.04)', overflow: 'hidden' }}>
            {/* Role Description */}
            <div style={{ padding: '24px 28px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                <div style={{ width: '32px', height: '32px', background: 'linear-gradient(135deg,#e0f2fe,#bae6fd)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <FileText size={16} color="#0369a1" />
                </div>
                <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: '700', color: '#0f172a' }}>Role Description</h3>
              </div>
              <div style={{ fontSize: '0.9rem', lineHeight: '1.75', color: '#374151', whiteSpace: 'pre-wrap' }}>
                {j.description || "Exciting opportunity shared by your college placement cell. Review the responsibilities and express your interest."}
              </div>
            </div>

            <div style={{ height: '1px', background: '#f1f5f9', margin: '0 28px' }} />

            {/* Key Responsibilities */}
            <div style={{ padding: '24px 28px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                <div style={{ width: '32px', height: '32px', background: 'linear-gradient(135deg,#ede9fe,#ddd6fe)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <ClipboardCheck size={16} color="#6d28d9" />
                </div>
                <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: '700', color: '#0f172a' }}>Key Responsibilities</h3>
              </div>
              <div style={{ fontSize: '0.9rem', lineHeight: '1.75', color: '#374151', whiteSpace: 'pre-wrap' }}>
                {j.responsibilities || "• Collaborate on core technical solutions and team initiatives\n• Deliver high quality assignments aligned with project objectives"}
              </div>
            </div>

            <div style={{ height: '1px', background: '#f1f5f9', margin: '0 28px' }} />

            {/* Requirements */}
            <div style={{ padding: '24px 28px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                <div style={{ width: '32px', height: '32px', background: 'linear-gradient(135deg,#fce7f3,#fbcfe8)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Award size={16} color="#be185d" />
                </div>
                <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: '700', color: '#0f172a' }}>Requirements & Eligibility</h3>
              </div>
              <div style={{ fontSize: '0.9rem', lineHeight: '1.75', color: '#374151', whiteSpace: 'pre-wrap' }}>
                {j.requirements || "• Enrolled in mapped academic batches\n• Strong fundamentals and motivation to grow"}
              </div>
            </div>

            <div style={{ height: '1px', background: '#f1f5f9', margin: '0 28px' }} />

            {/* Skills */}
            <div style={{ padding: '24px 28px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                <div style={{ width: '32px', height: '32px', background: 'linear-gradient(135deg,#fef3c7,#fde68a)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Zap size={16} color="#b45309" />
                </div>
                <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: '700', color: '#0f172a' }}>Skills & Competencies</h3>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
                <div>
                  <p style={{ margin: '0 0 10px 0', fontSize: '0.75rem', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.6px' }}>Must Have</p>
                  {mustSkills.length === 0 ? (
                    <span style={{ color: '#94a3b8', fontSize: '0.875rem' }}>Core fundamentals</span>
                  ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {mustSkills.map((skill, idx) => (
                        <span key={`must-${idx}`} style={{ padding: '6px 14px', background: 'linear-gradient(135deg,#e0e7ff,#ede9fe)', color: '#4338ca', borderRadius: '6px', fontSize: '0.85rem', fontWeight: '600', border: '1px solid #c7d2fe' }}>
                          {skill}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {goodSkills.length > 0 && (
                  <div>
                    <p style={{ margin: '0 0 10px 0', fontSize: '0.75rem', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.6px' }}>Good to Have</p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {goodSkills.map((skill, idx) => (
                        <span key={`good-${idx}`} style={{ padding: '6px 14px', background: '#f8fafc', color: '#475569', borderRadius: '6px', fontSize: '0.85rem', fontWeight: '600', border: '1px solid #e2e8f0' }}>
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderCampusJobOpenings = () => {
    if (loadingInternalJobs) {
      return (
        <div className="stu-empty-state" style={{ marginTop: '0', textAlign: 'center' }}>
          <Loader2 size={36} className="animate-spin" style={{ color: '#3b82f6', margin: '0 auto 12px' }} />
          <h3 style={{ color: 'var(--text-muted)' }}>Loading campus job openings...</h3>
        </div>
      );
    }

    if (internalJobs.length === 0) {
      return (
        <div className="stu-empty-state" style={{ marginTop: '0', textAlign: 'center' }}>
          <Building2 size={48} style={{ color: 'var(--border)', marginBottom: '16px' }} />
          <h3 style={{ color: 'var(--text-muted)', marginBottom: '8px' }}>No Campus Jobs Available</h3>
          <p style={{ color: 'var(--text-faint)', fontSize: '14px' }}>
            There are currently no internal jobs mapped to your batch. When your TPO posts new opportunities, they will appear here.
          </p>
        </div>
      );
    }

    return (
      <div className="stu-sessions-grid" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '20px' }}>
        {internalJobs.map(job => (
          <div
            key={job.id}
            className="stu-session-card stu-placement-job-card"
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '24px',
              padding: '24px',
              background: '#fff',
              borderRadius: '16px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 2px 10px rgba(0, 0, 0, 0.02)',
              transition: 'all 0.3s ease',
              position: 'relative'
            }}
          >
            <div style={{ flex: '1', minWidth: '0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.85rem', fontWeight: '700', color: '#2563eb', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Building2 size={15} /> {job.company_name || 'Campus Partner'}
                </span>
                <span style={{ color: '#cbd5e1' }}>•</span>
                <span style={{ fontSize: '0.82rem', color: '#64748b', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <MapPin size={13} /> {job.location || 'On-Site / Hybrid'}
                </span>
                <span style={{ color: '#cbd5e1' }}>•</span>
                <span style={{ fontSize: '0.82rem', color: '#16a34a', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <DollarSign size={13} /> {job.salary_package || 'Industry Standard'}
                </span>
              </div>

              <h3 style={{ fontSize: '1.25rem', fontWeight: '800', color: '#0f172a', margin: '0 0 10px 0' }}>
                {job.title}
              </h3>

              <p style={{ fontSize: '0.9rem', color: '#475569', lineHeight: '1.5', margin: '0 0 16px 0', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                {job.description || 'Exclusive opportunity shared by your college placement cell.'}
              </p>

              {/* Skill chips */}
              {job.must_have_skills && job.must_have_skills.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '14px' }}>
                  {job.must_have_skills.slice(0, 4).map((sk, i) => (
                    <span key={i} style={{ padding: '3px 10px', background: '#eff6ff', color: '#1d4ed8', borderRadius: '6px', fontSize: '0.78rem', fontWeight: '600' }}>
                      {sk}
                    </span>
                  ))}
                  {job.must_have_skills.length > 4 && (
                    <span style={{ padding: '3px 8px', background: '#f1f5f9', color: '#64748b', borderRadius: '6px', fontSize: '0.78rem' }}>
                      +{job.must_have_skills.length - 4} more
                    </span>
                  )}
                </div>
              )}

              {/* TPO Remarks alert if any */}
              {job.remarks && (
                <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', padding: '10px 14px', fontSize: '0.85rem', color: '#78350f', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <MessageSquare size={15} style={{ color: '#d97706', flexShrink: 0 }} />
                  <span><strong>TPO Remarks:</strong> {job.remarks}</span>
                </div>
              )}
            </div>

            {/* Right Action Column */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '10px', minWidth: '180px' }}>
              {/* Status indicator */}
              {job.has_shown_interest ? (
                <span style={{
                  padding: '6px 14px', borderRadius: '20px', fontWeight: '700', fontSize: '0.82rem',
                  background: job.interest_status === 'APPROVED' ? '#ecfdf5' : (job.interest_status === 'REJECTED' ? '#fef2f2' : '#fffbeb'),
                  color: job.interest_status === 'APPROVED' ? '#065f46' : (job.interest_status === 'REJECTED' ? '#991b1b' : '#92400e'),
                  border: `1px solid ${job.interest_status === 'APPROVED' ? '#a7f3d0' : (job.interest_status === 'REJECTED' ? '#fecaca' : '#fde68a')}`,
                  display: 'inline-flex', alignItems: 'center', gap: '6px'
                }}>
                  {job.interest_status === 'APPROVED' ? '✓ Shortlisted' : (job.interest_status === 'REJECTED' ? 'Not Shortlisted' : '✓ Interest Expressed')}
                </span>
              ) : (
                <span style={{
                  padding: '4px 10px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '700',
                  background: '#f1f5f9', color: '#475569'
                }}>
                  Open for Batches
                </span>
              )}

              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '4px' }}>
                <button
                  type="button"
                  onClick={() => setSelectedInternalJobModal(job)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '6px',
                    padding: '8px 16px', borderRadius: '8px', border: '1px solid #cbd5e1',
                    background: '#ffffff', color: '#1e293b', fontWeight: '600', fontSize: '0.85rem', cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={e => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.borderColor = '#cbd5e1'; }}
                  onMouseOut={e => { e.currentTarget.style.background = '#ffffff'; e.currentTarget.style.borderColor = '#cbd5e1'; }}
                >
                  <Eye size={15} /> View Details
                </button>

                {!job.has_shown_interest && canApplyInternalJobs && (
                  <button
                    type="button"
                    onClick={() => handleShowInterest(job.id)}
                    disabled={submittingInterestId === job.id}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '6px',
                      padding: '8px 18px', borderRadius: '8px', border: 'none',
                      background: 'linear-gradient(135deg, #10b981, #059669)', color: '#ffffff',
                      fontWeight: '700', fontSize: '0.85rem', cursor: 'pointer',
                      boxShadow: '0 2px 8px rgba(16,185,129,0.3)'
                    }}
                  >
                    {submittingInterestId === job.id ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                    {submittingInterestId === job.id ? 'Submitting...' : 'Show Interest'}
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // --- TAB: COMPLETED SESSIONS ---
  const renderJobsTab = () => {
    if (selectedInternalJobModal && !showSkillGapAnalysis) {
      return renderInternalJobDetailView();
    }

    return (
      <div className="stu-fade-in" style={{ height: "100%", display: "flex", flexDirection: "column" }}>
        {!selectedJobDetailModal && !showSkillGapAnalysis && (
          <>
            <header className="stu-top-bar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
              <div>
                <h1>{campusJobView === 'campus' ? 'Campus Job Openings' : 'Placement Drives'}</h1>
                <span style={{ fontSize: '0.875rem', color: 'var(--text-muted)', fontWeight: 500 }}>
                  {campusJobView === 'campus'
                    ? 'Internal openings posted directly by your university placement office'
                    : "Jobs scheduled by your university's placement office"}
                </span>
              </div>

              {/* Section Switcher: Campus Postings vs Mantrika */}
              {(canViewInternalJobs && canViewPlacementDrives) && (
                <div style={{ display: 'flex', background: 'var(--surface)', padding: '4px', borderRadius: '10px', border: '1px solid var(--border)', gap: '6px' }}>
                  <button
                    onClick={() => { setCampusJobView('campus'); fetchInternalJobs(); }}
                    style={{
                      padding: '8px 16px', borderRadius: '8px', border: 'none', fontWeight: 700, fontSize: '0.86rem', cursor: 'pointer',
                      background: campusJobView === 'campus' ? '#2563eb' : 'transparent',
                      color: campusJobView === 'campus' ? '#ffffff' : 'var(--text-muted)',
                      boxShadow: campusJobView === 'campus' ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
                      display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.2s'
                    }}
                  >
                    <Building2 size={15} /> Campus Openings
                    <span style={{
                      background: campusJobView === 'campus' ? '#ffffff' : '#2563eb',
                      color: campusJobView === 'campus' ? '#2563eb' : '#ffffff',
                      fontSize: '0.72rem', padding: '1px 7px', borderRadius: '10px', fontWeight: 800
                    }}>
                      {internalJobs.length}
                    </span>
                  </button>
                  <button
                    onClick={() => setCampusJobView('placement')}
                    style={{
                      padding: '8px 16px', borderRadius: '8px', border: 'none', fontWeight: 700, fontSize: '0.86rem', cursor: 'pointer',
                      background: campusJobView === 'placement' ? '#2563eb' : 'transparent',
                      color: campusJobView === 'placement' ? '#ffffff' : 'var(--text-muted)',
                      boxShadow: campusJobView === 'placement' ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
                      display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.2s'
                    }}
                  >
                    <Briefcase size={15} /> Placement Drives
                    {jobs.length > 0 && (
                      <span style={{
                        background: campusJobView === 'placement' ? '#ffffff' : '#64748b',
                        color: campusJobView === 'placement' ? '#2563eb' : '#ffffff',
                        fontSize: '0.72rem', padding: '1px 7px', borderRadius: '10px', fontWeight: 800
                      }}>
                        {jobs.length}
                      </span>
                    )}
                  </button>
                </div>
              )}
            </header>

            <div className="stu-padded">
              {campusJobView === 'campus' && canViewInternalJobs ? (
                renderCampusJobOpenings()
              ) : canViewPlacementDrives ? (
                <>
                  <div className="stu-placement-controls" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '20px' }}>
                    <div className="stu-placement-tabs" style={{ display: 'flex', background: 'var(--surface)', padding: '4px', borderRadius: '8px', border: '1px solid var(--border)' }}>
                      <button
                        onClick={() => setJobsSubTab('available')}
                        style={{ padding: '6px 12px', fontSize: '0.85rem', fontWeight: 600, border: 'none', background: jobsSubTab === 'available' ? '#fff' : 'transparent', color: jobsSubTab === 'available' ? '#3b82f6' : 'var(--text-muted)', borderRadius: '6px', boxShadow: jobsSubTab === 'available' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none', cursor: 'pointer', transition: 'all 0.2s ease' }}
                      >
                        Available Drives
                      </button>
                      <button
                        onClick={() => setJobsSubTab('applied')}
                        style={{ padding: '6px 12px', fontSize: '0.85rem', fontWeight: 600, border: 'none', background: jobsSubTab === 'applied' ? '#fff' : 'transparent', color: jobsSubTab === 'applied' ? '#3b82f6' : 'var(--text-muted)', borderRadius: '6px', boxShadow: jobsSubTab === 'applied' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none', cursor: 'pointer', transition: 'all 0.2s ease' }}
                      >
                        My Applications
                      </button>
                    </div>

                    {jobsSubTab === 'available' && (
              <label className="stu-placement-filter" style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.9rem', color: 'var(--text-muted)', cursor: 'pointer', fontWeight: 600 }}>
                <input
                  type="checkbox"
                  checked={showOnlyMatchedJobs}
                  onChange={(e) => setShowOnlyMatchedJobs(e.target.checked)}
                  style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#10b981' }}
                />
                SHOW ONLY AI MATCHED ROLES
              </label>
            )}
          </div>

          {loadingJobs ? (
            <div className="stu-empty-state" style={{ marginTop: '0', textAlign: 'center' }}>
              <h3 style={{ color: 'var(--text-muted)' }}>Loading scheduled drives...</h3>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: '24px', alignItems: 'start' }}>
              <div>
                {jobs.length === 0 ? (
                  <div className="stu-empty-state" style={{ marginTop: '0', textAlign: 'center' }}>
                    <Briefcase size={48} style={{ color: 'var(--border)', marginBottom: '16px' }} />
                    <h3 style={{ color: 'var(--text-muted)', marginBottom: '8px' }}>No Drives Scheduled</h3>
                    <p style={{ color: 'var(--text-faint)', fontSize: '14px' }}>There are currently no placement drives scheduled for your university.</p>
                  </div>
                ) : (
                  <div className="stu-sessions-grid" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '20px' }}>
                    {jobsSubTab === 'available' ? (
                (showOnlyMatchedJobs ? jobs.filter(j => j.ai_matched && !j.application_status) : jobs.filter(j => !j.application_status)).length === 0 ? (
                  <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
                    No jobs found for the selected filter. Try unchecking "Show Only AI Matched Roles".
                  </div>
                ) : (
                  (showOnlyMatchedJobs ? jobs.filter(j => j.ai_matched && !j.application_status) : jobs.filter(j => !j.application_status)).map(job => (
                    <div 
                      key={job.drive_id} 
                      className="stu-session-card stu-placement-job-card"
                      style={{ 
                        display: 'flex', 
                        flexDirection: 'row', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        gap: '24px',
                        padding: '24px',
                        background: '#fff',
                        borderRadius: '16px',
                        border: '1px solid #e2e8f0',
                        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.02)',
                        transition: 'all 0.3s ease',
                        position: 'relative'
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.transform = 'translateY(-3px)';
                        e.currentTarget.style.boxShadow = '0 12px 24px rgba(0, 0, 0, 0.06)';
                        e.currentTarget.style.borderColor = '#cbd5e1';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.transform = 'none';
                        e.currentTarget.style.boxShadow = '0 2px 10px rgba(0, 0, 0, 0.02)';
                        e.currentTarget.style.borderColor = '#e2e8f0';
                      }}
                    >
                      {/* Left Section: Job Identity */}
                      <div className="stu-placement-job-info" style={{ flex: '1', minWidth: '250px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
                          <span style={{
                            background: 'rgba(59, 130, 246, 0.1)',
                            color: '#3b82f6',
                            padding: '4px 10px',
                            borderRadius: '20px',
                            fontSize: '11px',
                            fontWeight: 700,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}>
                            <Briefcase size={12} />
                            {job.status}
                          </span>
                          {job.ai_matched && (
                            <span style={{
                              background: 'linear-gradient(135deg, rgba(16,185,129,0.12), rgba(5,150,105,0.08))',
                              color: '#059669',
                              padding: '4px 10px',
                              borderRadius: '20px',
                              fontSize: '11px',
                              fontWeight: 700,
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              border: '1px solid rgba(16,185,129,0.2)'
                            }}>
                              <Sparkles size={10} /> AI Match
                            </span>
                          )}
                          {job.application_status && (
                            <span style={{
                              background: job.application_status.toUpperCase() === 'APPROVED' ? 'rgba(16, 185, 129, 0.1)' : job.application_status.toUpperCase() === 'PENDING' ? 'rgba(234, 179, 8, 0.1)' : 'rgba(245, 158, 11, 0.1)',
                              color: job.application_status.toUpperCase() === 'APPROVED' ? '#059669' : job.application_status.toUpperCase() === 'PENDING' ? '#ca8a04' : '#d97706',
                              padding: '4px 10px',
                              borderRadius: '20px',
                              fontSize: '11px',
                              fontWeight: 700,
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}>
                              {job.application_status.toUpperCase() === 'APPROVED' ? '🎉 Approved' : job.application_status.toUpperCase() === 'PENDING' ? '🔄 Pending' : '⏳ ' + job.application_status}
                            </span>
                          )}
                        </div>

                        <h3 style={{ fontSize: '1.25rem', fontWeight: 800, margin: '0 0 8px 0', color: '#0f172a', letterSpacing: '-0.3px' }}>
                          {job.job_name}
                        </h3>

                        <div className="stu-placement-job-meta" style={{ display: 'flex', alignItems: 'center', gap: '16px', color: '#64748b', fontSize: '13px', fontWeight: '500' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#0369a1', fontWeight: 600 }}>
                            <Building2 size={15} /> {job.company_name ? job.company_name.replace(/\b\w/g, l => l.toUpperCase()) : 'Corporate Partner'}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Calendar size={14} /> {job.scheduled_date}
                          </div>
                        </div>

                        {job.remarks && !job.student_remarks_read && (
                          <div style={{ marginTop: '16px', background: 'rgba(245, 158, 11, 0.08)', border: '1px solid rgba(245, 158, 11, 0.3)', borderRadius: '10px', padding: '10px 12px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '4px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 700, fontSize: '11px', color: '#b45309' }}>
                                <MessageSquare size={12} /> Remarks from TPO
                              </div>
                              <button
                                title="Mark as read"
                                onClick={(e) => { e.stopPropagation(); handleMarkRemarkAsRead(job.drive_id); }}
                                style={{ background: '#f59e0b', color: '#fff', border: 'none', padding: '2px 4px', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                              >
                                <Check size={12} />
                              </button>
                            </div>
                            <div style={{ fontSize: '12px', color: '#78350f', lineHeight: 1.4 }}>
                              {job.remarks}
                            </div>
                          </div>
                        )}
                      </div>



                      <div className="stu-placement-job-actions" style={{ display: 'flex', flexDirection: 'column', gap: '10px', minWidth: '160px', alignItems: 'stretch' }}>
                        {job.application_status ? (
                          job.application_status.toUpperCase() === 'INTERVIEW' && (job.interview_state === 'AVAILABLE' || job.interview_state === 'SCHEDULED') ? (
                            <SubFeatureGate feature="mantrika" sub="mantrika_interview">
                            <button
                              onClick={(e) => { e.stopPropagation(); handleMantrikaInterview(job); }}
                              style={{ background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)', color: '#fff', border: 'none', padding: '12px 20px', borderRadius: '10px', fontSize: '13px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', cursor: 'pointer', boxShadow: '0 4px 12px rgba(139, 92, 246, 0.2)' }}
                              onMouseOver={(e) => {
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = '0 6px 16px rgba(139, 92, 246, 0.35)';
                              }}
                              onMouseOut={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(139, 92, 246, 0.2)';
                              }}
                            >
                              <ExternalLink size={15} /> {job.interview_intent === 'HUMAN_INTERVIEW' ? 'Start Interview' : 'Start AI Interview'}
                            </button>
                            </SubFeatureGate>
                          ) : null
                        ) : (
                          <SubFeatureGate feature="jobs" sub="jobs_apply">
                          <button
                            onClick={() => handleApplyJob(job)}
                            style={{ background: 'linear-gradient(135deg, #3b82f6, #2563eb)', color: '#fff', border: 'none', padding: '12px 20px', borderRadius: '10px', fontSize: '13px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 4px 12px rgba(59, 130, 246, 0.25)' }}
                            onMouseOver={(e) => {
                              e.currentTarget.style.transform = 'translateY(-2px)';
                              e.currentTarget.style.boxShadow = '0 6px 16px rgba(59, 130, 246, 0.35)';
                            }}
                            onMouseOut={(e) => {
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.25)';
                            }}
                          >
                            Apply Now
                          </button>
                          </SubFeatureGate>
                        )}
                        
                        <button
                          onClick={() => setSelectedJobDetailModal(job)}
                          style={{ background: '#f8fafc', color: '#475569', border: '1px solid #e2e8f0', padding: '12px 20px', borderRadius: '10px', fontSize: '13px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', cursor: 'pointer', transition: 'all 0.2s ease' }}
                          onMouseOver={e => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.borderColor = '#cbd5e1'; }}
                          onMouseOut={e => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = '#e2e8f0'; }}
                        >
                          <Eye size={16} /> View Details
                        </button>
                      </div>
                    </div>
                  ))
                )
              ) : (
                jobs.filter(j => j.application_status).length === 0 ? (
                  <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
                    You have not applied to any placement drives yet.
                  </div>
                ) : (
                  jobs.filter(j => j.application_status)
                  .sort((a, b) => new Date(b.applied_at || 0) - new Date(a.applied_at || 0))
                  .map(job => (
                    <div 
                      key={job.drive_id} 
                      className="stu-session-card stu-placement-job-card"
                      style={{ 
                        display: 'flex', 
                        flexDirection: 'row', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        gap: '24px',
                        padding: '24px',
                        background: '#fff',
                        borderRadius: '16px',
                        border: '1px solid #e2e8f0',
                        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.02)',
                        transition: 'all 0.3s ease',
                        position: 'relative'
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.transform = 'translateY(-3px)';
                        e.currentTarget.style.boxShadow = '0 12px 24px rgba(0, 0, 0, 0.06)';
                        e.currentTarget.style.borderColor = '#cbd5e1';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.transform = 'none';
                        e.currentTarget.style.boxShadow = '0 2px 10px rgba(0, 0, 0, 0.02)';
                        e.currentTarget.style.borderColor = '#e2e8f0';
                      }}
                    >
                      {/* Left Section: Job Identity */}
                      <div className="stu-placement-job-info" style={{ flex: '1', minWidth: '250px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
                          <span style={{
                            background: 'rgba(59, 130, 246, 0.1)',
                            color: '#3b82f6',
                            padding: '4px 10px',
                            borderRadius: '20px',
                            fontSize: '11px',
                            fontWeight: 700,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}>
                            <Briefcase size={12} />
                            {job.status}
                          </span>
                          {job.ai_matched && (
                            <span style={{
                              background: 'linear-gradient(135deg, rgba(16,185,129,0.12), rgba(5,150,105,0.08))',
                              color: '#059669',
                              padding: '4px 10px',
                              borderRadius: '20px',
                              fontSize: '11px',
                              fontWeight: 700,
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              border: '1px solid rgba(16,185,129,0.2)'
                            }}>
                              <Sparkles size={10} /> AI Match
                            </span>
                          )}
                          {job.application_status && (
                            <span style={{
                              background: job.application_status.toUpperCase() === 'APPROVED' ? 'rgba(16, 185, 129, 0.1)' : job.application_status.toUpperCase() === 'PENDING' ? 'rgba(234, 179, 8, 0.1)' : 'rgba(245, 158, 11, 0.1)',
                              color: job.application_status.toUpperCase() === 'APPROVED' ? '#059669' : job.application_status.toUpperCase() === 'PENDING' ? '#ca8a04' : '#d97706',
                              padding: '4px 10px',
                              borderRadius: '20px',
                              fontSize: '11px',
                              fontWeight: 700,
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}>
                              {job.application_status.toUpperCase() === 'APPROVED' ? '🎉 Approved' : job.application_status.toUpperCase() === 'PENDING' ? '🔄 Pending' : '⏳ ' + job.application_status}
                            </span>
                          )}
                        </div>

                        <h3 style={{ fontSize: '1.25rem', fontWeight: 800, margin: '0 0 8px 0', color: '#0f172a', letterSpacing: '-0.3px' }}>
                          {job.job_name}
                        </h3>

                        <div className="stu-placement-job-meta" style={{ display: 'flex', alignItems: 'center', gap: '16px', color: '#64748b', fontSize: '13px', fontWeight: '500' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#0369a1', fontWeight: 600 }}>
                            <Building2 size={15} /> {job.company_name ? job.company_name.replace(/\b\w/g, l => l.toUpperCase()) : 'Corporate Partner'}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Calendar size={14} /> {job.scheduled_date}
                          </div>
                        </div>

                        {job.remarks && !job.student_remarks_read && (
                          <div style={{ marginTop: '16px', background: 'rgba(245, 158, 11, 0.08)', border: '1px solid rgba(245, 158, 11, 0.3)', borderRadius: '10px', padding: '10px 12px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '4px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 700, fontSize: '11px', color: '#b45309' }}>
                                <MessageSquare size={12} /> Remarks from TPO
                              </div>
                              <button
                                title="Mark as read"
                                onClick={(e) => { e.stopPropagation(); handleMarkRemarkAsRead(job.drive_id); }}
                                style={{ background: '#f59e0b', color: '#fff', border: 'none', padding: '2px 4px', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                              >
                                <Check size={12} />
                              </button>
                            </div>
                            <div style={{ fontSize: '12px', color: '#78350f', lineHeight: 1.4 }}>
                              {job.remarks}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Right Section: Actions */}
                      <div className="stu-placement-job-actions" style={{ display: 'flex', flexDirection: 'column', gap: '10px', minWidth: '160px', alignItems: 'stretch' }}>
                        {job.application_status.toUpperCase() === 'INTERVIEW' && (job.interview_state === 'AVAILABLE' || job.interview_state === 'SCHEDULED') ? (
                          <SubFeatureGate feature="mantrika" sub="mantrika_interview">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleMantrikaInterview(job); }}
                            style={{ width: '100%', background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)', color: '#fff', border: 'none', padding: '12px 20px', borderRadius: '10px', fontSize: '13px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', cursor: 'pointer', boxShadow: '0 4px 12px rgba(139, 92, 246, 0.2)', transition: 'all 0.2s' }}
                            onMouseOver={(e) => {
                              e.currentTarget.style.transform = 'translateY(-2px)';
                              e.currentTarget.style.boxShadow = '0 6px 16px rgba(139, 92, 246, 0.35)';
                            }}
                            onMouseOut={(e) => {
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = '0 4px 12px rgba(139, 92, 246, 0.2)';
                            }}
                          >
                            <ExternalLink size={15} /> {job.interview_intent === 'HUMAN_INTERVIEW' ? 'Start Interview' : 'Start AI Interview'}
                          </button>
                          </SubFeatureGate>
                        ) : null}
                        
                        <button
                          onClick={() => setSelectedJobDetailModal(job)}
                          style={{ background: '#f8fafc', color: '#475569', border: '1px solid #e2e8f0', padding: '12px 20px', borderRadius: '10px', fontSize: '13px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', cursor: 'pointer', transition: 'all 0.2s ease' }}
                          onMouseOver={e => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.borderColor = '#cbd5e1'; }}
                          onMouseOut={e => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = '#e2e8f0'; }}
                        >
                          <Eye size={16} /> View Details
                        </button>
                      </div>
                    </div>
                  ))
                )
              )}
            </div>
          )}
        </div>
        
        {/* --- Custom Skill Gap Analysis Sidebar --- */}
        <div style={{ background: '#fff', borderRadius: '16px', padding: '24px', border: '1px solid #e2e8f0', boxShadow: '0 2px 10px rgba(0,0,0,0.02)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
            <Sparkles size={20} color="#10b981" />
            <h3 style={{ fontSize: '1.1rem', fontWeight: 800, color: '#0f172a', margin: 0 }}>Custom Analysis</h3>
          </div>
          <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '20px', lineHeight: 1.5 }}>
            Enter any job role you're interested in to see how your current skills match up against the requirements.
          </p>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#475569', marginBottom: '8px', textTransform: 'uppercase' }}>Target Job Role</label>
            <input 
              type="text" 
              placeholder="e.g. Data Scientist" 
              value={customJobRole}
              onChange={e => setCustomJobRole(e.target.value)}
              style={{ width: '100%', padding: '12px 14px', border: '1px solid #cbd5e1', borderRadius: '10px', fontSize: '14px', boxSizing: 'border-box' }}
            />
          </div>
          <button 
            onClick={() => handleSkillGapAnalysis({ job_name: customJobRole, description: `Analysis for ${customJobRole}`, responsibilities: '', must_have_skills: [], good_to_have_skills: [], isCustom: true })}
            disabled={!customJobRole.trim() || classifyingSkills}
            style={{ width: '100%', background: 'linear-gradient(135deg, #10b981, #059669)', color: '#fff', border: 'none', padding: '14px', borderRadius: '10px', fontWeight: 700, fontSize: '14px', cursor: customJobRole.trim() ? 'pointer' : 'not-allowed', opacity: customJobRole.trim() ? 1 : 0.6 }}
          >
            {classifyingSkills ? 'Analyzing...' : 'Generate Analysis'}
          </button>
        </div>
      </div>
    )}
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-muted)' }}>
                  Campus jobs and placement drives are not currently available for your account.
                </div>
              )}
            </div>
          </>
        )}

          {/* Job Details Modal */}
        {selectedJobDetailModal && !showSkillGapAnalysis && (
          <div style={{ flex: 1, background: '#f8fafc', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

            {/* ── Header ── */}
            <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0' }}>

              {/* Top action bar */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 28px', borderBottom: '1px solid #f1f5f9' }}>
                <button
                  onClick={() => setSelectedJobDetailModal(null)}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'transparent', border: 'none', color: '#64748b', padding: '6px 0', fontSize: '0.875rem', fontWeight: '600', cursor: 'pointer', transition: 'color 0.2s' }}
                  onMouseOver={e => e.currentTarget.style.color = '#0f172a'}
                  onMouseOut={e => e.currentTarget.style.color = '#64748b'}
                >
                  <ArrowLeft size={16} /> Back to Drives
                </button>

                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <button
                    type="button"
                    onClick={() => handleSkillGapAnalysis(selectedJobDetailModal)}
                    disabled={classifyingSkills}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#fff', border: '1.5px solid #e2e8f0', color: '#334155', padding: '8px 18px', borderRadius: '8px', fontSize: '0.85rem', fontWeight: '600', cursor: classifyingSkills ? 'not-allowed' : 'pointer', transition: 'all 0.2s', opacity: classifyingSkills ? 0.6 : 1 }}
                    onMouseOver={e => { if (!classifyingSkills) { e.currentTarget.style.borderColor = '#3b82f6'; e.currentTarget.style.color = '#2563eb'; e.currentTarget.style.background = '#eff6ff'; } }}
                    onMouseOut={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.color = '#334155'; e.currentTarget.style.background = '#fff'; }}
                  >
                    {classifyingSkills ? <Loader2 size={15} className="animate-spin" /> : <Target size={15} color="#3b82f6" />}
                    {classifyingSkills ? 'Analyzing…' : 'Skill Gap Analysis'}
                  </button>

                  {!selectedJobDetailModal.application_status && (
                    <SubFeatureGate feature="jobs" sub="jobs_apply">
                    <button
                      type="button"
                      onClick={() => { handleApplyJob(selectedJobDetailModal); setSelectedJobDetailModal(null); }}
                      style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#2563eb', color: '#fff', border: 'none', padding: '8px 20px', borderRadius: '8px', fontSize: '0.85rem', fontWeight: '700', cursor: 'pointer', boxShadow: '0 1px 6px rgba(37,99,235,0.3)', transition: 'all 0.2s' }}
                      onMouseOver={e => { e.currentTarget.style.background = '#1d4ed8'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(37,99,235,0.4)'; }}
                      onMouseOut={e => { e.currentTarget.style.background = '#2563eb'; e.currentTarget.style.boxShadow = '0 1px 6px rgba(37,99,235,0.3)'; }}
                    >
                      Apply Now
                    </button>
                    </SubFeatureGate>
                  )}

                  {selectedJobDetailModal.application_status && selectedJobDetailModal.application_status.toUpperCase() === 'INTERVIEW' && (selectedJobDetailModal.interview_state === 'AVAILABLE' || selectedJobDetailModal.interview_state === 'SCHEDULED') && (
                    <SubFeatureGate feature="mantrika" sub="mantrika_interview">
                    <button
                      onClick={() => handleMantrikaInterview(selectedJobDetailModal)}
                      style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#7c3aed', color: '#fff', border: 'none', padding: '8px 20px', borderRadius: '8px', fontSize: '0.85rem', fontWeight: '700', cursor: 'pointer', boxShadow: '0 1px 6px rgba(124,58,237,0.3)', transition: 'all 0.2s' }}
                    >
                      <ExternalLink size={14} /> {selectedJobDetailModal.interview_intent === 'HUMAN_INTERVIEW' ? 'Start Interview' : 'Start AI Interview'}
                    </button>
                    </SubFeatureGate>
                  )}
                </div>
              </div>

              {/* Job identity */}
              <div style={{ padding: '24px 28px 0' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '20px' }}>

                  {/* Left: title */}
                  <div>

                    {/* Title */}
                    <h1 style={{ margin: '0 0 6px 0', fontSize: '1.9rem', fontWeight: '800', color: '#0f172a', letterSpacing: '-0.5px', lineHeight: 1.15 }}>
                      {selectedJobDetailModal.job_name}
                    </h1>

                    {/* Company */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#2563eb', fontSize: '1.1rem', fontWeight: '600' }}>
                      <Building2 size={14} />
                      {selectedJobDetailModal.company_name
                        ? selectedJobDetailModal.company_name.replace(/\b\w/g, l => l.toUpperCase())
                        : 'Corporate Partner'}
                    </div>
                  </div>

                  {/* Right: status badge */}
                  {selectedJobDetailModal.application_status && (
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: '6px',
                      padding: '8px 16px', borderRadius: '8px', fontWeight: '700', fontSize: '0.82rem', flexShrink: 0,
                      background: selectedJobDetailModal.application_status.toUpperCase() === 'APPROVED' ? '#ecfdf5' : '#fffbeb',
                      color: selectedJobDetailModal.application_status.toUpperCase() === 'APPROVED' ? '#065f46' : '#92400e',
                      border: `1px solid ${selectedJobDetailModal.application_status.toUpperCase() === 'APPROVED' ? '#6ee7b7' : '#fcd34d'}`,
                    }}>
                      {selectedJobDetailModal.application_status.toUpperCase() === 'APPROVED' ? <CheckCircle size={13} /> : <Clock size={13} />}
                      {selectedJobDetailModal.application_status.toUpperCase() === 'APPROVED' ? 'Approved' : selectedJobDetailModal.application_status}
                    </span>
                  )}
                </div>

                {/* ── Meta stat row ── */}
                <div style={{ display: 'flex', gap: '0', marginTop: '24px', borderTop: '1px solid #f1f5f9' }}>
                  {[
                    { icon: <DollarSign size={16} />, iconBg: '#dcfce7', iconColor: '#16a34a', label: 'Salary / CTC', value: selectedJobDetailModal.salary_package || 'Industry Standard' },
                    { icon: <MapPin size={16} />, iconBg: '#fee2e2', iconColor: '#dc2626', label: 'Location', value: selectedJobDetailModal.location || 'Bengaluru / Hybrid' },
                    { icon: <Calendar size={16} />, iconBg: '#dbeafe', iconColor: '#2563eb', label: 'Posted On', value: selectedJobDetailModal.scheduled_date || 'N/A' },
                  ].map((item, i) => (
                    <div key={i} style={{
                      flex: 1,
                      display: 'flex', alignItems: 'center', gap: '14px',
                      padding: '16px 24px',
                      borderRight: i < 2 ? '1px solid #f1f5f9' : 'none',
                    }}>
                      <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: item.iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: item.iconColor }}>
                        {item.icon}
                      </div>
                      <div>
                        <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '3px' }}>{item.label}</div>
                        <div style={{ fontSize: '0.9rem', color: '#0f172a', fontWeight: '700' }}>{item.value}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* ── Content body ── */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '24px 28px' }}>
              {/* Single unified card */}
              <div style={{ background: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 2px 12px rgba(0,0,0,0.04)', overflow: 'hidden' }}>

                {/* Role Description */}
                <div style={{ padding: '24px 28px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                    <div style={{ width: '32px', height: '32px', background: 'linear-gradient(135deg,#e0f2fe,#bae6fd)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <FileText size={16} color="#0369a1" />
                    </div>
                    <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: '700', color: '#0f172a' }}>Role Description</h3>
                  </div>
                  <div style={{ fontSize: '0.9rem', lineHeight: '1.75', color: '#374151', whiteSpace: 'pre-wrap' }}>
                    {(selectedJobDetailModal.description && selectedJobDetailModal.description.trim()) || "We are looking for an exceptional candidate for this role. The selected candidate will collaborate with cross-functional technical teams and lead key projects."}
                  </div>
                </div>

                {/* Divider */}
                <div style={{ height: '1px', background: '#f1f5f9', margin: '0 28px' }} />

                {/* Key Responsibilities */}
                <div style={{ padding: '24px 28px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                    <div style={{ width: '32px', height: '32px', background: 'linear-gradient(135deg,#ede9fe,#ddd6fe)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <ClipboardCheck size={16} color="#6d28d9" />
                    </div>
                    <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: '700', color: '#0f172a' }}>Key Responsibilities</h3>
                  </div>
                  <div style={{ fontSize: '0.9rem', lineHeight: '1.75', color: '#374151', whiteSpace: 'pre-wrap' }}>
                    {(selectedJobDetailModal.responsibilities && selectedJobDetailModal.responsibilities.trim()) || "• Lead development and deployment of key features\n• Collaborate with engineering and product teams\n• Ensure high standards of performance and reliability"}
                  </div>
                </div>

                {/* Divider */}
                <div style={{ height: '1px', background: '#f1f5f9', margin: '0 28px' }} />

                {/* Requirements */}
                <div style={{ padding: '24px 28px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                    <div style={{ width: '32px', height: '32px', background: 'linear-gradient(135deg,#fce7f3,#fbcfe8)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Award size={16} color="#be185d" />
                    </div>
                    <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: '700', color: '#0f172a' }}>Requirements</h3>
                  </div>
                  <div style={{ fontSize: '0.9rem', lineHeight: '1.75', color: '#374151', whiteSpace: 'pre-wrap' }}>
                    {(selectedJobDetailModal.requirements && selectedJobDetailModal.requirements.trim()) || "• Bachelor's or Master's degree in related field\n• Relevant industry experience\n• Strong communication skills"}
                  </div>
                </div>

                {/* Divider */}
                <div style={{ height: '1px', background: '#f1f5f9', margin: '0 28px' }} />

                {/* Skills & Competencies */}
                <div style={{ padding: '24px 28px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                    <div style={{ width: '32px', height: '32px', background: 'linear-gradient(135deg,#fef3c7,#fde68a)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Zap size={16} color="#b45309" />
                    </div>
                    <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: '700', color: '#0f172a' }}>Skills & Competencies</h3>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    <div>
                      <p style={{ margin: '0 0 10px 0', fontSize: '0.75rem', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.6px' }}>Must Have</p>
                      {(() => {
                        const skills = selectedJobDetailModal.must_have_skills;
                        if (!skills || skills.length === 0) return <span style={{ color: '#94a3b8', fontSize: '0.875rem' }}>Not specified</span>;
                        
                        if (typeof skills[0] === 'string') {
                          return (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                              {skills.map((skill, idx) => (
                                <span key={`must-${idx}`} style={{ padding: '6px 14px', background: 'linear-gradient(135deg,#e0e7ff,#ede9fe)', color: '#4338ca', borderRadius: '6px', fontSize: '0.85rem', fontWeight: '600', border: '1px solid #c7d2fe' }}>
                                  {skill}
                                </span>
                              ))}
                            </div>
                          );
                        }

                        const grouped = skills.reduce((acc, curr) => {
                          const cat = curr.categoryLabel || curr.category || 'Other Skills';
                          if (!acc[cat]) acc[cat] = [];
                          acc[cat].push(curr.skill);
                          return acc;
                        }, {});

                        return (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%' }}>
                            {Object.entries(grouped).map(([cat, items]) => (
                              <div key={cat}>
                                <div style={{ fontSize: '0.75rem', fontWeight: '700', color: '#64748b', marginBottom: '6px' }}>{cat}</div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                  {items.map((skill, idx) => (
                                    <span key={`must-${cat}-${idx}`} style={{ padding: '6px 14px', background: 'linear-gradient(135deg,#e0e7ff,#ede9fe)', color: '#4338ca', borderRadius: '6px', fontSize: '0.85rem', fontWeight: '600', border: '1px solid #c7d2fe' }}>
                                      {skill}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                    <div>
                      <p style={{ margin: '0 0 10px 0', fontSize: '0.75rem', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.6px' }}>Good to Have</p>
                      {(() => {
                        const skills = selectedJobDetailModal.good_to_have_skills;
                        if (!skills || skills.length === 0) return <span style={{ color: '#94a3b8', fontSize: '0.875rem' }}>Not specified</span>;
                        
                        if (typeof skills[0] === 'string') {
                          return (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                              {skills.map((skill, idx) => (
                                <span key={`good-${idx}`} style={{ padding: '6px 14px', background: '#f8fafc', color: '#475569', borderRadius: '6px', fontSize: '0.85rem', fontWeight: '600', border: '1px solid #e2e8f0' }}>
                                  {skill}
                                </span>
                              ))}
                            </div>
                          );
                        }

                        const grouped = skills.reduce((acc, curr) => {
                          const cat = curr.categoryLabel || curr.category || 'Other Skills';
                          if (!acc[cat]) acc[cat] = [];
                          acc[cat].push(curr.skill);
                          return acc;
                        }, {});

                        return (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%' }}>
                            {Object.entries(grouped).map(([cat, items]) => (
                              <div key={cat}>
                                <div style={{ fontSize: '0.75rem', fontWeight: '700', color: '#64748b', marginBottom: '6px' }}>{cat}</div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                  {items.map((skill, idx) => (
                                    <span key={`good-${cat}-${idx}`} style={{ padding: '6px 14px', background: '#f8fafc', color: '#475569', borderRadius: '6px', fontSize: '0.85rem', fontWeight: '600', border: '1px solid #e2e8f0' }}>
                                      {skill}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>

              </div>

              <div style={{ height: '24px' }} />
            </div>
          </div>
        )}

          {/* Skill Gap Analysis Full Page View */}
          {showSkillGapAnalysis && selectedJobDetailModal && !skillGapError && (
            <div style={{ flex: 1, background: '#f8fafc', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', padding: '16px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                      <div style={{ background: 'linear-gradient(135deg, #0ea5e9, #3b82f6)', color: 'white', padding: '8px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Activity size={24} />
                      </div>
                      <div>
                        <h2 style={{ margin: 0, fontSize: '1.4rem', fontWeight: '800', color: '#0f172a', letterSpacing: '-0.5px' }}>
                          Skill Gap Analysis
                        </h2>
                        <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#64748b' }}>Detailed AI fitment report for {selectedJobDetailModal.job_name}</p>
                      </div>
                    </div>
                  <button
                    onClick={() => {
                      setShowSkillGapAnalysis(false);
                      if (selectedJobDetailModal?.isCustom) {
                        setSelectedJobDetailModal(null);
                      }
                    }}
                    style={{ background: '#f1f5f9', border: 'none', color: '#64748b', width: '44px', height: '44px', borderRadius: '50%', fontSize: '1.2rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}
                    onMouseOver={e => { e.currentTarget.style.background = '#e2e8f0'; e.currentTarget.style.color = '#0f172a'; }}
                    onMouseOut={e => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = '#64748b'; }}
                  >
                    ✕
                  </button>
                </div>
                <div style={{ flex: 1, padding: '32px 48px', overflowY: 'auto', display: 'flex', flexDirection: 'column', maxWidth: '1600px', margin: '0 auto', width: '100%' }}>
                  <div style={{ display: 'flex', gap: '24px', marginBottom: '24px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#1e293b', fontWeight: '500' }}>
                      <div style={{ width: '12px', height: '12px', background: '#3b82f6', borderRadius: '50%' }}></div>
                      Your Skills
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#1e293b', fontWeight: '500' }}>
                      <div style={{ width: '12px', height: '12px', background: '#f97316', borderRadius: '50%' }}></div>
                      {selectedJobDetailModal.job_name || 'Job Role'}
                    </div>
                  </div>

                  <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0', marginBottom: '32px' }}>
                    <button 
                      onClick={() => setSkillGapTab('tools')}
                      style={{ flex: 1, padding: '16px', background: 'none', border: 'none', borderBottom: skillGapTab === 'tools' ? '3px solid #f97316' : '3px solid transparent', fontWeight: 600, color: skillGapTab === 'tools' ? '#1e293b' : '#64748b', cursor: 'pointer', transition: 'all 0.2s' }}
                    >
                      Tools
                    </button>
                    <button 
                      onClick={() => setSkillGapTab('skills')}
                      style={{ flex: 1, padding: '16px', background: 'none', border: 'none', borderBottom: skillGapTab === 'skills' ? '3px solid #f97316' : '3px solid transparent', fontWeight: 600, color: skillGapTab === 'skills' ? '#1e293b' : '#64748b', cursor: 'pointer', transition: 'all 0.2s' }}
                    >
                      Skills
                    </button>
                  </div>

                  {(() => {
                    const studentSkills = (profile?.skills_list || profile?.skills || []).map(s => typeof s === 'string' ? s.toLowerCase() : (s.name || '').toLowerCase());
                    let toolsRaw = classifiedSkillsData?.tools || [];
                    let skillsRaw = classifiedSkillsData?.skills || [];
                    
                    if (!classifiedSkillsData || (toolsRaw.length === 0 && skillsRaw.length === 0)) {
                        const reqs = selectedJobDetailModal.requirements || selectedJobDetailModal.required_skills || '';
                        const parsedReqs = reqs ? String(reqs).split(',') : [];
                        
                        const jobSkills = [
                          ...(Array.isArray(selectedJobDetailModal.must_have_skills) ? selectedJobDetailModal.must_have_skills : (selectedJobDetailModal.must_have_skills ? String(selectedJobDetailModal.must_have_skills).split(',') : [])),
                          ...(Array.isArray(selectedJobDetailModal.good_to_have_skills) ? selectedJobDetailModal.good_to_have_skills : (selectedJobDetailModal.good_to_have_skills ? String(selectedJobDetailModal.good_to_have_skills).split(',') : [])),
                          ...parsedReqs
                        ].map(s => typeof s === 'object' && s !== null ? s.skill : s)
                         .map(s => String(s).trim())
                         .filter(Boolean);
                         
                        // Remove duplicates
                        const uniqueJobSkills = [...new Set(jobSkills)];
                        
                        toolsRaw = uniqueJobSkills.slice(0, Math.ceil(uniqueJobSkills.length / 2)).map(s => ({name: s, required_score: 8}));
                        skillsRaw = uniqueJobSkills.slice(Math.ceil(uniqueJobSkills.length / 2)).map(s => ({name: s, required_score: 8}));
                    }

                    const toolsData = toolsRaw;
                    const skillsData = skillsRaw;

                    let knowledgeScores = [];
                    try {
                      knowledgeScores = profile?.ai_knowledge_scores || [];
                      if (typeof knowledgeScores === 'string') {
                          knowledgeScores = JSON.parse(knowledgeScores);
                      }
                      if (!Array.isArray(knowledgeScores)) knowledgeScores = [];
                    } catch (e) {
                      knowledgeScores = [];
                    }

                    let calculatedFitmentScore = classifiedSkillsData?.fitment_score || selectedJobDetailModal.fitment_score;
                    if (!calculatedFitmentScore) {
                        let totalMatched = 0;
                        let totalSkills = toolsData.length + skillsData.length;
                        if (totalSkills > 0) {
                            [...toolsData, ...skillsData].forEach(skillObj => {
                                const skillName = typeof skillObj === 'string' ? skillObj : (skillObj.name || 'Unknown');
                                const lowerSkill = skillName.toLowerCase();
                                const hasSkill = studentSkills.some(ss => ss.includes(lowerSkill) || lowerSkill.includes(ss));
                                if (hasSkill) totalMatched++;
                            });
                            calculatedFitmentScore = (totalMatched / totalSkills) * 100;
                        } else {
                            calculatedFitmentScore = 0;
                        }
                    }

                    const fitmentScore = calculatedFitmentScore || 0;

                    const mapToData = (skillsArrayRaw) => skillsArrayRaw.map((skillObj) => {
                      const skillName = typeof skillObj === 'string' ? skillObj : (skillObj.name || 'Unknown');
                      const aiReqScore = (typeof skillObj === 'object' && skillObj.required_score) ? parseFloat(skillObj.required_score) : 8;
                      const aiStudentScore = (typeof skillObj === 'object' && skillObj.student_score !== undefined) ? parseFloat(skillObj.student_score) : null;
                      const lowerSkill = skillName.toLowerCase();
                      
                      const matchedScoreObj = knowledgeScores.find(k => k.name && (k.name.toLowerCase().includes(lowerSkill) || lowerSkill.includes(k.name.toLowerCase())));
                      
                      let score = 0;
                      if (aiStudentScore !== null) {
                         score = aiStudentScore;
                      } else if (matchedScoreObj && !isNaN(parseFloat(matchedScoreObj.score))) {
                         score = parseFloat(matchedScoreObj.score);
                      } else {
                         const hasSkill = studentSkills.some(ss => ss.includes(lowerSkill) || lowerSkill.includes(ss));
                         if (hasSkill) {
                             score = Math.min(10, Math.max(aiReqScore, 8));
                         } else {
                             score = Math.min(aiReqScore - 1, 4);
                         }
                      }

                      // Extract AI provenance fields
                      const isGoodScore = score >= (aiReqScore - 0.5);
                      const rawSourceType = typeof skillObj === 'object' ? skillObj.source_type : null;
                      const rawProjectName = typeof skillObj === 'object' ? skillObj.project_name : null;
                      const rawUrl = typeof skillObj === 'object' ? skillObj.project_url : null;
                      const rawExpl = typeof skillObj === 'object' ? skillObj.explanation : null;

                      let finalSourceType = rawSourceType;
                      if (!finalSourceType) {
                        finalSourceType = isGoodScore ? 'project' : 'missing';
                      }

                      let finalProjectName = rawProjectName;
                      if (!finalProjectName || finalProjectName === 'string') {
                        finalProjectName = isGoodScore ? 'Verified in Technical Portfolio' : 'Not detected in projects';
                      }

                      let finalExplanation = rawExpl;
                      if (!finalExplanation || finalExplanation === 'string') {
                        finalExplanation = isGoodScore
                          ? `Evidence for ${skillName} was verified from your projects and technical background.`
                          : `No project or resume evidence found yet for ${skillName}. Building a hands-on project with ${skillName} will help bridge this gap.`;
                      }
                      
                      return { 
                        subject: skillName, 
                        A: parseFloat(score.toFixed(1)), 
                        B: parseFloat(aiReqScore.toFixed(1)), 
                        fullMark: 10,
                        source_type: finalSourceType,
                        project_name: finalProjectName,
                        project_url: rawUrl,
                        explanation: finalExplanation
                      };
                    });

                    const dataTools = mapToData(toolsData);
                    const dataSkills = mapToData(skillsData);
                    
                    const activeData = skillGapTab === 'tools' ? dataTools : dataSkills;
                    
                    const isOverallMatch = fitmentScore >= 60;
                    const fitmentText = isOverallMatch ? 'Right Match' : 'Not a Right Match';

                    const greatAt = activeData.filter(d => d.A >= d.B - 0.5);
                    const toImprove = activeData.filter(d => d.A < d.B - 0.5);

                    const toggleSkillExpand = (name) => {
                      setExpandedSkillCards(prev => ({
                        ...prev,
                        [name]: !prev[name]
                      }));
                    };

                    const renderSkillCard = (item, isGreat) => {
                      const isExpanded = !!expandedSkillCards[item.subject];
                      const isMissing = item.source_type === 'missing' || item.A < item.B - 0.5;

                      let badgeIcon = <Folder size={11} />;
                      let badgeText = item.project_name;
                      let badgeColor = isGreat ? '#0284c7' : '#b45309';
                      let badgeBg = isGreat ? '#f0f9ff' : '#fffbeb';
                      let badgeBorder = isGreat ? '#bae6fd' : '#fde68a';

                      if (item.source_type === 'resume') {
                        badgeIcon = <FileText size={11} />;
                        badgeText = item.project_name || 'Resume Experience';
                      } else if (item.source_type === 'both') {
                        badgeIcon = <Sparkles size={11} />;
                        badgeText = `${item.project_name} & Resume`;
                      } else if (isMissing || item.source_type === 'missing') {
                        badgeIcon = <AlertTriangle size={11} />;
                        badgeText = item.project_name && item.project_name !== 'Not detected in projects' ? item.project_name : 'Missing in Projects / Resume';
                        badgeColor = '#b91c1c';
                        badgeBg = '#fef2f2';
                        badgeBorder = '#fecaca';
                      }

                      return (
                        <div 
                          key={item.subject} 
                          style={{ 
                            border: '1px solid #e2e8f0', 
                            borderRadius: '10px', 
                            background: isExpanded ? '#fafafa' : '#ffffff', 
                            transition: 'all 0.2s ease',
                            boxShadow: isExpanded ? '0 4px 12px rgba(0,0,0,0.05)' : '0 1px 3px rgba(0,0,0,0.02)',
                            overflow: 'hidden'
                          }}
                        >
                          <div 
                            onClick={() => toggleSkillExpand(item.subject)}
                            style={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'flex-start', 
                              padding: '12px 14px', 
                              cursor: 'pointer',
                              userSelect: 'none'
                            }}
                          >
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth: 0, paddingRight: '12px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                                <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: isGreat ? '#3b82f6' : '#ef4444', flexShrink: 0 }}></div>
                                <span title={item.subject} style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.subject}</span>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '2px', flexWrap: 'wrap' }}>
                                <span 
                                  style={{ 
                                    display: 'inline-flex', 
                                    alignItems: 'center', 
                                    gap: '4px', 
                                    fontSize: '11px', 
                                    fontWeight: '500', 
                                    color: badgeColor, 
                                    background: badgeBg, 
                                    border: `1px solid ${badgeBorder}`, 
                                    padding: '2px 8px', 
                                    borderRadius: '4px',
                                    maxWidth: '100%',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}
                                  title={badgeText}
                                >
                                  {badgeIcon}
                                  <span>{badgeText}</span>
                                </span>
                              </div>

                              {/* Scores sit on their own row under the name + evidence badge,
                                  so the skill name always gets the full card width. */}
                              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '2px 6px', marginTop: '6px', alignSelf: 'flex-start', maxWidth: '100%', background: isGreat ? '#e0f2fe' : '#f1f5f9', padding: '5px 12px', borderRadius: '14px', fontSize: '12.5px', fontWeight: '700', color: isGreat ? '#0369a1' : '#475569' }}>
                                <span>Your Skill: {item.A}</span>
                                <span style={{ opacity: 0.5 }}>|</span>
                                <span>Job Requires: {item.B}</span>
                              </div>
                            </div>

                            <div style={{ color: '#94a3b8', display: 'flex', alignItems: 'center', flexShrink: 0, paddingTop: '2px' }}>
                              {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                            </div>
                          </div>

                          {isExpanded && (
                            <div style={{ 
                              padding: '12px 14px 14px', 
                              borderTop: '1px dashed #e2e8f0', 
                              background: '#f8fafc',
                              fontSize: '12.5px',
                              color: '#334155',
                              lineHeight: '1.5'
                            }}>
                              <div style={{ marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '600', color: '#0f172a' }}>
                                <Info size={14} color="#6366f1" />
                                <span>AI Origin Analysis & Explanation:</span>
                              </div>
                              <p style={{ margin: '0 0 8px 0', color: '#475569' }}>
                                {item.explanation}
                              </p>

                              {item.project_url && (
                                <div style={{ marginTop: '8px' }}>
                                  <a 
                                    href={item.project_url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    style={{ 
                                      display: 'inline-flex', 
                                      alignItems: 'center', 
                                      gap: '5px', 
                                      fontSize: '11.5px', 
                                      color: '#2563eb', 
                                      fontWeight: '600',
                                      textDecoration: 'none' 
                                    }}
                                    onClick={e => e.stopPropagation()}
                                  >
                                    <Github size={13} />
                                    <span>View GitHub Repository</span>
                                    <ExternalLink size={11} />
                                  </a>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    };

                    const renderBarChartTick = (props) => {
                      const { payload, x, y } = props;
                      const dataItem = activeData.find(d => d.subject === payload.value);
                      const isMatch = dataItem && dataItem.A >= dataItem.B - 0.5;

                      return (
                        <g transform={`translate(${x},${y})`}>
                          <text x={-10} y={0} dy={4} textAnchor="end" fill="#64748b" fontSize={12}>{payload.value}</text>
                          {!isMatch && <text x={-10} y={16} dy={4} textAnchor="end" fill="#ef4444" fontSize={10}>Not a Right Match</text>}
                        </g>
                      );
                    };

                    return (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '32px', width: '100%', height: '100%', background: '#fff' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '48px', width: '100%' }}>
                        {/* Left Column: Radar Chart */}
                        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', paddingTop: '32px', position: 'relative', borderRight: '1px solid #e2e8f0', paddingRight: '24px' }}>
                          <div style={{ position: 'relative', minHeight: '500px', height: `${Math.max(500, activeData.length * 40)}px`, width: '100%', background: '#fff' }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart layout="vertical" data={activeData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }} barGap={2} barSize={12}>
                                <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} stroke="#e2e8f0" />
                                <XAxis type="number" domain={[0, 10]} tick={{ fill: '#94a3b8', fontSize: 12 }} />
                                <YAxis dataKey="subject" type="category" tick={renderBarChartTick} width={260} />
                                <Tooltip cursor={{fill: '#f1f5f9'}} contentStyle={{borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'}} />
                                <Legend wrapperStyle={{ paddingTop: '20px' }} />
                                <Bar name="Job Requirement" dataKey="B" fill="#f97316" radius={[0, 4, 4, 0]} />
                                <Bar name="Your Skills" dataKey="A" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        </div>

                        {/* Right Column: Breakdown */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '32px', paddingTop: '32px', overflowY: 'auto', paddingRight: '8px' }}>
                          <h3 style={{ margin: 0, fontSize: '20px', color: '#1e293b' }}>
                            Fitment Score: {fitmentScore.toFixed(2)}% - {fitmentText}
                          </h3>
                          
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '48px' }}>
                             {/* You're Great At Column */}
                             <div>
                               <h4 style={{ margin: '0 0 24px 0', fontSize: '16px', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                 You're Great at <Info size={16} color="#94a3b8" />
                               </h4>
                               {greatAt.length === 0 ? <p style={{ color: '#94a3b8', fontSize: '14px', margin: 0 }}>No skills found</p> : (
                                 <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                   {greatAt.map(item => renderSkillCard(item, true))}
                                 </div>
                               )}
                             </div>

                             {/* Rooms to improve Column */}
                             <div>
                               <h4 style={{ margin: '0 0 24px 0', fontSize: '16px', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                 Rooms to improve <Info size={16} color="#94a3b8" />
                               </h4>
                               {toImprove.length === 0 ? <p style={{ color: '#94a3b8', fontSize: '14px', margin: 0 }}>No skills found</p> : (
                                 <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                   {toImprove.map(item => renderSkillCard(item, false))}
                                 </div>
                               )}
                             </div>
                          </div>
                          </div>
                        </div>

                        {/* AI Recommendations */}
                        {classifiedSkillsData?.recommendations && classifiedSkillsData.recommendations.length > 0 && (
                          <div style={{ 
                            padding: '32px', 
                            background: 'linear-gradient(145deg, rgba(79, 70, 229, 0.03) 0%, rgba(147, 51, 234, 0.03) 100%)', 
                            borderRadius: '0', 
                            border: '1px solid rgba(99, 102, 241, 0.15)',
                            boxShadow: '0 10px 40px -10px rgba(79, 70, 229, 0.08)',
                            position: 'relative',
                            overflow: 'hidden'
                          }}>
                            {/* Decorative background element */}
                            <div style={{ position: 'absolute', top: '-40px', right: '-40px', width: '200px', height: '200px', background: 'radial-gradient(circle, rgba(147,51,234,0.1) 0%, rgba(255,255,255,0) 70%)', borderRadius: '50%' }}></div>
                            
                            <h4 style={{ margin: '0 0 24px 0', fontSize: '18px', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '12px', fontWeight: '700', letterSpacing: '-0.3px', position: 'relative', zIndex: 1 }}>
                              <div style={{ background: 'linear-gradient(135deg, #6366f1, #a855f7)', padding: '10px', borderRadius: '0', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)' }}>
                                <Sparkles size={18} color="#ffffff" />
                              </div>
                              Personalized AI Learning Recommendations
                            </h4>
                            <ul style={{ margin: 0, paddingLeft: '0', listStyleType: 'none', color: '#334155', fontSize: '15px', lineHeight: '1.6', fontWeight: '500', display: 'flex', flexDirection: 'column', gap: '12px', position: 'relative', zIndex: 1 }}>
                              {classifiedSkillsData.recommendations.map((rec, i) => (
                                <li key={i} style={{ display: 'flex', gap: '14px', alignItems: 'flex-start', background: 'rgba(255, 255, 255, 0.8)', padding: '18px 24px', borderRadius: '0', border: '1px solid rgba(226, 232, 240, 0.8)', boxShadow: '0 2px 10px rgba(15, 23, 42, 0.03)' }}>
                                  <div style={{ color: '#8b5cf6', marginTop: '2px', background: 'rgba(139, 92, 246, 0.1)', padding: '4px', borderRadius: '0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Target size={16} strokeWidth={2.5} />
                                  </div>
                                  <div style={{ paddingTop: '2px' }}>{rec}</div>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>
          )}

          {skillGapError && (
            <div style={{ flex: 1, background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '32px' }}>
              <div role="alert" style={{ width: 'min(620px, 100%)', background: '#fff', border: '1px solid #fecaca', borderRadius: '16px', padding: '32px', textAlign: 'center', boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)' }}>
                <AlertTriangle size={42} color="#dc2626" style={{ marginBottom: '12px' }} />
                <h2 style={{ margin: '0 0 12px', color: '#991b1b', fontSize: '20px' }}>Analysis unavailable</h2>
                <p style={{ margin: '0 0 24px', color: '#475569', lineHeight: 1.6 }}>{skillGapError}</p>
                <button
                  onClick={() => {
                    setSkillGapError(null);
                    setSelectedJobDetailModal(null);
                  }}
                  style={{ background: '#0f766e', color: '#fff', border: 'none', borderRadius: '8px', padding: '11px 22px', fontWeight: 700, cursor: 'pointer' }}
                >
                  Close
                </button>
              </div>
            </div>
          )}

      </div>
    );
  };

  const renderCompletedSessions = () => {
    const totalCompleted = trulyCompletedPublic.length + trulyCompletedPersonal.length;
    const totalRefunded = refundedPublic.length + refundedPersonal.length;
    const totalAll = totalCompleted + totalRefunded;

    return (
      <div className="stu-fade-in">
        <header className="stu-top-bar">
          <h1>Completed Sessions</h1>
          <span style={{ fontSize: '0.875rem', color: 'var(--text-muted)', fontWeight: 500 }}>
            {totalAll} session{totalAll !== 1 ? 's' : ''} in your history
          </span>
        </header>

        {totalAll === 0 ? (
          <div className="stu-empty-state stu-padded" style={{ marginTop: '0', textAlign: 'center' }}>
            <CheckCircle size={48} style={{ color: 'var(--border)', marginBottom: '16px' }} />
            <h3 style={{ color: 'var(--text-muted)', marginBottom: '8px' }}>No session history yet</h3>
            <p style={{ color: 'var(--text-faint)', fontSize: '14px' }}>Sessions you complete will appear here.</p>
          </div>
        ) : (
          <div className="stu-padded">
            {/* TRULY COMPLETED SECTION */}
            {(trulyCompletedPersonal.length > 0 || trulyCompletedPublic.length > 0) && (
              <section className="stu-sessions-container" style={{ marginTop: '24px' }}>
                <div className="stu-sessions-header">
                  <h3>
                    <CheckCircle size={20} style={{ verticalAlign: 'middle', marginRight: '8px', color: '#10b981' }} />
                    Completed Sessions
                  </h3>
                </div>

                <div className="stu-sessions-grid">
                  {/* Personal */}
                  {trulyCompletedPersonal.map(req => (
                    <div key={`comp-req-${req.id}`} className="stu-session-card" style={{ borderLeft: '4px solid #10b981' }}>
                      <div className="stu-card-top">
                        <span className={`stu-type-badge ${req.session_type === 'GROUP' ? 'group-bg' : 'indiv-bg'}`}>
                          {req.session_type === 'GROUP' ? <Users size={12} /> : <User size={12} />} {req.session_type}
                        </span>
                        <span style={{ background: '#dcfce7', color: '#15803d', padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 600 }}>
                          Completed
                        </span>
                      </div>
                      <h4 className="stu-session-title">{req.title}</h4>
                      <p className="stu-prof-name">With Prof. {req.professor_name}</p>
                      <div className="stu-session-details">
                        <div className="stu-detail-line"><Calendar size={14} /> {new Date(req.event_date.split('T')[0]).toLocaleDateString('en-GB')}</div>
                        <div className="stu-detail-line"><Clock size={14} /> {req.start_time}</div>
                      </div>
                      <div style={{ marginTop: '10px', fontSize: '12px', background: 'var(--engine-black)', padding: '10px', borderRadius: '8px', border: '1px solid var(--border)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                          <span style={{ color: 'var(--text-muted)' }}>Amount Paid:</span>
                          <span style={{ fontWeight: 600 }}>{req.proposed_amount || req.price ? `₹${(req.proposed_amount || req.price).toLocaleString()}` : 'Free'}</span>
                        </div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '10px' }}>
                        <button
                          onClick={() => handleGiveFeedback(req.id, req.type || 'REQUEST')}
                          className="stu-action-btn secondary"
                        >
                          <MessageSquare size={14} /> Give Feedback
                        </button>
                        <SubFeatureGate feature="certificates" sub="certificates_download">
                        <button
                          onClick={() => handleDownloadCertificate(req.id, req.session_type || 'REQUEST')}
                          className="stu-action-btn success"
                        >
                          <Award size={14} /> Download Certificate
                        </button>
                        </SubFeatureGate>
                      </div>
                    </div>
                  ))}

                  {/* Public */}
                  {trulyCompletedPublic.map(session => (
                    <div key={`comp-pub-${session.id}`} className="stu-session-card" style={{ borderLeft: '4px solid #10b981' }}>
                      <div className="stu-card-top">
                        <span className="stu-type-badge">{session.domain}</span>
                        <span style={{ background: '#dcfce7', color: '#15803d', padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 600 }}>
                          Completed
                        </span>
                      </div>
                      <h4 className="stu-session-title">{session.title}</h4>
                      <p className="stu-prof-name">Prof. {session.professor_name}</p>
                      {renderSessionSchedule(session)}
                      {session.university_name && (
                        <div style={{ marginTop: '6px', fontSize: '11px', color: '#6366f1', fontWeight: 500 }}>
                          <Shield size={12} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                          {session.university_name}
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                        <button
                          onClick={() => handleGiveFeedback(session.id, 'PUBLIC')}
                          className="stu-action-btn secondary"
                          style={{ flex: 1 }}
                        >
                          <MessageSquare size={14} /> Feedback
                        </button>
                        <SubFeatureGate feature="certificates" sub="certificates_download">
                        <button
                          onClick={() => handleDownloadCertificate(session.id, 'PUBLIC')}
                          className="stu-action-btn success"
                          style={{ flex: 1 }}
                        >
                          <Award size={14} /> Certificate
                        </button>
                        </SubFeatureGate>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* REFUNDED / CANCELLED SECTION */}
            {(refundedPersonal.length > 0 || refundedPublic.length > 0) && (
              <section className="stu-sessions-container" style={{ marginTop: '40px' }}>
                <div className="stu-sessions-header">
                  <h3>
                    <RotateCcw size={20} style={{ verticalAlign: 'middle', marginRight: '8px', color: '#ef4444' }} />
                    Refunded & Cancelled Sessions
                  </h3>
                </div>

                <div className="stu-sessions-grid">
                  {/* Personal Refunded */}
                  {refundedPersonal.map(req => (
                    <div key={`ref-req-${req.id}`} className="stu-session-card" style={{ borderLeft: '4px solid #f87171', background: '#fffafa' }}>
                      <div className="stu-card-top">
                        <span className="stu-type-badge">{req.session_type}</span>
                        {req.payment_status === 'REFUND_REQUESTED' ? (
                          <span style={{ background: '#fff7ed', color: '#c2410c', padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 600, border: '1px solid #ffedd5' }}>
                            Refund Requested
                          </span>
                        ) : (
                          <span style={{ background: '#fee2e2', color: '#991b1b', padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 600, border: '1px solid #fecaca' }}>
                            Refunded
                          </span>
                        )}
                      </div>
                      <h4 className="stu-session-title">{req.title}</h4>
                      <p className="stu-prof-name">Prof. {req.professor_name}</p>
                      <div className="stu-session-details">
                        <div className="stu-detail-line"><Calendar size={14} /> {new Date(req.event_date.split('T')[0]).toLocaleDateString('en-GB')}</div>
                      </div>
                      <div style={{ marginTop: '10px', fontSize: '12px', color: 'var(--text-muted)' }}>
                        Processing for: <strong>₹{(req.proposed_amount || req.price || 0).toLocaleString()}</strong>
                      </div>
                    </div>
                  ))}

                  {/* Public Refunded */}
                  {refundedPublic.map(session => (
                    <div key={`ref-pub-${session.id}`} className="stu-session-card" style={{ borderLeft: '4px solid #f87171', background: '#fffafa' }}>
                      <div className="stu-card-top">
                        <span className="stu-type-badge">{session.domain}</span>
                        {session.payment_status === 'REFUND_REQUESTED' ? (
                          <span style={{ background: '#fff7ed', color: '#c2410c', padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 600, border: '1px solid #ffedd5' }}>
                            Refund Requested
                          </span>
                        ) : (
                          <span style={{ background: '#fee2e2', color: '#991b1b', padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 600, border: '1px solid #fecaca' }}>
                            Refunded
                          </span>
                        )}
                      </div>
                      <h4 className="stu-session-title">{session.title}</h4>
                      <p className="stu-prof-name">Prof. {session.professor_name}</p>
                      <div className="stu-session-details">
                        <div className="stu-detail-line"><Calendar size={14} /> {new Date(session.event_date.split('T')[0]).toLocaleDateString('en-GB')}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderSessionSchedule = (sessionObj) => {
    window.renderSessionScheduleHelper = renderSessionSchedule;
    const isLongEngagement = ['ADJUNCT', 'FRACTIONAL', 'MODULAR'].includes(sessionObj.engagement_type);

    if (isLongEngagement && sessionObj.end_date) {
      return (
        <div className="stu-session-details" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
          <div className="stu-detail-line" style={{ width: '100%', marginBottom: '4px' }}>
            <Calendar size={14} style={{ minWidth: '14px', flexShrink: 0 }} />
            <span>{new Date(sessionObj.event_date.split('T')[0]).toLocaleDateString('en-GB')} - {new Date(sessionObj.end_date.split('T')[0]).toLocaleDateString('en-GB')}</span>
          </div>
          {(sessionObj.days_of_week?.length > 0 || sessionObj.start_time) && (
            <div className="stu-detail-line" style={{ width: '100%', marginBottom: '4px' }}>
              <Clock size={14} style={{ minWidth: '14px', flexShrink: 0 }} />
              <span>
                {sessionObj.days_of_week?.length > 0 ? `${sessionObj.days_of_week.join(', ')} ` : ''}
                {sessionObj.start_time ? `(${sessionObj.start_time}${sessionObj.end_time ? ` to ${sessionObj.end_time}` : ''})` : ''}
              </span>
            </div>
          )}
          {sessionObj.weekly_hours > 0 && sessionObj.engagement_type !== 'ADJUNCT' && (
            <div className="stu-detail-line" style={{ width: '100%' }}>
              <Clock size={14} style={{ minWidth: '14px', flexShrink: 0 }} />
              <span>{sessionObj.weekly_hours} hrs/week</span>
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="stu-session-details">
        <div className="stu-detail-line"><Calendar size={14} /> {new Date(sessionObj.event_date.split('T')[0]).toLocaleDateString('en-GB')}</div>
        <div className="stu-detail-line"><Clock size={14} /> {sessionObj.start_time} ({sessionObj.duration_value} {sessionObj.duration_unit})</div>
      </div>
    );
  };

  return (
    <div className="stu-dashboard-wrapper">
      {/* AI Disclaimer Modal — shown once per session before any AI feature */}
      {showAiDisclaimer && (
        <AiDisclaimerCard
          featureName={aiDisclaimerFeature}
          onAgree={() => {
            sessionStorage.setItem('ai_disclaimer_agreed', '1');
            setShowAiDisclaimer(false);
            if (pendingAiAction) {
              pendingAiAction();
              setPendingAiAction(null);
            }
          }}
          onCancel={() => {
            setShowAiDisclaimer(false);
            setPendingAiAction(null);
          }}
        />
      )}
      <StudentSidebar
        aiAgentEnabled={profile?.ai_agent_enabled !== false}
        activeTab={activeTab}
        onScheduleClick={() => setActiveTab('sessions')}
        onDashboardClick={() => setActiveTab('explore')}
        onSettingsClick={() => setActiveTab('settings')}
        onProfileClick={() => setActiveTab('settings')}
        onDiscoverClick={() => setActiveTab('discover')}
        onCompletedClick={() => setActiveTab('completed')}
        onLMSClick={() => setActiveTab('lms')}
        onPartnerCoursesClick={() => setActiveTab('partner_courses')}
        onDiscussionsClick={() => setActiveTab('discussions')}
        onChatClick={() => setActiveTab('chat')}
        onJobsClick={() => {
          setActiveTab('jobs');
          setCampusJobView('campus');
          fetchInternalJobs();
          fetchJobs();
          if (jobs.length > 0) {
            localStorage.setItem('seen_student_jobs', JSON.stringify(jobs.map(j => j.drive_id)));
            setHasNewJobs(false);
          }
        }}

        onPlatformFeedbackClick={() => setActiveTab('platform_feedback')}
        unreadCount={unreadMessagesCount}
        hasNewJobs={hasNewJobs}
      />

      <main className="stu-main-content">
        {activeTab === 'platform_feedback' ? (
          <FeatureGate feature="platform_feedback">
            <SubFeatureGate feature="platform_feedback" sub="platform_feedback_view">
              <PlatformFeedback />
            </SubFeatureGate>
          </FeatureGate>
        ) :
          activeTab === 'discussions' ? <DiscussionsPage embedded={true} /> :
          activeTab === 'chat' ? (
            <FeatureGate feature="messages">
              <SubFeatureGate feature="messages" sub="messages_view">
                <ChatPage embedded={true} />
              </SubFeatureGate>
            </FeatureGate>
          ) :
            (activeTab === 'settings' || activeTab === 'profile') ? renderMySettings() :
              activeTab === 'discover' ? <DiscoverProfessors /> :
                activeTab === 'lms' ? <StudentPartnerCourses /> :
                    activeTab === 'completed' ? renderCompletedSessions() :
                      activeTab === 'jobs' ? (
                        <FeatureGate feature="jobs">
                          <SubFeatureGate feature="jobs" sub="jobs_view">
                            {renderJobsTab()}
                          </SubFeatureGate>
                        </FeatureGate>
                      ) :
                        activeTab === 'explore' ? (
                        <StudentAnalyticsDashboard
                          profile={profile}
                          onExploreSessionsClick={() => setActiveTab('discover')}
                          onLMSClick={() => setActiveTab('lms')}
                        />
                      ) : activeTab === 'sessions' ? (
                        <FeatureGate feature="schedule">
                          <SubFeatureGate feature="schedule" sub="schedule_view">
                            <StudentRealTimeCalendar
                              userId={profile?.user_id || profile?.id}
                              profile={profile}
                            />
                          </SubFeatureGate>
                        </FeatureGate>
                      ) : (
                        <>
                          <header className="stu-top-bar" id="student-header">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                              <h1>{activeTab === 'explore' ? `Welcome back, ${profile?.full_name ? profile.full_name.split(' ')[0] : 'Student'}! 👋` : 'My Schedule 📅'}</h1>
                              {activeTab === 'sessions' && (
                                <button
                                  onClick={() => setShowCalendarModal(true)}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    padding: '6px 14px',
                                    background: '#eef2ff',
                                    color: '#4f46e5',
                                    border: '1px solid #c7d2fe',
                                    borderRadius: '8px',
                                    fontSize: '13px',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    marginTop: '4px'
                                  }}
                                >
                                  <Calendar size={14} /> Go to Calendar View
                                </button>
                              )}
                            </div>

                            <div className="stu-header-actions" style={{ display: 'flex', alignItems: 'center' }}>
                              <button
                                className="stu-notif-trigger"
                                onClick={() => setShowInfoModal(true)}
                                title="Dashboard Guide"
                                style={{ position: 'relative', marginRight: '16px', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--primary)' }}
                              >
                                <Info size={22} />
                              </button>
                              <div style={{ position: 'relative' }}>
                                <button
                                  className="stu-notif-trigger"
                                  onClick={() => setShowNotifs(!showNotifs)}
                                  title="Notifications"
                                  style={{ position: 'relative', marginRight: '8px' }}
                                >
                                  <Bell size={20} />
                                  {notifications.some(n => !n.is_read) && (
                                    <span style={{
                                      position: 'absolute', top: '8px', right: '8px',
                                      width: '8px', height: '8px', background: '#ef4444',
                                      borderRadius: '50%', border: '1px solid white'
                                    }}></span>
                                  )}
                                </button>
                                {showNotifs && (
                                  <div className="stu-card" style={{
                                    position: 'absolute', top: '50px', right: 0, width: '320px',
                                    zIndex: 100, padding: '16px', border: '1px solid var(--border)',
                                    boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)',
                                    background: 'var(--graphite-dark)'
                                  }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                      <h4 style={{ fontSize: '14px', fontWeight: 600, margin: 0 }}>Notifications</h4>
                                      <button onClick={() => setShowNotifs(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>
                                        <XCircle size={16} color="var(--text-muted)" />
                                      </button>
                                    </div>
                                    <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                      {notifications.length > 0 ? notifications.map(n => (
                                        <div key={n.id} style={{ padding: '10px', borderBottom: '1px solid var(--border)', fontSize: '13px' }}>
                                          <div style={{ fontWeight: 600, color: 'var(--proto-white)' }}>{n.title}</div>
                                          <div style={{ color: 'var(--text-muted)', marginTop: '2px' }}>{n.message}</div>
                                          <div style={{ fontSize: '11px', color: 'var(--text-faint)', marginTop: '4px' }}>{new Date(n.created_at).toLocaleString()}</div>
                                        </div>
                                      )) : <p style={{ fontSize: '13px', color: 'var(--text-faint)', textAlign: 'center', padding: '10px' }}>No new notifications</p>}
                                    </div>
                                  </div>
                                )}
                              </div>

                              <button
                                id="stu-monthly-cal-btn"
                                className={`stu-cal-trigger ${showCalendarModal ? 'active' : ''}`}
                                onClick={() => setShowCalendarModal(true)}
                                title="Open Monthly Calendar"
                              >
                                <Calendar size={20} />
                              </button>

                            </div>
                          </header>

                          <OnboardingTour user={profile} role="student" />

                          {/* RESTORED CALENDAR MODAL */}
                          {showCalendarModal && (
                            <div className="stu-modal-overlay" onClick={() => setShowCalendarModal(false)}>
                              <div className="stu-big-cal-modal" onClick={(e) => e.stopPropagation()}>
                                <div className="stu-modal-close-btn" onClick={() => setShowCalendarModal(false)}><X /></div>
                                <div className="stu-big-cal-layout">
                                  <div className="stu-cal-left-pane">
                                    <CalendarLib
                                      onChange={setSelectedDate}
                                      value={selectedDate}
                                      className="stu-main-calendar-ui"
                                      tileContent={({ date, view }) => {
                                        if (view !== 'month') return null;
                                        const status = getDaySessionStatus(date);
                                        return (
                                          <div className="stu-cal-dots-container">
                                            {status.hasMaster && <div className="stu-dot dot-master"></div>}
                                            {status.hasPersonal && <div className="stu-dot dot-personal"></div>}
                                          </div>
                                        );
                                      }}
                                    />
                                    <div className="stu-cal-legend">
                                      <div className="legend-item"><span className="dot-master"></span> Masterclass</div>
                                      <div className="legend-item"><span className="dot-personal"></span> Personal Session</div>
                                    </div>
                                  </div>
                                  <div className="stu-cal-right-pane">
                                    <h3>Agenda</h3>
                                    <p className="stu-agenda-date">{selectedDate.toLocaleDateString('en-GB', { dateStyle: 'full' })}</p>
                                    <div className="stu-agenda-list">
                                      {sessionsForSelectedDay.length > 0 ? (
                                        sessionsForSelectedDay.map((s, i) => (
                                          <div key={i} className={`stu-agenda-item border-${s.type.toLowerCase()}`}>
                                            <div className="stu-agenda-time">{s.time}</div>
                                            <div className="stu-agenda-info">
                                              <p>{s.title}</p>
                                              <span className={`tag-${s.type.toLowerCase()}`}>{s.type}</span>
                                            </div>
                                          </div>
                                        ))
                                      ) : (
                                        <div className="stu-agenda-empty">No events scheduled.</div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}

                          <div className="stu-dashboard-body">
                            <section className="stu-welcome-section">

                              <p>
                                {activeTab === 'explore'
                                  ? `Explore upcoming industry masterclasses and domains matching your career path.`
                                  : `You have ${filteredPublicSessions.length + myPersonalRequests.filter(r => r.status === 'ACCEPTED').length} confirmed sessions.`}
                              </p>
                            </section>

                            {/* 1. UNIVERSITY POSTS - PREFERRED DOMAIN */}
                            {universityPreferredSessions.length > 0 && (
                              <section className="stu-sessions-container">
                                <div className="stu-sessions-header">
                                  <h3>
                                    <Shield size={20} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                                    {activeTab === 'explore' ? 'University Sessions (Your Preferred Domains)' : 'Your University Sessions (Preferred Domains)'}
                                  </h3>
                                  <span style={{ fontSize: '0.875rem', color: 'var(--text-muted)', fontWeight: 500 }}>
                                    Sessions from your university
                                  </span>
                                </div>
                                <div className="stu-sessions-grid">
                                  {universityPreferredSessions.map(session => (
                                    <div key={`uni-pref-${session.id}`} className="stu-session-card" style={{ borderLeft: '4px solid #8b5cf6' }}>
                                      <div className="stu-card-top">
                                        <span className="stu-type-badge" style={{ background: '#dbeafe', color: '#1e40af' }}>{session.domain}</span>
                                        {(session.price === 0 || session.isFree) && (
                                          <span className="stu-type-badge" style={{ background: '#ecfdf5', color: '#059669', marginLeft: '6px' }}>FREE</span>
                                        )}
                                        <span className="participant-pill"><User size={12} /> {session.participant_count} Joined</span>
                                      </div>
                                      <h4 className="stu-session-title">{session.title}</h4>
                                      <p className="stu-prof-name">Prof. {session.professor_name}</p>
                                      {renderSessionSchedule(session)}
                                      {/* Payment & Status Logic for Public Sessions */}
                                      {(() => {
                                        const pStatus = session.payment_status;
                                        const isSuccess = pStatus === 'SUCCESS'; // Paid, waiting compliance
                                        const isConfirmed = pStatus === 'HELD' || pStatus === 'RELEASED' || pStatus === 'ENROLLED';

                                        if (isSuccess) {
                                          return (
                                            <>
                                              <div className="stu-session-active-card paid-confirm" style={{ marginTop: '10px' }}>
                                                <div className="active-badge" style={{ background: '#fef3c7', color: '#d97706' }}>
                                                  <Clock size={14} /> Payment Received
                                                </div>
                                                <div className="stu-status-note-v2" style={{ padding: '8px', background: 'var(--engine-black)', fontSize: '11px' }}>
                                                  ⌛ Awaiting Compliance...
                                                </div>
                                              </div>
                                              <button
                                                onClick={() => setShowDetails(session)}
                                                style={{
                                                  marginTop: '8px',
                                                  padding: '8px 12px',
                                                  background: 'transparent',
                                                  border: '1px solid var(--border)',
                                                  borderRadius: '8px',
                                                  color: 'var(--text-muted)',
                                                  fontSize: '12px',
                                                  fontWeight: 600,
                                                  cursor: 'pointer',
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'center',
                                                  gap: '6px',
                                                  width: '100%',
                                                  transition: 'all 0.2s'
                                                }}
                                                onMouseEnter={(e) => {
                                                  e.target.style.background = '#f8fafc';
                                                  e.target.style.borderColor = 'var(--text-faint)';
                                                }}
                                                onMouseLeave={(e) => {
                                                  e.target.style.background = 'transparent';
                                                  e.target.style.borderColor = 'var(--border)';
                                                }}
                                              >
                                                <Info size={14} /> View Details
                                              </button>
                                            </>
                                          );
                                        }

                                        // Requested - pending university approval
                                        if (pStatus === 'REQUESTED') {
                                          return (
                                            <>
                                              <div className="stu-session-active-card" style={{ marginTop: '10px' }}>
                                                <div className="active-badge" style={{ background: '#fef3c7', color: '#d97706' }}>
                                                  <Clock size={14} /> Requested University
                                                </div>
                                                <div className="stu-status-note-v2" style={{ padding: '8px', background: 'var(--engine-black)', fontSize: '11px', textAlign: 'center' }}>
                                                  Awaiting university approval...
                                                </div>
                                              </div>
                                              <button
                                                onClick={() => setShowDetails(session)}
                                                style={{
                                                  marginTop: '8px',
                                                  padding: '8px 12px',
                                                  background: 'transparent',
                                                  border: '1px solid var(--border)',
                                                  borderRadius: '8px',
                                                  color: 'var(--text-muted)',
                                                  fontSize: '12px',
                                                  fontWeight: 600,
                                                  cursor: 'pointer',
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'center',
                                                  gap: '6px',
                                                  width: '100%',
                                                  transition: 'all 0.2s'
                                                }}
                                                onMouseEnter={(e) => {
                                                  e.target.style.background = '#f8fafc';
                                                  e.target.style.borderColor = 'var(--text-faint)';
                                                }}
                                                onMouseLeave={(e) => {
                                                  e.target.style.background = 'transparent';
                                                  e.target.style.borderColor = 'var(--border)';
                                                }}
                                              >
                                                <Info size={14} /> View Details
                                              </button>
                                            </>
                                          );
                                        }

                                        if (isConfirmed) {
                                          const eventTime = new Date(`${session.event_date.split('T')[0]}T${session.start_time}`);
                                          const now = new Date();
                                          const joinTime = new Date(eventTime.getTime() - 10 * 60000); // 10 mins before
                                          const isJoinable = now >= joinTime;

                                          // If session is already marked complete (by student or otherwise), show completed state
                                          if (session.is_completed || pStatus === 'RELEASED') {
                                            return (
                                              <>
                                                <div className="stu-session-active-card" style={{ marginTop: '10px', textAlign: 'center' }}>
                                                  <div style={{ padding: '8px', color: '#10b981', fontWeight: 600, background: '#dcfce7', borderRadius: '6px', fontSize: '12px' }}>
                                                    ✅ Completed
                                                  </div>
                                                </div>
                                                <button
                                                  onClick={() => setShowDetails(session)}
                                                  style={{
                                                    marginTop: '8px',
                                                    padding: '8px 12px',
                                                    background: 'transparent',
                                                    border: '1px solid var(--border)',
                                                    borderRadius: '8px',
                                                    color: 'var(--text-muted)',
                                                    fontSize: '12px',
                                                    fontWeight: 600,
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: '6px',
                                                    width: '100%',
                                                    transition: 'all 0.2s'
                                                  }}
                                                  onMouseEnter={(e) => {
                                                    e.target.style.background = '#f8fafc';
                                                    e.target.style.borderColor = 'var(--text-faint)';
                                                  }}
                                                  onMouseLeave={(e) => {
                                                    e.target.style.background = 'transparent';
                                                    e.target.style.borderColor = 'var(--border)';
                                                  }}
                                                >
                                                  <Info size={14} /> View Details
                                                </button>
                                              </>
                                            );
                                          }

                                          return (
                                            <>
                                              <div className="stu-session-active-card" style={{ marginTop: '10px' }}>
                                                <div className="active-badge" style={{ background: '#dcfce7', color: '#15803d' }}>
                                                  <CheckCircle size={14} /> Enrolled
                                                </div>
                                                {session.meeting_link ? (
                                                  <button
                                                    onClick={() => handleJoinAndCheckIn(session.id, 'PUBLIC', session.meeting_link)}
                                                    className={`stu-join-btn-v2 ${!isJoinable ? 'disabled' : ''}`}
                                                    style={{
                                                      marginTop: '10px',
                                                      width: '100%',
                                                      border: 'none',
                                                      cursor: isJoinable ? 'pointer' : 'not-allowed',
                                                      opacity: isJoinable ? 1 : 0.6
                                                    }}
                                                    disabled={!isJoinable}
                                                  >
                                                    <Video size={16} /> {isJoinable ? "Join Now" : `Starts at ${session.start_time}`}
                                                  </button>
                                                ) : (
                                                  <span style={{ fontSize: '11px', color: 'var(--text-muted)', textAlign: 'center', display: 'block', marginTop: '8px' }}>Link will appear before session</span>
                                                )}


                                              </div>
                                              <button
                                                onClick={() => setShowDetails(session)}
                                                style={{
                                                  marginTop: '8px',
                                                  padding: '8px 12px',
                                                  background: 'transparent',
                                                  border: '1px solid var(--border)',
                                                  borderRadius: '8px',
                                                  color: 'var(--text-muted)',
                                                  fontSize: '12px',
                                                  fontWeight: 600,
                                                  cursor: 'pointer',
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'center',
                                                  gap: '6px',
                                                  width: '100%',
                                                  transition: 'all 0.2s'
                                                }}
                                                onMouseEnter={(e) => {
                                                  e.target.style.background = '#f8fafc';
                                                  e.target.style.borderColor = 'var(--text-faint)';
                                                }}
                                                onMouseLeave={(e) => {
                                                  e.target.style.background = 'transparent';
                                                  e.target.style.borderColor = 'var(--border)';
                                                }}
                                              >
                                                <Info size={14} /> View Details
                                              </button>
                                            </>
                                          );
                                        }

                                        // Default: Not Enrolled / Not Paid
                                        const isFull = session.max_participants && session.participant_count >= session.max_participants;

                                        // Check if session is completed
                                        if (session.is_completed) {
                                          return (
                                            <div style={{
                                              padding: '12px',
                                              background: '#f0fdf4',
                                              border: '1px solid #86efac',
                                              borderRadius: '8px',
                                              color: '#15803d',
                                              fontSize: '13px',
                                              fontWeight: 600,
                                              textAlign: 'center',
                                              marginTop: '10px'
                                            }}>
                                              ✅ Session Ended
                                            </div>
                                          );
                                        }

                                        return (
                                          <>
                                            {isFull ? (
                                              <div style={{
                                                padding: '12px',
                                                background: '#fef2f2',
                                                border: '1px solid #fecaca',
                                                borderRadius: '8px',
                                                color: '#dc2626',
                                                fontSize: '13px',
                                                fontWeight: 600,
                                                textAlign: 'center',
                                                marginTop: '10px'
                                              }}>
                                                🔒 Session Full ({session.participant_count}/{session.max_participants})
                                              </div>
                                            ) : (
                                              <>
                                                {session.price > 0 ? (
                                                  <button className="stu-btn-express" onClick={() => initPayment({ ...session, type: 'PUBLIC' })}>
                                                    <CreditCard size={16} /> Pay ₹{session.price}
                                                  </button>
                                                ) : (
                                                  <button className="stu-btn-express" onClick={() => handleJoinPublicSession(session.id)}>
                                                    <Zap size={16} /> Enroll for Free
                                                  </button>
                                                )}
                                              </>
                                            )}
                                            <button
                                              onClick={() => setShowDetails(session)}
                                              style={{
                                                marginTop: '8px',
                                                padding: '8px 12px',
                                                background: 'transparent',
                                                border: '1px solid var(--border)',
                                                borderRadius: '8px',
                                                color: 'var(--text-muted)',
                                                fontSize: '12px',
                                                fontWeight: 600,
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '6px',
                                                width: '100%',
                                                transition: 'all 0.2s'
                                              }}
                                              onMouseEnter={(e) => {
                                                e.target.style.background = '#f8fafc';
                                                e.target.style.borderColor = 'var(--text-faint)';
                                              }}
                                              onMouseLeave={(e) => {
                                                e.target.style.background = 'transparent';
                                                e.target.style.borderColor = 'var(--border)';
                                              }}
                                            >
                                              <Info size={14} /> View Details
                                            </button>
                                          </>
                                        );
                                      })()}
                                    </div>
                                  ))}
                                </div>
                              </section>
                            )}

                            {/* 2. INDUSTRY MASTERCLASSES - PREFERRED DOMAIN */}
                            {industryPreferredSessions.length > 0 && (
                              <section className="stu-sessions-container" style={{ marginTop: universityPreferredSessions.length > 0 ? '40px' : '0' }}>
                                <div className="stu-sessions-header">
                                  <h3>
                                    <Video size={20} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                                    {activeTab === 'explore' ? 'Industry Masterclasses (Your Preferred Domains)' : 'Your Industry Sessions (Preferred Domains)'}
                                  </h3>
                                  <span style={{ fontSize: '0.875rem', color: 'var(--text-muted)', fontWeight: 500 }}>
                                    Expert-led industry sessions
                                  </span>
                                </div>
                                <div className="stu-sessions-grid">
                                  {industryPreferredSessions.map(session => (
                                    <div key={`ind-pref-${session.id}`} className="stu-session-card" style={{ borderLeft: '4px solid #3b82f6' }}>
                                      <div className="stu-card-top">
                                        <span className="stu-type-badge">{session.domain}</span>
                                        {(session.price === 0 || session.isFree) && (
                                          <span className="stu-type-badge" style={{ background: '#ecfdf5', color: '#059669', marginLeft: '6px' }}>FREE</span>
                                        )}
                                        <span className="participant-pill"><User size={12} /> {session.participant_count} Joined</span>
                                      </div>
                                      <h4 className="stu-session-title">{session.title}</h4>
                                      <p className="stu-prof-name">Prof. {session.professor_name}</p>
                                      {renderSessionSchedule(session)}
                                      {/* Payment & Status Logic */}
                                      {(() => {
                                        const pStatus = session.payment_status;
                                        const isSuccess = pStatus === 'SUCCESS';
                                        const isConfirmed = pStatus === 'HELD' || pStatus === 'RELEASED' || pStatus === 'ENROLLED';

                                        if (isSuccess) {
                                          return (
                                            <>
                                              <div className="stu-session-active-card paid-confirm" style={{ marginTop: '10px' }}>
                                                <div className="active-badge" style={{ background: '#fef3c7', color: '#d97706' }}>
                                                  <Clock size={14} /> Payment Received
                                                </div>
                                                <div className="stu-status-note-v2" style={{ padding: '8px', background: 'var(--engine-black)', fontSize: '11px' }}>
                                                  ⌛ Awaiting Compliance...
                                                </div>
                                              </div>
                                              <button
                                                onClick={() => setShowDetails(session)}
                                                style={{
                                                  marginTop: '8px',
                                                  padding: '8px 12px',
                                                  background: 'transparent',
                                                  border: '1px solid var(--border)',
                                                  borderRadius: '8px',
                                                  color: 'var(--text-muted)',
                                                  fontSize: '12px',
                                                  fontWeight: 600,
                                                  cursor: 'pointer',
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'center',
                                                  gap: '6px',
                                                  width: '100%',
                                                  transition: 'all 0.2s'
                                                }}
                                                onMouseEnter={(e) => {
                                                  e.target.style.background = '#f8fafc';
                                                  e.target.style.borderColor = 'var(--text-faint)';
                                                }}
                                                onMouseLeave={(e) => {
                                                  e.target.style.background = 'transparent';
                                                  e.target.style.borderColor = 'var(--border)';
                                                }}
                                              >
                                                <Info size={14} /> View Details
                                              </button>
                                            </>
                                          );
                                        }

                                        // Requested - pending university approval
                                        if (pStatus === 'REQUESTED') {
                                          return (
                                            <>
                                              <div className="stu-session-active-card" style={{ marginTop: '10px' }}>
                                                <div className="active-badge" style={{ background: '#fef3c7', color: '#d97706' }}>
                                                  <Clock size={14} /> Requested University
                                                </div>
                                                <div className="stu-status-note-v2" style={{ padding: '8px', background: 'var(--engine-black)', fontSize: '11px', textAlign: 'center' }}>
                                                  Awaiting university approval...
                                                </div>
                                              </div>
                                              <button
                                                onClick={() => setShowDetails(session)}
                                                style={{
                                                  marginTop: '8px',
                                                  padding: '8px 12px',
                                                  background: 'transparent',
                                                  border: '1px solid var(--border)',
                                                  borderRadius: '8px',
                                                  color: 'var(--text-muted)',
                                                  fontSize: '12px',
                                                  fontWeight: 600,
                                                  cursor: 'pointer',
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'center',
                                                  gap: '6px',
                                                  width: '100%',
                                                  transition: 'all 0.2s'
                                                }}
                                                onMouseEnter={(e) => {
                                                  e.target.style.background = '#f8fafc';
                                                  e.target.style.borderColor = 'var(--text-faint)';
                                                }}
                                                onMouseLeave={(e) => {
                                                  e.target.style.background = 'transparent';
                                                  e.target.style.borderColor = 'var(--border)';
                                                }}
                                              >
                                                <Info size={14} /> View Details
                                              </button>
                                            </>
                                          );
                                        }

                                        if (isConfirmed) {
                                          const eventTime = new Date(`${session.event_date.split('T')[0]}T${session.start_time}`);
                                          const now = new Date();
                                          const joinTime = new Date(eventTime.getTime() - 10 * 60000);
                                          const isJoinable = now >= joinTime;

                                          if (session.is_completed || pStatus === 'RELEASED') {
                                            return (
                                              <>
                                                <div className="stu-session-active-card" style={{ marginTop: '10px', textAlign: 'center' }}>
                                                  <div style={{ padding: '8px', color: '#10b981', fontWeight: 600, background: '#dcfce7', borderRadius: '6px', fontSize: '12px' }}>
                                                    ✅ Completed
                                                  </div>
                                                </div>
                                                <button
                                                  onClick={() => setShowDetails(session)}
                                                  style={{
                                                    marginTop: '8px',
                                                    padding: '8px 12px',
                                                    background: 'transparent',
                                                    border: '1px solid var(--border)',
                                                    borderRadius: '8px',
                                                    color: 'var(--text-muted)',
                                                    fontSize: '12px',
                                                    fontWeight: 600,
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: '6px',
                                                    width: '100%',
                                                    transition: 'all 0.2s'
                                                  }}
                                                  onMouseEnter={(e) => {
                                                    e.target.style.background = '#f8fafc';
                                                    e.target.style.borderColor = 'var(--text-faint)';
                                                  }}
                                                  onMouseLeave={(e) => {
                                                    e.target.style.background = 'transparent';
                                                    e.target.style.borderColor = 'var(--border)';
                                                  }}
                                                >
                                                  <Info size={14} /> View Details
                                                </button>
                                              </>
                                            );
                                          }

                                          return (
                                            <>
                                              <div className="stu-session-active-card" style={{ marginTop: '10px' }}>
                                                <div className="active-badge" style={{ background: '#dcfce7', color: '#15803d' }}>
                                                  <CheckCircle size={14} /> Enrolled
                                                </div>
                                                {session.meeting_link ? (
                                                  <button
                                                    onClick={() => handleJoinAndCheckIn(session.id, 'PUBLIC', session.meeting_link)}
                                                    className={`stu-join-btn-v2 ${!isJoinable ? 'disabled' : ''}`}
                                                    style={{
                                                      marginTop: '10px',
                                                      width: '100%',
                                                      border: 'none',
                                                      cursor: isJoinable ? 'pointer' : 'not-allowed',
                                                      opacity: isJoinable ? 1 : 0.6
                                                    }}
                                                    disabled={!isJoinable}
                                                  >
                                                    <Video size={16} /> {isJoinable ? "Join Now" : `Starts at ${session.start_time}`}
                                                  </button>
                                                ) : (
                                                  <span style={{ fontSize: '11px', color: 'var(--text-muted)', textAlign: 'center', display: 'block', marginTop: '8px' }}>Link will appear before session</span>
                                                )}


                                              </div>
                                              <button
                                                onClick={() => setShowDetails(session)}
                                                style={{
                                                  marginTop: '8px',
                                                  padding: '8px 12px',
                                                  background: 'transparent',
                                                  border: '1px solid var(--border)',
                                                  borderRadius: '8px',
                                                  color: 'var(--text-muted)',
                                                  fontSize: '12px',
                                                  fontWeight: 600,
                                                  cursor: 'pointer',
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'center',
                                                  gap: '6px',
                                                  width: '100%',
                                                  transition: 'all 0.2s'
                                                }}
                                                onMouseEnter={(e) => {
                                                  e.target.style.background = '#f8fafc';
                                                  e.target.style.borderColor = 'var(--text-faint)';
                                                }}
                                                onMouseLeave={(e) => {
                                                  e.target.style.background = 'transparent';
                                                  e.target.style.borderColor = 'var(--border)';
                                                }}
                                              >
                                                <Info size={14} /> View Details
                                              </button>
                                            </>
                                          );
                                        }

                                        // Check if session is completed
                                        if (session.is_completed) {
                                          return (
                                            <div style={{
                                              padding: '12px',
                                              background: '#f0fdf4',
                                              border: '1px solid #86efac',
                                              borderRadius: '8px',
                                              color: '#15803d',
                                              fontSize: '13px',
                                              fontWeight: 600,
                                              textAlign: 'center',
                                              marginTop: '10px'
                                            }}>
                                              ✅ Session Ended
                                            </div>
                                          );
                                        }

                                        return (
                                          <>
                                            {session.max_participants && session.participant_count >= session.max_participants ? (
                                              <div style={{
                                                padding: '12px',
                                                background: '#fef2f2',
                                                border: '1px solid #fecaca',
                                                borderRadius: '8px',
                                                color: '#dc2626',
                                                fontSize: '13px',
                                                fontWeight: 600,
                                                textAlign: 'center',
                                                marginTop: '10px'
                                              }}>
                                                🔒 Session Full ({session.participant_count}/{session.max_participants})
                                              </div>
                                            ) : (
                                              <>
                                                {session.price > 0 ? (
                                                  <button className="stu-btn-express" onClick={() => initPayment({ ...session, type: 'PUBLIC' })}>
                                                    <CreditCard size={16} /> Pay ₹{session.price}
                                                  </button>
                                                ) : (
                                                  <button className="stu-btn-express" onClick={() => handleJoinPublicSession(session.id)}><Zap size={16} /> Enroll for Free</button>
                                                )}
                                              </>
                                            )}
                                            <button
                                              onClick={() => setShowDetails(session)}
                                              style={{
                                                marginTop: '8px',
                                                padding: '8px 12px',
                                                background: 'transparent',
                                                border: '1px solid var(--border)',
                                                borderRadius: '8px',
                                                color: 'var(--text-muted)',
                                                fontSize: '12px',
                                                fontWeight: 600,
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '6px',
                                                width: '100%',
                                                transition: 'all 0.2s'
                                              }}
                                              onMouseEnter={(e) => {
                                                e.target.style.background = '#f8fafc';
                                                e.target.style.borderColor = 'var(--text-faint)';
                                              }}
                                              onMouseLeave={(e) => {
                                                e.target.style.background = 'transparent';
                                                e.target.style.borderColor = 'var(--border)';
                                              }}
                                            >
                                              <Info size={14} /> View Details
                                            </button>
                                          </>
                                        );
                                      })()}
                                    </div>
                                  ))}
                                </div>
                              </section>
                            )}

                            {/* Empty State */}
                            {filteredPublicSessions.length === 0 && (
                              <div className="stu-empty-state"><p>No masterclasses found.</p></div>
                            )}

                            {/* 3. PERSONAL REQUESTS TO EXPERTS */}
                            {myPersonalRequests.filter(r => r.status !== 'COMPLETED' && !r.is_completed).length > 0 && (
                              <section className="stu-sessions-container" style={{ marginTop: '40px' }}>
                                <div className="stu-sessions-header">
                                  <h3>
                                    <Zap size={20} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                                    {activeTab === 'explore' ? 'My Personal Requests to Experts' : 'My Personal Sessions'}
                                  </h3>
                                  <span style={{ fontSize: '0.875rem', color: 'var(--text-muted)', fontWeight: 500 }}>
                                    Your 1:1 and group sessions with experts
                                  </span>
                                </div>
                                <div className="stu-sessions-grid">
                                  {myPersonalRequests.filter(r => r.status !== 'COMPLETED' && !r.is_completed).map(req => (
                                    <div key={`req-${req.id}`} className="stu-session-card" style={{ borderLeft: '4px solid #10b981' }}>
                                      <div className="stu-card-top">
                                        <span className={`stu-type-badge ${req.session_type === 'GROUP' ? 'group-bg' : 'indiv-bg'}`}>
                                          {req.session_type === 'GROUP' ? <Users size={12} /> : <User size={12} />} {req.session_type}
                                        </span>
                                        <span className={`stu-status-badge ${req.status.toLowerCase()}`}>
                                          {req.status === 'ACCEPTED' ? '✅ Accepted' : req.status === 'PENDING' ? '⏳ Pending' : req.status === 'COMPLETED' ? '✅ Completed' : '❌ Declined'}
                                        </span>
                                      </div>
                                      <h4 className="stu-session-title">{req.title}</h4>
                                      <p className="stu-prof-name">With Prof. {req.professor_name}</p>
                                      <div className="stu-session-details">
                                        <div className="stu-detail-line">📅 {new Date(req.event_date.split('T')[0]).toLocaleDateString('en-GB')}</div>
                                        <div className="stu-detail-line">🕒 {req.start_time}</div>
                                      </div>

                                      {/* Payment & Link Logic */}
                                      {req.status === 'ACCEPTED' && (() => {
                                        const pStatus = req.payment_status;
                                        const needsPayment = !pStatus || pStatus === 'NO_PAYMENT' || pStatus === 'PENDING';
                                        const isSuccess = pStatus === 'SUCCESS'; // Paid, waiting compliance
                                        const isHeld = pStatus === 'HELD' || pStatus === 'ESCROWED'; // Confirmed
                                        const isReleased = pStatus === 'RELEASED'; // Completed

                                        if (needsPayment) {
                                          return (
                                            <button
                                              className="stu-btn-express"
                                              onClick={() => initPayment(req)}
                                              style={{ width: '100%', display: 'flex', justifyContent: 'center', gap: '8px' }}
                                            >
                                              <CreditCard size={16} /> {req.proposed_amount || req.price ? `Pay Now ₹${(req.proposed_amount || req.price).toLocaleString()}` : 'Join for Free'}
                                            </button>
                                          );
                                        }

                                        if (isSuccess) {
                                          return (
                                            <div className="stu-session-active-card paid-confirm">
                                              <div className="active-badge" style={{ background: '#fef3c7', color: '#d97706' }}>
                                                <Clock size={14} /> Payment Received
                                              </div>
                                              <p className="active-note" style={{ fontSize: '12px' }}>Your payment has been received and is awaiting Compliance confirmation. Once verified, funds will be moved to escrow.</p>
                                              <div className="stu-status-note-v2" style={{ padding: '8px', background: 'var(--engine-black)' }}>
                                                ⌛ Awaiting Compliance Confirmation...
                                              </div>
                                            </div>
                                          );
                                        }

                                        if (isHeld) {
                                          const eventTime = new Date(`${req.event_date.split('T')[0]}T${req.start_time}`);
                                          const now = new Date();
                                          const joinTime = new Date(eventTime.getTime() - 10 * 60000);
                                          const isJoinable = now >= joinTime;

                                          if (req.is_completed) {
                                            return (
                                              <div className="stu-session-active-card" style={{ marginTop: '10px', textAlign: 'center' }}>
                                                <div style={{ color: '#10b981', fontWeight: 600, fontSize: '13px', background: '#dcfce7', padding: '8px', borderRadius: '6px' }}>
                                                  ✅ Session Completed
                                                </div>
                                                <div style={{ marginTop: '12px', fontSize: '12px', textAlign: 'left', background: 'var(--engine-black)', padding: '10px', borderRadius: '8px', border: '1px solid var(--border)' }}>
                                                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                                    <span style={{ color: 'var(--text-muted)' }}>Amount Paid:</span>
                                                    <span style={{ fontWeight: 600 }}>{req.proposed_amount || req.price ? `₹${(req.proposed_amount || req.price).toLocaleString()}` : 'Free'}</span>
                                                  </div>
                                                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <span style={{ color: 'var(--text-muted)' }}>Status:</span>
                                                    <span style={{ color: '#f59e0b', fontWeight: 500 }}>Processing Release</span>
                                                  </div>
                                                </div>
                                              </div>
                                            );
                                          }

                                          return (
                                            <div className="stu-session-active-card" style={{ marginTop: '10px' }}>
                                              <div className="active-badge" style={{ background: '#dcfce7', color: '#15803d' }}>
                                                <CheckCircle size={14} /> Confirmed
                                              </div>
                                              {req.meeting_link ? (
                                                <button
                                                  onClick={() => handleJoinAndCheckIn(req.id, 'REQUEST', req.meeting_link)}
                                                  className={`stu-join-btn-v2 ${!isJoinable ? 'disabled' : ''}`}
                                                  style={{
                                                    marginTop: '10px',
                                                    width: '100%',
                                                    border: 'none',
                                                    cursor: isJoinable ? 'pointer' : 'not-allowed',
                                                    opacity: isJoinable ? 1 : 0.6
                                                  }}
                                                  disabled={!isJoinable}
                                                >
                                                  <Video size={16} /> {isJoinable ? "Join Session" : `Starts at ${req.start_time}`}
                                                </button>
                                              ) : (
                                                <span style={{ fontSize: '11px', color: 'var(--text-muted)', textAlign: 'center', display: 'block', marginTop: '8px' }}>Link will appear 10m before start</span>
                                              )}


                                            </div>
                                          );
                                        }
                                        return null;
                                      })()}

                                      {req.status === 'PENDING' && (
                                        <div className="stu-pending-note">
                                          Waiting for professor approval.
                                        </div>
                                      )}
                                      <button
                                        onClick={() => setShowDetails(req)}
                                        style={{
                                          marginTop: '8px',
                                          padding: '8px 12px',
                                          background: 'transparent',
                                          border: '1px solid var(--border)',
                                          borderRadius: '8px',
                                          color: 'var(--text-muted)',
                                          fontSize: '12px',
                                          fontWeight: 600,
                                          cursor: 'pointer',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          gap: '6px',
                                          width: '100%',
                                          transition: 'all 0.2s'
                                        }}
                                        onMouseEnter={(e) => {
                                          e.target.style.background = '#f8fafc';
                                          e.target.style.borderColor = 'var(--text-faint)';
                                        }}
                                        onMouseLeave={(e) => {
                                          e.target.style.background = 'transparent';
                                          e.target.style.borderColor = 'var(--border)';
                                        }}
                                      >
                                        <Info size={14} /> View Details
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              </section>
                            )}

                            {/* 4. UNIVERSITY SESSIONS - OTHER DOMAINS */}
                            {universityOtherSessions.length > 0 && (
                              <section className="stu-sessions-container" style={{ marginTop: '40px' }}>
                                <div className="stu-sessions-header">
                                  <h3>
                                    <Shield size={20} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                                    {activeTab === 'explore' ? 'University Sessions (Other Domains)' : 'Your University Sessions (Other Domains)'}
                                  </h3>
                                  <span style={{ fontSize: '0.875rem', color: 'var(--text-muted)', fontWeight: 500 }}>
                                    Explore other university offerings
                                  </span>
                                </div>
                                <div className="stu-sessions-grid">
                                  {universityOtherSessions.map(session => (
                                    <div key={`uni-other-${session.id}`} className="stu-session-card" style={{ borderLeft: '4px solid var(--border)' }}>
                                      <div className="stu-card-top">
                                        <span className="stu-type-badge">{session.domain}</span>
                                        {(session.price === 0 || session.isFree) && (
                                          <span className="stu-type-badge" style={{ background: '#ecfdf5', color: '#059669', marginLeft: '6px' }}>FREE</span>
                                        )}
                                        <span className="participant-pill"><User size={12} /> {session.participant_count} Joined</span>
                                      </div>
                                      <h4 className="stu-session-title">{session.title}</h4>
                                      <p className="stu-prof-name">Prof. {session.professor_name}</p>
                                      {renderSessionSchedule(session)}
                                      {/* Payment & Status Logic */}
                                      {(() => {
                                        const pStatus = session.payment_status;
                                        const isSuccess = pStatus === 'SUCCESS';
                                        const isConfirmed = pStatus === 'HELD' || pStatus === 'RELEASED' || pStatus === 'ENROLLED';

                                        if (isSuccess) {
                                          return (
                                            <div className="stu-session-active-card paid-confirm" style={{ marginTop: '10px' }}>
                                              <div className="active-badge" style={{ background: '#fef3c7', color: '#d97706' }}>
                                                <Clock size={14} /> Payment Received
                                              </div>
                                              <div className="stu-status-note-v2" style={{ padding: '8px', background: 'var(--engine-black)', fontSize: '11px' }}>
                                                Awaiting Compliance...
                                              </div>
                                            </div>
                                          );
                                        }

                                        if (pStatus === 'REQUESTED') {
                                          return (
                                            <div className="stu-session-active-card" style={{ marginTop: '10px' }}>
                                              <div className="active-badge" style={{ background: '#fef3c7', color: '#d97706' }}>
                                                <Clock size={14} /> Requested University
                                              </div>
                                              <div className="stu-status-note-v2" style={{ padding: '8px', background: 'var(--engine-black)', fontSize: '11px', textAlign: 'center' }}>
                                                Awaiting university approval...
                                              </div>
                                            </div>
                                          );
                                        }

                                        if (isConfirmed) {
                                          const eventTime = new Date(`${session.event_date.split('T')[0]}T${session.start_time}`);
                                          const now = new Date();
                                          const joinTime = new Date(eventTime.getTime() - 10 * 60000);
                                          const isJoinable = now >= joinTime;

                                          if (session.is_completed || pStatus === 'RELEASED') {
                                            return (
                                              <div className="stu-session-active-card" style={{ marginTop: '10px', textAlign: 'center' }}>
                                                <div style={{ padding: '8px', color: '#10b981', fontWeight: 600, background: '#dcfce7', borderRadius: '6px', fontSize: '12px' }}>
                                                  Completed
                                                </div>
                                              </div>
                                            );
                                          }

                                          return (
                                            <div className="stu-session-active-card" style={{ marginTop: '10px' }}>
                                              <div className="active-badge" style={{ background: '#dcfce7', color: '#15803d' }}>
                                                <CheckCircle size={14} /> Enrolled
                                              </div>
                                              {session.meeting_link ? (
                                                <button
                                                  onClick={() => handleJoinAndCheckIn(session.id, 'PUBLIC', session.meeting_link)}
                                                  className={`stu-join-btn-v2 ${!isJoinable ? 'disabled' : ''}`}
                                                  style={{ marginTop: '10px', width: '100%', border: 'none', cursor: isJoinable ? 'pointer' : 'not-allowed', opacity: isJoinable ? 1 : 0.6 }}
                                                  disabled={!isJoinable}
                                                >
                                                  <Video size={16} /> {isJoinable ? "Join Now" : `Starts at ${session.start_time}`}
                                                </button>
                                              ) : (
                                                <span style={{ fontSize: '11px', color: 'var(--text-muted)', textAlign: 'center', display: 'block', marginTop: '8px' }}>Link will appear before session</span>
                                              )}


                                            </div>
                                          );
                                        }

                                        // Default: Not Enrolled
                                        const isFull = session.max_participants && session.participant_count >= session.max_participants;
                                        if (session.is_completed) {
                                          return (
                                            <div style={{ padding: '12px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '8px', color: '#15803d', fontSize: '13px', fontWeight: 600, textAlign: 'center', marginTop: '10px' }}>
                                              Session Ended
                                            </div>
                                          );
                                        }

                                        return (
                                          <>
                                            {isFull ? (
                                              <div style={{ padding: '12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#dc2626', fontSize: '13px', fontWeight: 600, textAlign: 'center', marginTop: '10px' }}>
                                                Session Full ({session.participant_count}/{session.max_participants})
                                              </div>
                                            ) : (
                                              <>
                                                {session.price > 0 ? (
                                                  <button className="stu-btn-express" onClick={() => initPayment({ ...session, type: 'PUBLIC' })}>
                                                    <CreditCard size={16} /> Pay ₹{session.price}
                                                  </button>
                                                ) : (
                                                  <button className="stu-btn-express" onClick={() => handleJoinPublicSession(session.id)}><Zap size={16} /> Join Session</button>
                                                )}
                                              </>
                                            )}
                                          </>
                                        );
                                      })()}
                                      <button
                                        id="view-session-details"
                                        onClick={() => setShowDetails(session)}
                                        style={{
                                          marginTop: '8px',
                                          padding: '8px 12px',
                                          background: 'transparent',
                                          border: '1px solid var(--border)',
                                          borderRadius: '8px',
                                          color: 'var(--text-muted)',
                                          fontSize: '12px',
                                          fontWeight: 600,
                                          cursor: 'pointer',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          gap: '6px',
                                          width: '100%',
                                          transition: 'all 0.2s'
                                        }}
                                        onMouseEnter={(e) => {
                                          e.target.style.background = '#f8fafc';
                                          e.target.style.borderColor = 'var(--text-faint)';
                                        }}
                                        onMouseLeave={(e) => {
                                          e.target.style.background = 'transparent';
                                          e.target.style.borderColor = 'var(--border)';
                                        }}
                                      >
                                        <Info size={14} /> View Details
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              </section>
                            )}

                            {/* 5. INDUSTRY MASTERCLASSES - OTHER DOMAINS */}
                            {industryOtherSessions.length > 0 && (
                              <section className="stu-sessions-container" style={{ marginTop: '40px' }}>
                                <div className="stu-sessions-header">
                                  <h3>
                                    <Video size={20} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                                    {activeTab === 'explore' ? 'Industry Masterclasses (Other Domains)' : 'Your Industry Sessions (Other Domains)'}
                                  </h3>
                                  <span style={{ fontSize: '0.875rem', color: 'var(--text-muted)', fontWeight: 500 }}>
                                    Explore beyond your preferred domains
                                  </span>
                                </div>
                                <div className="stu-sessions-grid">
                                  {industryOtherSessions.map(session => (
                                    <div key={`ind-other-${session.id}`} className="stu-session-card">
                                      <div className="stu-card-top">
                                        <span className="stu-type-badge">{session.domain}</span>
                                        {(session.price === 0 || session.isFree) && (
                                          <span className="stu-type-badge" style={{ background: '#ecfdf5', color: '#059669', marginLeft: '6px' }}>FREE</span>
                                        )}
                                        <span className="participant-pill"><User size={12} /> {session.participant_count} Joined</span>
                                      </div>
                                      <h4 className="stu-session-title">{session.title}</h4>
                                      <p className="stu-prof-name">Prof. {session.professor_name}</p>
                                      {renderSessionSchedule(session)}
                                      {/* Payment & Status Logic */}
                                      {(() => {
                                        const pStatus = session.payment_status;
                                        const isSuccess = pStatus === 'SUCCESS';
                                        const isConfirmed = pStatus === 'HELD' || pStatus === 'RELEASED' || pStatus === 'ENROLLED';

                                        if (isSuccess) {
                                          return (
                                            <div className="stu-session-active-card paid-confirm" style={{ marginTop: '10px' }}>
                                              <div className="active-badge" style={{ background: '#fef3c7', color: '#d97706' }}>
                                                <Clock size={14} /> Payment Received
                                              </div>
                                              <div className="stu-status-note-v2" style={{ padding: '8px', background: 'var(--engine-black)', fontSize: '11px' }}>
                                                Awaiting Compliance...
                                              </div>
                                            </div>
                                          );
                                        }

                                        if (isConfirmed) {
                                          const eventTime = new Date(`${session.event_date.split('T')[0]}T${session.start_time}`);
                                          const now = new Date();
                                          const joinTime = new Date(eventTime.getTime() - 10 * 60000);
                                          const isJoinable = now >= joinTime;

                                          if (session.is_completed || pStatus === 'RELEASED') {
                                            return (
                                              <div className="stu-session-active-card" style={{ marginTop: '10px', textAlign: 'center' }}>
                                                <div style={{ padding: '8px', color: '#10b981', fontWeight: 600, background: '#dcfce7', borderRadius: '6px', fontSize: '12px' }}>
                                                  Completed
                                                </div>
                                              </div>
                                            );
                                          }

                                          return (
                                            <div className="stu-session-active-card" style={{ marginTop: '10px' }}>
                                              <div className="active-badge" style={{ background: '#dcfce7', color: '#15803d' }}>
                                                <CheckCircle size={14} /> Enrolled
                                              </div>
                                              {session.meeting_link ? (
                                                <button
                                                  onClick={() => handleJoinAndCheckIn(session.id, 'PUBLIC', session.meeting_link)}
                                                  className={`stu-join-btn-v2 ${!isJoinable ? 'disabled' : ''}`}
                                                  style={{ marginTop: '10px', width: '100%', border: 'none', cursor: isJoinable ? 'pointer' : 'not-allowed', opacity: isJoinable ? 1 : 0.6 }}
                                                  disabled={!isJoinable}
                                                >
                                                  <Video size={16} /> {isJoinable ? "Join Now" : `Starts at ${session.start_time}`}
                                                </button>
                                              ) : (
                                                <span style={{ fontSize: '11px', color: 'var(--text-muted)', textAlign: 'center', display: 'block', marginTop: '8px' }}>Link will appear before session</span>
                                              )}


                                            </div>
                                          );
                                        }

                                        // Default: Not Enrolled
                                        const isFull = session.max_participants && session.participant_count >= session.max_participants;
                                        if (session.is_completed) {
                                          return (
                                            <div style={{ padding: '12px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '8px', color: '#15803d', fontSize: '13px', fontWeight: 600, textAlign: 'center', marginTop: '10px' }}>
                                              Session Ended
                                            </div>
                                          );
                                        }

                                        return (
                                          <>
                                            {isFull ? (
                                              <div style={{ padding: '12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#dc2626', fontSize: '13px', fontWeight: 600, textAlign: 'center', marginTop: '10px' }}>
                                                Session Full ({session.participant_count}/{session.max_participants})
                                              </div>
                                            ) : (
                                              <>
                                                {session.price > 0 ? (
                                                  <button className="stu-btn-express" onClick={() => initPayment({ ...session, type: 'PUBLIC' })}>
                                                    <CreditCard size={16} /> Pay ₹{session.price}
                                                  </button>
                                                ) : (
                                                  <button className="stu-btn-express" onClick={() => handleJoinPublicSession(session.id)}><Zap size={16} /> Enroll for Free</button>
                                                )}
                                              </>
                                            )}
                                          </>
                                        );
                                      })()}
                                      <button
                                        onClick={() => setShowDetails(session)}
                                        style={{
                                          marginTop: '8px',
                                          padding: '8px 12px',
                                          background: 'transparent',
                                          border: '1px solid var(--border)',
                                          borderRadius: '8px',
                                          color: 'var(--text-muted)',
                                          fontSize: '12px',
                                          fontWeight: 600,
                                          cursor: 'pointer',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          gap: '6px',
                                          width: '100%',
                                          transition: 'all 0.2s'
                                        }}
                                        onMouseEnter={(e) => {
                                          e.target.style.background = '#f8fafc';
                                          e.target.style.borderColor = 'var(--text-faint)';
                                        }}
                                        onMouseLeave={(e) => {
                                          e.target.style.background = 'transparent';
                                          e.target.style.borderColor = 'var(--border)';
                                        }}
                                      >
                                        <Info size={14} /> View Details
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              </section>
                            )}

                            {/* Empty State for Personal */}
                            {myPersonalRequests.length === 0 && (
                              <div className="stu-empty-state" style={{ marginTop: '40px' }}><p>No personal sessions booked yet.{/* <a href="/discover">Find an expert.</a> */}</p></div>
                            )}
                          </div >
                        </>
                      )}
      </main >

      {/* MODALS */}
      {
        paymentModalData && (
          <PaymentModal
            session={paymentModalData.session}
            sessionType={paymentModalData.sessionType}
            onClose={() => setPaymentModalData(null)}
            onPaymentComplete={() => {
              setPaymentModalData(null);
              fetchDashData(); // Refresh to show new status
            }}
          />
        )
      }

      {
        showDetails && (
          <DetailsModal
            data={showDetails}
            onClose={() => setShowDetails(null)}
            onRequestRefund={handleRequestRefund}
          />
        )
      }

      {/* Refund Request Modal */}
      {showRefundModal && (
        <div className="stu-modal-overlay" onClick={() => setShowRefundModal(false)} style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10001,
          backdropFilter: 'blur(4px)'
        }}>
          <div className="stu-modal-card" onClick={e => e.stopPropagation()} style={{
            background: 'var(--graphite-dark)',
            borderRadius: '16px',
            maxWidth: '450px',
            width: '90%',
            overflow: 'hidden',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
          }}>
            <div style={{
              background: 'linear-gradient(135deg, var(--proto-white) 0%, var(--proto-white) 100%)',
              padding: '24px',
              position: 'relative',
              color: 'white'
            }}>
              <h3 style={{ fontSize: '20px', fontWeight: 700, margin: 0 }}>Request Refund</h3>
              <p style={{ fontSize: '13px', opacity: 0.8, marginTop: '4px' }}>Submit a refund request for compliance review</p>
              <button
                onClick={() => setShowRefundModal(false)}
                style={{
                  position: 'absolute',
                  top: '20px',
                  right: '20px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: 'none',
                  borderRadius: '50%',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: 'white'
                }}
              >
                <X size={18} />
              </button>
            </div>
            <div className="stu-modal-body" style={{ padding: '28px' }}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 700, color: 'var(--proto-white)', marginBottom: '8px' }}>
                  Reason for Refund
                </label>
                <select
                  value={refundData.reason}
                  onChange={e => setRefundData({ ...refundData, reason: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '2px solid var(--border)',
                    fontSize: '14px',
                    outline: 'none'
                  }}
                >
                  <option value="PROFESSOR_NO_SHOW">Professor No-Show</option>
                  <option value="STUDENT_CANCELLATION">Cancellation ({'>'}24h notice)</option>
                  <option value="TECHNICAL_FAILURE">Technical Platform Failure</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 700, color: 'var(--proto-white)', marginBottom: '8px' }}>
                  Provide Details
                </label>
                <textarea
                  value={refundData.details}
                  onChange={e => setRefundData({ ...refundData, details: e.target.value })}
                  placeholder="Explain the reason for your refund request..."
                  rows="4"
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '2px solid var(--border)',
                    fontSize: '14px',
                    outline: 'none',
                    resize: 'none'
                  }}
                />
              </div>

              <div style={{
                marginTop: '20px',
                padding: '16px',
                background: 'var(--engine-black)',
                borderRadius: '12px',
                border: '1px solid var(--border)'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: 'var(--text-muted)', fontSize: '14px', fontWeight: 500 }}>Estimated Refund:</span>
                  <span style={{ fontWeight: 700, color: 'var(--proto-white)', fontSize: '18px' }}>₹{refundData.amount.toLocaleString()}</span>
                </div>
              </div>

              <button
                onClick={submitRefundRequest}
                style={{
                  width: '100%',
                  marginTop: '24px',
                  padding: '14px',
                  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '15px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 16px rgba(239, 68, 68, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.3)';
                }}
              >
                Submit Refund Request
              </button>
            </div>
          </div>
        </div>
      )
      }

      {/* Attempts History Modal */}
      {showAttemptsModal && (() => {
        let hist = profile?.assessment_attempts_history;
        if (typeof hist === 'string') { try { hist = JSON.parse(hist); } catch (e) { hist = []; } }
        const attempts = Array.isArray(hist) ? hist : [];
        return (
          <div
            onClick={() => setShowAttemptsModal(false)}
            style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(0,0,0,0.45)', zIndex: 9999,
              display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px'
            }}
          >
            <div
              onClick={e => e.stopPropagation()}
              style={{
                background: '#ffffff', borderRadius: '20px', padding: '32px',
                width: '100%', maxWidth: '480px', boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                fontFamily: "'Inter', sans-serif"
              }}
            >
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', paddingBottom: '16px', borderBottom: '1px solid #f1f5f9' }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '800', color: '#0f172a' }}>Assessment History</h3>
                  <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#64748b' }}>{attempts.length} attempt{attempts.length !== 1 ? 's' : ''} recorded</p>
                </div>
                <button
                  onClick={() => setShowAttemptsModal(false)}
                  style={{ background: '#f1f5f9', border: 'none', borderRadius: '10px', padding: '8px 14px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', color: '#475569' }}
                >
                  ✕ Close
                </button>
              </div>

              {/* Attempts List */}
              {attempts.length === 0 ? (
                <p style={{ textAlign: 'center', color: '#94a3b8', fontSize: '14px', padding: '32px 0' }}>No attempts recorded yet.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxHeight: '360px', overflowY: 'auto' }}>
                  {[...attempts].reverse().map((h, i) => {
                    const isPassing = h.score >= 70;
                    return (
                      <div key={i} style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '14px 18px', borderRadius: '12px',
                        background: isPassing ? '#f0fdf4' : '#fff7ed',
                        border: `1px solid ${isPassing ? '#bbf7d0' : '#fed7aa'}`
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                          <div style={{
                            width: '38px', height: '38px', borderRadius: '10px',
                            background: isPassing ? '#16a34a' : '#ea580c',
                            color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontWeight: '800', fontSize: '14px', flexShrink: 0
                          }}>
                            #{h.attempt || (attempts.length - i)}
                          </div>
                          <div>
                            <div style={{ fontSize: '13.5px', fontWeight: '700', color: '#0f172a' }}>
                              {isPassing ? '✅ Passed' : '📚 Needs Improvement'}
                            </div>
                            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>{h.date}</div>
                          </div>
                        </div>
                        <div style={{
                          fontSize: '20px', fontWeight: '800',
                          color: isPassing ? '#16a34a' : '#ea580c'
                        }}>
                          {h.score}%
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Feedback Modal - Modern Premium Design */}
      {
        showFeedbackModal && (
          <div className="stu-modal-overlay" onClick={() => setShowFeedbackModal(false)} style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}>
            <div className="stu-payment-modal" style={{
              maxWidth: '520px',
              width: '90%',
              borderRadius: '12px',
              overflow: 'hidden',
              maxHeight: '90vh',
              overflowY: 'auto',
              margin: '0 auto'
            }} onClick={(e) => e.stopPropagation()}>
              {/* Header with Gradient */}
              <div className="pay-modal-header" style={{
                background: 'linear-gradient(135deg, var(--proto-white) 0%, var(--proto-white) 100%)',
                padding: '24px',
                position: 'relative'
              }}>
                <div className="header-text">
                  <h3 style={{ fontSize: '20px', fontWeight: 700, marginBottom: '6px', color: 'white' }}>Share Your Experience</h3>
                  <p style={{ fontSize: '13px', opacity: 0.9, color: 'white' }}>Your feedback helps us improve</p>
                </div>
                <button
                  className="pay-close-btn"
                  onClick={() => setShowFeedbackModal(false)}
                  style={{
                    position: 'absolute',
                    top: '16px',
                    right: '16px',
                    background: 'rgba(255, 255, 255, 0.15)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '32px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.25)'}
                  onMouseLeave={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.15)'}
                >
                  <X size={18} color="white" />
                </button>
              </div>

              {/* Body */}
              <div className="pay-modal-body" style={{ padding: '24px', background: 'var(--engine-black)' }}>
                {/* Quality Rating */}
                <div style={{
                  marginBottom: '20px',
                  padding: '18px',
                  background: 'var(--graphite-dark)',
                  borderRadius: '10px',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                  border: '1px solid var(--border)'
                }}>
                  <label style={{
                    display: 'block',
                    fontWeight: 600,
                    marginBottom: '10px',
                    color: 'var(--proto-white)',
                    fontSize: '14px'
                  }}>
                    Session Quality <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '12px' }}>Rate the overall quality</p>
                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                    {[1, 2, 3, 4, 5].map(rating => (
                      <button
                        key={rating}
                        onClick={() => setFeedbackData({ ...feedbackData, quality_rating: rating })}
                        onMouseEnter={(e) => {
                          if (rating > feedbackData.quality_rating) {
                            e.target.style.transform = 'scale(1.15)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.transform = 'scale(1)';
                        }}
                        style={{
                          fontSize: '32px',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: rating <= feedbackData.quality_rating ? '#f59e0b' : '#e5e7eb',
                          transition: 'all 0.2s ease',
                          transform: 'scale(1)',
                          filter: rating <= feedbackData.quality_rating ? 'drop-shadow(0 2px 4px rgba(245, 158, 11, 0.3))' : 'none'
                        }}
                      >
                        ★
                      </button>
                    ))}
                  </div>
                  {feedbackData.quality_rating > 0 && (
                    <p style={{
                      textAlign: 'center',
                      marginTop: '12px',
                      fontSize: '12px',
                      color: 'var(--proto-white)',
                      fontWeight: 600
                    }}>
                      {feedbackData.quality_rating === 5 ? ' Excellent!' :
                        feedbackData.quality_rating === 4 ? 'Great!' :
                          feedbackData.quality_rating === 3 ? 'Good' :
                            feedbackData.quality_rating === 2 ? ' Fair' : ' Needs Improvement'}
                    </p>
                  )}
                </div>

                {/* Expert Effectiveness */}
                <div style={{
                  marginBottom: '20px',
                  padding: '18px',
                  background: 'var(--graphite-dark)',
                  borderRadius: '10px',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                  border: '1px solid var(--border)'
                }}>
                  <label style={{
                    display: 'block',
                    fontWeight: 600,
                    marginBottom: '10px',
                    color: 'var(--proto-white)',
                    fontSize: '14px'
                  }}>
                    Expert Effectiveness
                  </label>
                  <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '12px' }}>How effective was the expert?</p>
                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                    {[1, 2, 3, 4, 5].map(rating => (
                      <button
                        key={rating}
                        onClick={() => setFeedbackData({ ...feedbackData, expert_effectiveness: rating })}
                        onMouseEnter={(e) => {
                          if (rating > feedbackData.expert_effectiveness) {
                            e.target.style.transform = 'scale(1.2)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.transform = 'scale(1)';
                        }}
                        style={{
                          fontSize: '40px',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: rating <= feedbackData.expert_effectiveness ? '#fbbf24' : '#e5e7eb',
                          transition: 'all 0.2s ease',
                          transform: 'scale(1)',
                          filter: rating <= feedbackData.expert_effectiveness ? 'drop-shadow(0 2px 4px rgba(251, 191, 36, 0.3))' : 'none'
                        }}
                      >
                        ★
                      </button>
                    ))}
                  </div>
                </div>

                {/* Relevance Rating */}
                <div style={{
                  marginBottom: '20px',
                  padding: '18px',
                  background: 'var(--graphite-dark)',
                  borderRadius: '10px',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                  border: '1px solid var(--border)'
                }}>
                  <label style={{
                    fontWeight: 700,
                    marginBottom: '12px',
                    color: 'var(--proto-white)',
                    fontSize: '15px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    {/* <span style={{ fontSize: '18px' }}></span> */}
                    Relevance of Guidance
                  </label>
                  <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '16px' }}>How relevant was the content to your learning goals?</p>
                  <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                    {[1, 2, 3, 4, 5].map(rating => (
                      <button
                        key={rating}
                        onClick={() => setFeedbackData({ ...feedbackData, relevance_rating: rating })}
                        onMouseEnter={(e) => {
                          if (rating > feedbackData.relevance_rating) {
                            e.target.style.transform = 'scale(1.2)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.transform = 'scale(1)';
                        }}
                        style={{
                          fontSize: '40px',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: rating <= feedbackData.relevance_rating ? '#fbbf24' : '#e5e7eb',
                          transition: 'all 0.2s ease',
                          transform: 'scale(1)',
                          filter: rating <= feedbackData.relevance_rating ? 'drop-shadow(0 2px 4px rgba(251, 191, 36, 0.3))' : 'none'
                        }}
                      >
                        ★
                      </button>
                    ))}
                  </div>
                </div>

                {/* Comments */}
                <div style={{
                  padding: '24px',
                  background: 'var(--graphite-dark)',
                  borderRadius: '12px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                  border: '1px solid #e5e7eb'
                }}>
                  <label style={{
                    fontWeight: 700,
                    marginBottom: '12px',
                    color: 'var(--proto-white)',
                    fontSize: '15px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    {/* <span style={{ fontSize: '18px' }}>💭</span> */}
                    Additional Comments
                    <span style={{ fontSize: '12px', fontWeight: 400, color: 'var(--text-faint)' }}>(Optional)</span>
                  </label>
                  <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '12px' }}>Share any specific feedback, suggestions, or highlights from the session</p>
                  <textarea
                    value={feedbackData.comments}
                    onChange={(e) => setFeedbackData({ ...feedbackData, comments: e.target.value })}
                    placeholder="What did you like most? What could be improved? Any specific insights you gained?"
                    style={{
                      width: '100%',
                      minHeight: '120px',
                      padding: '14px',
                      borderRadius: '10px',
                      border: '2px solid var(--border)',
                      fontSize: '14px',
                      fontFamily: 'inherit',
                      resize: 'vertical',
                      transition: 'border-color 0.2s',
                      outline: 'none'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#667eea'}
                    onBlur={(e) => e.target.style.borderColor = 'var(--border)'}
                  />
                </div>
              </div>

              {/* Footer */}
              <div className="pay-modal-footer" style={{
                padding: '20px 24px',
                background: 'var(--graphite-dark)',
                borderTop: '1px solid #e5e7eb',
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end'
              }}>
                <button
                  className="pay-cancel-btn"
                  onClick={() => setShowFeedbackModal(false)}
                  style={{
                    padding: '12px 24px',
                    borderRadius: '10px',
                    border: '2px solid var(--border)',
                    background: 'var(--graphite-dark)',
                    color: 'var(--text-muted)',
                    fontWeight: 600,
                    fontSize: '14px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.borderColor = 'var(--border)';
                    e.target.style.background = '#f8fafc';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.borderColor = 'var(--border)';
                    e.target.style.background = 'white';
                  }}
                >
                  Cancel
                </button>
                <button
                  className="pay-confirm-btn"
                  onClick={submitFeedback}
                  disabled={feedbackData.quality_rating === 0}
                  style={{
                    padding: '12px 32px',
                    borderRadius: '10px',
                    border: 'none',
                    background: feedbackData.quality_rating === 0
                      ? 'var(--border)'
                      : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    fontWeight: 700,
                    fontSize: '14px',
                    cursor: feedbackData.quality_rating === 0 ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s',
                    boxShadow: feedbackData.quality_rating === 0
                      ? 'none'
                      : '0 4px 12px rgba(102, 126, 234, 0.4)'
                  }}
                  onMouseEnter={(e) => {
                    if (feedbackData.quality_rating > 0) {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 6px 16px rgba(102, 126, 234, 0.5)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (feedbackData.quality_rating > 0) {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
                    }
                  }}
                >
                  Submit Feedback & Complete Session
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Interests Update Modal */}
      {showInterestsModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          backdropFilter: 'blur(4px)'
        }} onClick={() => setShowInterestsModal(false)}>
          <div style={{
            background: 'var(--graphite-dark)',
            borderRadius: '16px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '100vh',
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            display: 'flex',
            flexDirection: 'column'
          }} onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div style={{
              padding: '24px 28px',
              background: 'linear-gradient(135deg, var(--proto-white) 0%, var(--proto-white) 100%)',
              color: 'white',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '22px', fontWeight: '700' }}>Update Skills & Interests</h2>
                <p style={{ margin: '4px 0 0 0', fontSize: '13px', opacity: 0.9 }}>
                  Personalize your learning experience
                </p>
              </div>
              <button
                onClick={() => setShowInterestsModal(false)}
                style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: 'none',
                  borderRadius: '8px',
                  width: '36px',
                  height: '36px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.3)'}
                onMouseLeave={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
              >
                <X size={20} color="white" />
              </button>
            </div>

            {/* Content */}
            <div style={{
              padding: '28px',
              overflowY: 'auto',
              flex: 1
            }}>
              {/* Skills Section */}
              <div style={{ marginBottom: '28px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '700',
                  color: 'var(--proto-white)',
                  marginBottom: '12px'
                }}>
                  Your Skills
                </label>
                <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '12px' }}>
                  Select from common skills or type to search
                </p>

                {/* Skill Dropdown with Autocomplete */}
                <div style={{ marginBottom: '12px', position: 'relative' }}>
                  <input
                    type="text"
                    value={newSkillInput}
                    onChange={(e) => setNewSkillInput(e.target.value)}
                    onFocus={() => document.getElementById('skills-dropdown').style.display = 'block'}
                    placeholder="Search and select skills..."
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      borderRadius: '8px',
                      border: '2px solid var(--border)',
                      fontSize: '14px',
                      outline: 'none',
                      transition: 'border-color 0.2s'
                    }}
                    onBlur={(e) => {
                      // Delay to allow click on dropdown item
                      setTimeout(() => {
                        if (document.getElementById('skills-dropdown')) {
                          document.getElementById('skills-dropdown').style.display = 'none';
                        }
                      }, 200);
                      e.target.style.borderColor = 'var(--border)';
                    }}
                  />
                  <div
                    id="skills-dropdown"
                    style={{
                      display: 'none',
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      maxHeight: '200px',
                      overflowY: 'auto',
                      background: 'var(--graphite-dark)',
                      border: '2px solid var(--border)',
                      borderTop: 'none',
                      borderRadius: '0 0 8px 8px',
                      zIndex: 1000,
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                    }}
                  >
                    {(() => {

                      const filtered = skillSuggestions.filter(skill =>
                        !interestsForm.skills.includes(skill)
                      );

                      // Show message if no input
                      if (!newSkillInput.trim()) {
                        return (
                          <div style={{
                            padding: '10px 14px',
                            fontSize: '13px',
                            color: 'var(--text-faint)',
                            fontStyle: 'italic'
                          }}>
                            Start typing to search or add a custom skill...
                          </div>
                        );
                      }

                      const items = [];

                      // Add custom skill option if user has typed something and it's not already selected
                      if (newSkillInput.trim() && !interestsForm.skills.includes(newSkillInput.trim())) {
                        items.push(
                          <div
                            key="custom-skill"
                            onClick={() => {
                              setInterestsForm(prev => ({
                                ...prev,
                                skills: [...prev.skills, newSkillInput.trim()]
                              }));
                              setNewSkillInput('');
                            }}
                            style={{
                              padding: '10px 14px',
                              cursor: 'pointer',
                              fontSize: '14px',
                              color: '#3b82f6',
                              fontWeight: '600',
                              borderBottom: filtered.length > 0 ? '1px solid var(--border)' : 'none',
                              background: '#f0f9ff'
                            }}
                            onMouseEnter={(e) => e.target.style.background = '#e0f2fe'}
                            onMouseLeave={(e) => e.target.style.background = '#f0f9ff'}
                          >
                            ➕ Add "{newSkillInput.trim()}" as custom skill
                          </div>
                        );
                      }

                      // Add filtered suggestions
                      filtered.forEach((skill, idx) => {
                        items.push(
                          <div
                            key={idx}
                            onClick={() => {
                              setInterestsForm(prev => ({
                                ...prev,
                                skills: [...prev.skills, skill]
                              }));
                              setNewSkillInput('');
                            }}
                            style={{
                              padding: '10px 14px',
                              cursor: 'pointer',
                              fontSize: '14px',
                              color: 'var(--proto-white)',
                              borderBottom: idx < filtered.length - 1 ? '1px solid var(--border)' : 'none'
                            }}
                            onMouseEnter={(e) => e.target.style.background = '#f8fafc'}
                            onMouseLeave={(e) => e.target.style.background = 'white'}
                          >
                            {skill}
                          </div>
                        );
                      });

                      return items.length > 0 ? items : (
                        <div style={{
                          padding: '10px 14px',
                          fontSize: '13px',
                          color: 'var(--text-faint)',
                          fontStyle: 'italic'
                        }}>
                          No matching skills found
                        </div>
                      );
                    })()}
                  </div>
                </div>

                {/* Skills Tags */}
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {interestsForm.skills.map((skill, idx) => (
                    <span key={idx} style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '6px 12px',
                      background: '#d1fae5',
                      color: '#047857',
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: '600',
                      border: '1px solid #a7f3d0'
                    }}>
                      {skill}
                      <button
                        onClick={() => handleRemoveSkill(skill)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: 0,
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        <X size={14} color="#047857" />
                      </button>
                    </span>
                  ))}
                  {interestsForm.skills.length === 0 && (
                    <span style={{ fontSize: '13px', color: 'var(--text-faint)', fontStyle: 'italic' }}>
                      No skills selected yet
                    </span>
                  )}
                </div>
              </div>

              {/* Domains Section */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '700',
                  color: 'var(--proto-white)',
                  marginBottom: '12px'
                }}>
                  Domain Interests
                </label>
                <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '12px' }}>
                  Select up to 3 domains you want to explore
                </p>

                {/* Domain Multi-Select Dropdown */}
                <div style={{ marginBottom: '12px', position: 'relative' }}>
                  <div
                    onClick={() => {
                      const dropdown = document.getElementById('domains-dropdown');
                      dropdown.style.display = dropdown.style.display === 'block' ? 'none' : 'block';
                    }}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      borderRadius: '8px',
                      border: '2px solid var(--border)',
                      fontSize: '14px',
                      cursor: 'pointer',
                      background: 'var(--graphite-dark)',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <span style={{ color: interestsForm.preferred_domains.length > 0 ? 'var(--proto-white)' : 'var(--text-faint)' }}>
                      {interestsForm.preferred_domains.length > 0
                        ? `${interestsForm.preferred_domains.length} domain(s) selected`
                        : '-- Select domains --'}
                    </span>
                    <span style={{ fontSize: '12px' }}>▼</span>
                  </div>
                  <div
                    id="domains-dropdown"
                    style={{
                      display: 'none',
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      maxHeight: '300px',
                      overflowY: 'auto',
                      background: 'var(--graphite-dark)',
                      border: '2px solid var(--border)',
                      borderTop: 'none',
                      borderRadius: '0 0 8px 8px',
                      zIndex: 1000,
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                    }}
                  >
                    {(() => {
                      const DOMAINS = INDUSTRY_DOMAINS.map(domain => ({
                        name: domain,
                        icon: DOMAIN_METADATA[domain]?.icon || '📚'
                      }));

                      return DOMAINS.map((domain, idx) => {
                        const isSelected = interestsForm.preferred_domains.includes(domain.name);
                        return (
                          <div
                            key={idx}
                            onClick={() => {
                              if (isSelected) {
                                setInterestsForm(prev => ({
                                  ...prev,
                                  preferred_domains: prev.preferred_domains.filter(d => d !== domain.name)
                                }));
                              } else if (interestsForm.preferred_domains.length < 3) {
                                setInterestsForm(prev => ({
                                  ...prev,
                                  preferred_domains: [...prev.preferred_domains, domain.name]
                                }));
                              }
                            }}
                            style={{
                              padding: '12px 14px',
                              cursor: interestsForm.preferred_domains.length >= 3 && !isSelected ? 'not-allowed' : 'pointer',
                              fontSize: '14px',
                              color: 'var(--proto-white)',
                              borderBottom: idx < DOMAINS.length - 1 ? '1px solid var(--border)' : 'none',
                              background: isSelected ? '#eff6ff' : 'white',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px',
                              opacity: interestsForm.preferred_domains.length >= 3 && !isSelected ? 0.5 : 1
                            }}
                            onMouseEnter={(e) => {
                              if (interestsForm.preferred_domains.length < 3 || isSelected) {
                                e.target.style.background = isSelected ? '#dbeafe' : '#f8fafc';
                              }
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = isSelected ? '#eff6ff' : 'white';
                            }}
                          >
                            <span style={{ fontSize: '18px' }}>{domain.icon}</span>
                            <span style={{ flex: 1 }}>{domain.name}</span>
                            {isSelected && <span style={{ color: '#3b82f6', fontWeight: '600' }}>✓</span>}
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>

                <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '12px' }}>
                  {interestsForm.preferred_domains.length}/3 selected
                </p>

                {/* Domain Tags */}
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {interestsForm.preferred_domains.map((domain, idx) => (
                    <span key={idx} style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '6px 12px',
                      background: '#e0e7ff',
                      color: '#4338ca',
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: '600',
                      border: '1px solid #c7d2fe'
                    }}>
                      {domain}
                      <button
                        onClick={() => handleRemoveDomain(domain)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: 0,
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        <X size={14} color="#4338ca" />
                      </button>
                    </span>
                  ))}
                  {interestsForm.preferred_domains.length === 0 && (
                    <span style={{ fontSize: '13px', color: 'var(--text-faint)', fontStyle: 'italic' }}>
                      No domains selected yet
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div style={{
              padding: '20px 28px',
              background: 'var(--engine-black)',
              borderTop: '1px solid var(--border)',
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => setShowInterestsModal(false)}
                style={{
                  padding: '10px 20px',
                  background: 'var(--graphite-dark)',
                  color: 'var(--text-muted)',
                  border: '1px solid var(--border)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.background = 'var(--border)'}
                onMouseLeave={(e) => e.target.style.background = 'white'}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitInterests}
                style={{
                  padding: '10px 24px',
                  background: 'linear-gradient(135deg, var(--proto-white) 0%, var(--proto-white) 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  boxShadow: '0 4px 12px rgba(15, 23, 42, 0.4)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 16px rgba(15, 23, 42, 0.5)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 12px rgba(15, 23, 42, 0.4)';
                }}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showChangePasswordModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            background: 'var(--graphite-dark)',
            borderRadius: '16px',
            width: '90%',
            maxWidth: '500px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden'
          }}>
            {/* Header */}
            <div style={{
              padding: '24px 28px',
              borderBottom: '1px solid var(--border)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Lock size={20} style={{ color: 'white' }} />
                </div>
                <h2 style={{ fontSize: '20px', fontWeight: '700', color: 'var(--proto-white)', margin: 0 }}>
                  Change Password
                </h2>
              </div>
              <button
                onClick={() => {
                  setShowChangePasswordModal(false);
                  setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '8px',
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.background = 'var(--border)'}
                onMouseLeave={(e) => e.target.style.background = 'none'}
              >
                <X size={20} style={{ color: 'var(--text-muted)' }} />
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: '28px' }}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: 'var(--text-muted)',
                  marginBottom: '8px'
                }}>
                  Current Password *
                </label>
                <input
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                  placeholder="Enter your current password"
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    border: '2px solid var(--border)',
                    borderRadius: '8px',
                    fontSize: '15px',
                    fontWeight: '500',
                    outline: 'none',
                    transition: 'border-color 0.2s'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                  onBlur={(e) => e.target.style.borderColor = 'var(--border)'}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: 'var(--text-muted)',
                  marginBottom: '8px'
                }}>
                  New Password *
                </label>
                <input
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  placeholder="Enter new password (min 6 characters)"
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    border: '2px solid var(--border)',
                    borderRadius: '8px',
                    fontSize: '15px',
                    fontWeight: '500',
                    outline: 'none',
                    transition: 'border-color 0.2s'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                  onBlur={(e) => e.target.style.borderColor = 'var(--border)'}
                />
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: 'var(--text-muted)',
                  marginBottom: '8px'
                }}>
                  Confirm New Password *
                </label>
                <input
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  placeholder="Re-enter new password"
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    border: '2px solid var(--border)',
                    borderRadius: '8px',
                    fontSize: '15px',
                    fontWeight: '500',
                    outline: 'none',
                    transition: 'border-color 0.2s'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                  onBlur={(e) => e.target.style.borderColor = 'var(--border)'}
                />
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => {
                    setShowChangePasswordModal(false);
                    setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                  }}
                  style={{
                    padding: '12px 24px',
                    borderRadius: '8px',
                    border: '2px solid var(--border)',
                    background: 'var(--graphite-dark)',
                    color: 'var(--text-muted)',
                    fontWeight: '600',
                    fontSize: '15px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.borderColor = 'var(--border)';
                    e.target.style.background = '#f8fafc';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.borderColor = 'var(--border)';
                    e.target.style.background = 'white';
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleChangePassword}
                  style={{
                    padding: '12px 32px',
                    fontSize: '15px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    background: 'linear-gradient(135deg, var(--proto-white) 0%, var(--proto-white) 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    boxShadow: '0 4px 12px rgba(15, 23, 42, 0.4)'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 16px rgba(15, 23, 42, 0.5)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 12px rgba(15, 23, 42, 0.4)';
                  }}
                >
                  <Lock size={16} />
                  Update Password
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Refund Request Modal */}
      {showRefundModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            background: 'var(--graphite-dark)',
            borderRadius: '16px',
            width: '90%',
            maxWidth: '500px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden'
          }}>
            <div style={{
              padding: '24px 28px',
              borderBottom: '1px solid var(--border)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: '#fff5f5'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  background: '#ef4444',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <ArrowLeft size={20} style={{ color: 'white' }} />
                </div>
                <h2 style={{ fontSize: '20px', fontWeight: '700', color: 'var(--proto-white)', margin: 0 }}>
                  Request Refund
                </h2>
              </div>
              <button
                onClick={() => setShowRefundModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '8px',
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.background = 'var(--border)'}
                onMouseLeave={(e) => e.target.style.background = 'none'}
              >
                <X size={20} style={{ color: 'var(--text-muted)' }} />
              </button>
            </div>

            <div style={{ padding: '28px' }}>
              <p style={{ fontSize: '14px', color: 'var(--text-muted)', marginBottom: '20px', lineHeight: '1.5' }}>
                You are requesting a refund for the following session. Please provide a clear reason for the compliance team to review.
              </p>

              <div style={{
                background: 'var(--engine-black)',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '24px',
                border: '1px solid var(--border)',
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '16px'
              }}>
                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: 'var(--text-faint)', textTransform: 'uppercase', marginBottom: '4px' }}>Professor</label>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--proto-white)' }}>{refundData.professor_name || 'Assigned Expert'}</div>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: 'var(--text-faint)', textTransform: 'uppercase', marginBottom: '4px' }}>Session Title</label>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--proto-white)' }}>{refundData.session_title}</div>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: 'var(--text-faint)', textTransform: 'uppercase', marginBottom: '4px' }}>Session Date</label>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--proto-white)' }}>{refundData.session_date}</div>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: 'var(--text-faint)', textTransform: 'uppercase', marginBottom: '4px' }}>Amount</label>
                  <div style={{ fontSize: '14px', fontWeight: '700', color: '#ef4444' }}>₹{refundData.amount}</div>
                </div>
                <div style={{ gridColumn: 'span 2' }}>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: 'var(--text-faint)', textTransform: 'uppercase', marginBottom: '4px' }}>Payment ID</label>
                  <div style={{ fontSize: '13px', fontFamily: 'monospace', color: 'var(--text-muted)' }}>#{refundData.payment_id}</div>
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: 'var(--text-muted)',
                  marginBottom: '8px'
                }}>
                  Reason for Refund (Minimum 20 characters) *
                </label>
                <textarea
                  value={refundData.details}
                  onChange={(e) => setRefundData({ ...refundData, details: e.target.value })}
                  placeholder="Please describe why you are requesting a refund..."
                  required
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    border: '2px solid var(--border)',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    outline: 'none',
                    minHeight: '120px',
                    transition: 'border-color 0.2s',
                    resize: 'none'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#ef4444'}
                  onBlur={(e) => e.target.style.borderColor = 'var(--border)'}
                />
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginTop: '6px'
                }}>
                  <span style={{ fontSize: '11px', color: refundData.details.length < 20 ? '#ef4444' : '#10b981' }}>
                    {refundData.details.length} / 20 characters required
                  </span>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setShowRefundModal(false)}
                  style={{
                    padding: '12px 24px',
                    borderRadius: '8px',
                    border: '2px solid var(--border)',
                    background: 'var(--graphite-dark)',
                    color: 'var(--text-muted)',
                    fontWeight: '600',
                    fontSize: '15px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={submitRefundRequest}
                  disabled={refundData.details.length < 20}
                  style={{
                    padding: '12px 32px',
                    fontSize: '15px',
                    background: refundData.details.length < 20 ? '#fca5a5' : '#ef4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: '600',
                    cursor: refundData.details.length < 20 ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s',
                    boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)'
                  }}
                >
                  Submit Request
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Resume Viewer Modal */}
      {resumeViewerModalOpen && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)', zIndex: 10000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px'
        }}>
          <div style={{
            background: '#ffffff', borderRadius: '16px', maxWidth: '800px', width: '100%',
            maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
          }}>
            <div style={{
              padding: '20px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex',
              alignItems: 'center', justifyContent: 'space-between'
            }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  📄 Your Uploaded Resume
                </h3>
                <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#64748b' }}>
                  Original PDF for your reference; extracted text used only by AI agents.
                </p>
              </div>
              <button onClick={() => setResumeViewerModalOpen(false)} style={{
                background: 'none', border: 'none', cursor: 'pointer', color: '#64748b'
              }}>
                <X size={20} />
              </button>
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0', background: '#f8fafc', padding: '0 24px' }}>
              <button
                onClick={() => setResumeModalTab('pdf')}
                style={{
                  padding: '12px 20px', background: 'none', border: 'none', cursor: 'pointer',
                  fontSize: '13px', fontWeight: '600',
                  color: resumeModalTab === 'pdf' ? '#2563eb' : '#64748b',
                  borderBottom: resumeModalTab === 'pdf' ? '2px solid #2563eb' : '2px solid transparent',
                  display: 'flex', alignItems: 'center', gap: '6px'
                }}
              >
                📑 Original PDF
              </button>
              <button
                onClick={() => setResumeModalTab('text')}
                style={{
                  padding: '12px 20px', background: 'none', border: 'none', cursor: 'pointer',
                  fontSize: '13px', fontWeight: '600',
                  color: resumeModalTab === 'text' ? '#2563eb' : '#64748b',
                  borderBottom: resumeModalTab === 'text' ? '2px solid #2563eb' : '2px solid transparent',
                  display: 'flex', alignItems: 'center', gap: '6px'
                }}
              >
                🤖 AI Extracted Text
              </button>
            </div>

            {/* Tab Content */}
            {resumeModalTab === 'pdf' ? (
              <div style={{ flex: 1, padding: '16px', display: 'flex', flexDirection: 'column', minHeight: '520px', justifyContent: 'center', alignItems: 'center' }}>
                {isLoadingPdfBlob ? (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', color: '#64748b' }}>
                    <Loader2 size={32} className="spin" color="#2563eb" />
                    <span style={{ fontSize: '14px', fontWeight: '500' }}>Loading original PDF resume...</span>
                  </div>
                ) : pdfFetchError ? (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', textAlign: 'center', padding: '40px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0', width: '100%', boxSizing: 'border-box' }}>
                    <ShieldAlert size={40} color="#ef4444" />
                    <h4 style={{ margin: 0, color: '#0f172a', fontSize: '16px', fontWeight: '700' }}>PDF File Unavailable</h4>
                    <p style={{ margin: 0, color: '#64748b', fontSize: '13px' }}>{pdfFetchError}</p>
                    <button
                      onClick={() => setResumeModalTab('text')}
                      style={{ padding: '8px 16px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', marginTop: '8px' }}
                    >
                      View AI Extracted Text
                    </button>
                  </div>
                ) : (
                  <iframe
                    src={resumePdfBlobUrl}
                    style={{ width: '100%', height: '520px', border: '1px solid #cbd5e1', borderRadius: '8px', background: '#ffffff' }}
                    title="Original PDF Resume"
                  />
                )}
              </div>
            ) : (
              <div style={{ padding: '20px', overflowY: 'auto', flex: 1, fontSize: '13px', color: '#334155', lineHeight: '1.6', whiteSpace: 'pre-wrap', fontFamily: 'monospace', background: '#f8fafc', margin: '16px', borderRadius: '8px', border: '1px solid #e2e8f0', maxHeight: '520px' }}>
                <div style={{ fontFamily: 'sans-serif' }}>
                  <FormattedResumeSummary summaryText={profile?.resume_text || profile?.resume_summary} title="AI Extracted Resume Structure" />
                </div>

                <div style={{ marginTop: '32px', paddingTop: '24px', borderTop: '2px dashed #cbd5e1', fontFamily: 'sans-serif' }}>
                  <h4 style={{ fontSize: '16px', fontWeight: '800', color: '#0f172a', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FileText size={18} color="#05a8f3" /> Raw Extracted Text
                  </h4>
                  <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '16px', marginTop: 0 }}>
                    This is the raw, unformatted text extracted from your PDF by our parsing engine.
                  </p>
                  <div style={{
                    background: '#ffffff',
                    padding: '20px',
                    borderRadius: '12px',
                    border: '1px solid #e2e8f0',
                    fontFamily: 'monospace',
                    fontSize: '12px',
                    color: '#475569',
                    whiteSpace: 'pre-wrap',
                    boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.02)'
                  }}>
                    {profile?.resume_text || profile?.resume_summary || 'No text extraction available for this document.'}
                  </div>
                </div>
              </div>
            )}

            <div style={{ padding: '16px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button onClick={() => {
                setResumeViewerModalOpen(false);
                resumeFileInputRef?.current?.click();
              }} style={{
                padding: '8px 16px', borderRadius: '8px', border: '1px solid #cbd5e1',
                background: '#ffffff', fontWeight: '600', fontSize: '13px', color: '#334155', cursor: 'pointer'
              }}>
                Replace Resume
              </button>
              <button onClick={() => setResumeViewerModalOpen(false)} style={{
                padding: '8px 16px', borderRadius: '8px', border: 'none',
                background: '#2563eb', fontWeight: '600', fontSize: '13px', color: '#ffffff', cursor: 'pointer'
              }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notifications */}
      <div style={{
        position: 'fixed',
        top: '20px',
        right: '20px',
        zIndex: 10000,
        display: 'flex',
        flexDirection: 'column',
        gap: '12px'
      }}>
        {toasts.map(toast => (
          <div
            key={toast.id}
            style={{
              background: toast.type === 'success' ? '#10b981' :
                toast.type === 'info' ? '#10b981' :  /* Processing/Preparing - make it green as requested */
                  toast.type === 'warning' ? '#f59e0b' : '#ef4444',
              color: '#fff',
              padding: '16px 24px',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              minWidth: '300px',
              maxWidth: '500px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              animation: 'slideIn 0.3s ease-out',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            <span style={{ fontSize: '20px' }}>
              {toast.type === 'success' ? '\u2713' :
                toast.type === 'info' ? '\u2139' :
                  toast.type === 'warning' ? '\u26A0' : '\u2715'}
            </span>
            <span style={{ flex: 1 }}>{toast.message}</span>
            <button
              onClick={() => setToasts(prev => prev.filter(t => t.id !== toast.id))}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#fff',
                cursor: 'pointer',
                fontSize: '18px',
                padding: '0',
                opacity: 0.8,
                transition: 'opacity 0.2s'
              }}
            >
              &times;
            </button>
          </div>
        ))}
      </div>

      <style>{`
        @keyframes slideIn {
          from {
            transform: translateX(400px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
      {/* Assessment Rules & Recording Notice Modal */}
      {showAssessmentRulesModal && (
        <div className="stu-modal-overlay" onClick={() => setShowAssessmentRulesModal(false)} style={{ zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(5px)' }}>
          <div className="stu-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '520px', width: '100%', padding: '32px', borderRadius: '18px', background: 'var(--graphite-dark)', border: '1px solid #3b82f6', boxShadow: '0 20px 40px rgba(0,0,0,0.5)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '20px' }}>
              <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'rgba(59, 130, 246, 0.15)', color: '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <ShieldAlert size={26} />
              </div>
              <h2 style={{ margin: 0, fontSize: '22px', fontWeight: '700', color: 'var(--proto-white)' }}>Important Assessment Rules</h2>
            </div>
            
            <div style={{ padding: '16px 20px', background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '12px', color: '#fca5a5', fontSize: '14px', marginBottom: '24px', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
              <Video size={20} style={{ flexShrink: 0, marginTop: '2px', color: '#ef4444' }} />
              <div>
                <strong style={{ color: '#ef4444', display: 'block', marginBottom: '4px', fontSize: '15px' }}>Proctoring is Active</strong>
                You are about to start a proctored assessment. Your camera, screen, and browser tabs will be monitored and recorded to ensure academic integrity.
              </div>
            </div>

            <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 24px 0', display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <li style={{ display: 'flex', alignItems: 'center', gap: '12px', color: 'var(--slate-gray)' }}>
                <CheckCircle size={18} color="#10b981" /> Ensure you are in a quiet, well-lit room.
              </li>
              <li style={{ display: 'flex', alignItems: 'center', gap: '12px', color: 'var(--slate-gray)' }}>
                <CheckCircle size={18} color="#10b981" /> Keep your face visible to the camera at all times.
              </li>
              <li style={{ display: 'flex', alignItems: 'center', gap: '12px', color: 'var(--slate-gray)' }}>
                <CheckCircle size={18} color="#10b981" /> Do not open other tabs, windows, or applications.
              </li>
              <li style={{ display: 'flex', alignItems: 'center', gap: '12px', color: 'var(--slate-gray)' }}>
                <CheckCircle size={18} color="#10b981" /> Your assessment will enter full-screen mode automatically.
              </li>
            </ul>

            <div style={{ display: 'flex', gap: '16px', justifyContent: 'flex-end', marginTop: '32px' }}>
              <button 
                onClick={() => setShowAssessmentRulesModal(false)}
                style={{ padding: '12px 24px', borderRadius: '10px', background: 'var(--bg-lighter)', color: 'var(--proto-white)', border: 'none', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s' }}
              >
                Cancel
              </button>
              <button 
                onClick={startAssessment}
                style={{ padding: '12px 24px', borderRadius: '10px', background: '#3b82f6', color: '#fff', border: 'none', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <Play size={18} /> Agree &amp; Start
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Faculty Retake Appeal Modal */}
      {showRetakeAppealModal && (
        <div className="stu-modal-overlay" onClick={() => setShowRetakeAppealModal(false)} style={{ zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(5px)' }}>
          <div className="stu-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '520px', width: '100%', padding: '30px', borderRadius: '18px', background: '#ffffff', border: '1px solid #cbd5e1', boxShadow: '0 20px 40px rgba(0,0,0,0.25)', color: '#0f172a' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '16px' }}>
              <div style={{ width: '46px', height: '46px', borderRadius: '12px', background: '#fef2f2', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <ShieldAlert size={26} />
              </div>
              <div>
                <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '800', color: '#0f172a' }}>Appeal Disqualification to Faculty</h2>
                <span style={{ fontSize: '12.5px', color: '#64748b' }}>Request an exception retake from your Faculty</span>
              </div>
            </div>

            <p style={{ fontSize: '13.5px', color: '#475569', lineHeight: 1.5, margin: '0 0 16px 0' }}>
              Your last assessment was terminated by automated proctoring. Only your <strong>Faculty</strong> has access to review the incident snapshots and violation logs to grant a retake.
            </p>

            <form onSubmit={handleRequestRetakeAppeal}>
              <label style={{ display: 'block', fontSize: '12.5px', fontWeight: '700', color: '#334155', marginBottom: '6px' }}>
                Reason for Appeal / Explanation
              </label>
              <textarea
                value={appealReason}
                onChange={(e) => setAppealReason(e.target.value)}
                placeholder="Explain what caused the violation (e.g. accidental keypress, system dialog, browser freeze, network drop)..."
                rows={4}
                style={{
                  width: '100%', padding: '12px 14px', borderRadius: '10px',
                  border: '1.5px solid #cbd5e1', fontSize: '13.5px', fontFamily: 'inherit',
                  resize: 'vertical', boxSizing: 'border-box', marginBottom: '20px',
                  outline: 'none', color: '#0f172a'
                }}
              />

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setShowRetakeAppealModal(false)}
                  style={{ padding: '10px 18px', borderRadius: '8px', background: '#f1f5f9', color: '#475569', border: 'none', fontWeight: '600', cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmittingAppeal || !appealReason || appealReason.trim().length < 8}
                  style={{
                    padding: '10px 22px', borderRadius: '8px',
                    background: (isSubmittingAppeal || !appealReason || appealReason.trim().length < 8) ? '#94a3b8' : '#2563eb',
                    color: '#ffffff', border: 'none', fontWeight: '700',
                    cursor: (isSubmittingAppeal || !appealReason || appealReason.trim().length < 8) ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', gap: '8px'
                  }}
                >
                  {isSubmittingAppeal ? <Loader2 size={16} className="animate-spin" /> : <ShieldAlert size={16} />}
                  {isSubmittingAppeal ? 'Submitting...' : 'Submit Appeal to Faculty'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div >
  );
}

export default StudentDashboard;
